/**
 * SDMS sheet — Univer renderer (migration Phase 1).
 *
 * Univer's OSS core (Apache-2.0) can't read .xlsx natively — that's the paid
 * server "exchange".  So we keep ExcelJS (MIT) as the .xlsx PARSER and map its
 * parsed model into Univer's workbook JSON here, then hand it to Univer for
 * rendering + (later) editing.  Everything stays client-side and license-clean.
 *
 * Phase 1 scope: render parity — visible sheets, cell VALUES (formatted the way
 * ALPACA shows them), fills, font colour/bold/size, alignment, merges, column
 * widths, hidden columns/rows.  Borders / number-format editing / Update-save
 * come in later phases.  `editable=false` renders read-only (View mode).
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ExcelJS from 'exceljs';
// Shared modal warning (dialogBus global — callable outside a React tree), used by
// the formula-cell protection guard to show OG's protected-sheet message.
import { warn } from '../../components/confirm/dialogBus';
import { dfOverlayKey, type SdmsDfOverlay } from './sdmsDfOverlay';
import { patchWorkbookCells, type SheetCellEdit, type SheetFormulaEdit } from './sdmsXlsxPatch';
// rawStr (format-noise-free scalar text) + persistText (OG DataTableExporter parity) live in a
// standalone module so they are unit-testable without this heavy Univer module (2026-09-21).
import { rawStr, persistText, liveDateOf, formulaDiffers, isSheetError, fmtNumber } from './sdmsPersistText';
import { expandBandCfInSheetXml, restoreCfTextInSheetXml } from './sdmsCfTextRepair';
import { foldDeadSelfColumnBranches, rewriteCrossSheetParentLookup, excludeSelfRowFromSelfColumnAggregates, rewriteEmptyStringComparisons } from './sdmsFormulaFold';
export { dfOverlayKey, type SdmsDfOverlay };
import { createUniver, defaultTheme, LocaleType, merge } from '@univerjs/presets';
import { UniverSheetsCorePreset, CalculationMode } from '@univerjs/presets/preset-sheets-core';
import { registerLimsRound } from './limsRoundFn';
import { restrictSdmsSheetActions } from './sdmsSheetPermissions';
import UniverPresetSheetsCoreEnUS from '@univerjs/presets/preset-sheets-core/locales/en-US';
import '@univerjs/presets/lib/styles/preset-sheets-core.css';
import { UniverSheetsConditionalFormattingPreset } from '@univerjs/presets/preset-sheets-conditional-formatting';
import UniverPresetSheetsCfEnUS from '@univerjs/presets/preset-sheets-conditional-formatting/locales/en-US';
import '@univerjs/presets/lib/styles/preset-sheets-conditional-formatting.css';
// The CF FACADE must be side-effect imported or `range.createConditionalFormattingRule`
// is undefined — the same omission that made the _LIMSRound facade route silently
// no-op twice (2026-08-13). Keep this import.
import '@univerjs/sheets-conditional-formatting/facade';
import { applySdmsConditionalFormats } from './sdmsConditionalFormat';
import { readChartAnchors, anchorToBox, type AnchorBox } from './sdmsChartAnchor';
import { dropCharts } from './sdmsChartStore';
import { floatWorkbookCharts } from './sdmsLiveCharts';
import { readFormulaCacheInfo } from './sdmsFormulaCache';
import { UniverSheetsFilterPreset } from '@univerjs/presets/preset-sheets-filter';
import UniverPresetSheetsFilterEnUS from '@univerjs/presets/preset-sheets-filter/locales/en-US';
import '@univerjs/presets/lib/styles/preset-sheets-filter.css';
import { UniverSheetsDataValidationPreset } from '@univerjs/presets/preset-sheets-data-validation';
import UniverPresetSheetsDvEnUS from '@univerjs/presets/preset-sheets-data-validation/locales/en-US';
import '@univerjs/presets/lib/styles/preset-sheets-data-validation.css';
// Drawing preset — floats the pictures an instrument upload carries (SDMS Import Files, PFAS
// chromatograms) over the per-sample sheets; see sdmsImportImages.ts.
import { UniverSheetsDrawingPreset } from '@univerjs/presets/preset-sheets-drawing';
import UniverPresetSheetsDrawingEnUS from '@univerjs/presets/preset-sheets-drawing/locales/en-US';
import '@univerjs/presets/lib/styles/preset-sheets-drawing.css';
// Cell NOTES — the hover "how this was calculated" tips on Result / %Recovery / RPD (OG
// AddQcCellComments, 2026-09-23 build); see sdmsQcNotes.ts.
import { UniverSheetsNotePreset } from '@univerjs/presets/preset-sheets-note';
import UniverPresetSheetsNoteEnUS from '@univerjs/presets/preset-sheets-note/locales/en-US';
import '@univerjs/presets/lib/styles/preset-sheets-note.css';
import type { QcCellNote } from './sdmsQcNotes';
import { isResultSheet } from './sdmsEngineOverlay';
import { shareFormulas } from './sdmsSharedFormulas';
import { readSheetPictures, patchWorkbookPictures, type PlacedImage } from './sdmsImportImages';
import { collectDataValidations, qualifierPickText, type SheetDvRule } from './sdmsDataValidation';

/** KILL-SWITCH for the Excel→Univer conditional-formatting port.
 *  History: switched OFF 2026-08-13 after the sheet rendered blank white.  ROOT CAUSE
 *  FOUND that day: the CF EXTRACTION block runs inside `excelToUniver`, and the switch
 *  did not gate it — a throw there aborted the whole mapping, so the container stayed
 *  empty (no grid, not even the formula bar).
 *  Switched back ON once all THREE CF steps were made individually fail-safe:
 *    1. extraction (inside excelToUniver) — gated by this flag AND try/caught;
 *    2. preset registration (inside createUniver) — on failure Univer is rebuilt
 *       WITHOUT the CF preset (see the `boot(...)` fallback below);
 *    3. rule application — already try/caught, best-effort per sheet.
 *  So a CF problem can now only cost the COLOURS, never the sheet.  If the colours are
 *  wrong/missing, check the console for `[SDMS] conditional-format extraction skipped`,
 *  `[SDMS] conditional-formatting preset failed to register`, or
 *  `[SDMS] no conditional-format rules applied to "<sheet>"`.
 *
 *  ⚠️ ASYNC-FAILURE (2026-08-13, second attempt — READ BEFORE RE-ENABLING): with all
 *  three steps guarded the sheet STILL rendered blank when this was `true`, and the
 *  `boot(false)` fallback NEVER fired — which proves `createUniver` does not throw
 *  synchronously.  Registering the CF plugin therefore breaks Univer LATER, during its
 *  async plugin/render boot, where no try/catch around our own call sites can see it.
 *  Everything reachable from our stack is already guarded, so do NOT simply flip this
 *  back on: capture the REAL error first — browser console, or temporarily attach
 *  window 'error' / 'unhandledrejection' listeners around the init and surface the
 *  message on screen.  Suspect the CF plugin's own dependencies (it may need
 *  `sheets-conditional-formatting-ui`, or a service the presets build omits — the same
 *  class of problem the `_LIMSRound` facade route hit twice). */
// ON (2026-08-13) — the blank sheet is FIXED at its real cause, which the console finally
// named: `[SDMS] conditional-format extraction skipped out of memory` + `Uncaught out of
// memory`. The band-end scan below iterated to `ws.rowCount` calling
// `ws.getRow(n).getCell(1)`, and ExcelJS materialises rows/cells lazily — so it allocated
// hundreds of thousands of objects and exhausted the heap, taking Univer down with it.
// Replaced with `ws.eachRow` (visits only existing rows). Neither the CF plugin, the CF
// facade import, nor the React dispose race was to blame — all three were wrong guesses.
const SDMS_CF_ENABLED = true;
/* LAZY FORMULAS — the sheet is READ-ONLY until the analyst clicks Edit, and a
   read-only sheet has nothing to recalculate. Handing Univer the formulas anyway
   left its engine grinding through 1,857 of them on this batch, which is the lag
   after the sheet opens; the build before 2026-08-14 emitted plain values and
   was smooth. So: values only while read-only, formulas restored the moment the
   sheet becomes editable (rebuilt once, before any edit exists). Set false to go
   back to always-formulas in one line. */
const SDMS_LAZY_FORMULAS = true;

/** Univer context-menu items HIDDEN on the SDMS sheet — the destructive actions a
 *  worksheet permission point can't target without also disabling something we keep
 *  (Copy/Paste-Special share Copy's/edit permissions; Clear-All shares editing; the
 *  cell Delete submenu shares delete-range). Univer's `MenuConfig`
 *  (`{ [menuItemId]: { hidden } }`) hides them precisely by id; the core preset forwards
 *  a `menu` option to UniverUIPlugin, which feeds the menu-item factories. KEPT visible:
 *  Copy, Cut, Paste, Clear-contents, Freeze. (user 2026-09-11)
 *  Structural insert/delete/hide/resize of rows & columns are ALSO blocked via permission
 *  points (sdmsSheetPermissions.ts); Freeze is intentionally kept. */
const SDMS_CONTEXT_MENU_HIDE: Record<string, { hidden?: boolean; disabled?: boolean }> = {
  // Clipboard / clear / protect — no clean permission point, hidden by menu id.
  'sheet.menu.copy-special': { hidden: true },                           // Copy Special (whole submenu)
  // Notes are system-written display tips (OG AddQcCellComments) — no add / delete / show-hide by hand.
  'sheet.operation.add-note-popup': { hidden: true },
  'sheet.command.delete-note': { hidden: true },
  'sheet.command.toggle-note-popup': { hidden: true },
  'sheet.menu.paste-special': { hidden: true },                          // Paste Special (whole submenu)
  'sheet.menu.delete': { hidden: true },                                 // Delete (cell submenu — delete cells / shift)
  'sheet.command.clear-selection-format': { hidden: true },              // Clear › Clear Format
  'sheet.command.clear-selection-all': { hidden: true },                 // Clear › Clear All
  'sheet.contextMenu.text-to-number': { hidden: true },                  // "Text to Number" (numfmt convert; menu-item id, cmd = sheet.command.text-to-number) — user 2026-09-11
  'sheet.contextMenu.permission': { hidden: true },                      // Protect Rows And Columns (submenu)
  'sheet.command.add-range-protection-from-context-menu': { hidden: true },
  'sheet.command.set-range-protection-from-context-menu': { hidden: true },
  'sheet.command.delete-range-protection-from-context-menu': { hidden: true },
  'sheet.command.view-sheet-permission-from-context-menu': { hidden: true },
  // Structural row/column ops — ALSO blocked by permission points (sdmsSheetPermissions.ts).
  // Hidden here as well so they don't linger GREYED in the menu (user 2026-09-11).
  'sheet.menu.cell-insert': { hidden: true },                            // cell right-click "Insert" submenu
  'sheet.command.insert-multi-cols-before': { hidden: true },            // col-header: Insert N cols left
  'sheet.command.insert-multi-cols-right': { hidden: true },             // col-header: Insert N cols right
  'sheet.command.insert-multi-rows-above': { hidden: true },             // row-header: Insert N rows above
  'sheet.command.insert-multi-rows-after': { hidden: true },             // row-header: Insert N rows below
  'sheet.command.insert-col-before': { hidden: true },                   // (belt-and-braces: cell-submenu insert col/row)
  'sheet.command.insert-row-before': { hidden: true },
  'sheet.command.hide-col-confirm': { hidden: true },                    // Hide Selected Column
  'sheet.command.hide-row-confirm': { hidden: true },                    // Hide Selected Row
  'sheet.command.remove-col-confirm': { hidden: true },                  // Delete Selected Column
  'sheet.command.remove-row-confirm': { hidden: true },                  // Delete Selected Row
  'sheet.command.set-col-auto-width': { hidden: true },                  // Fit for data
  // Manual resize via the context menu's inline stepper — the SDMS sheet is fixed to the
  // template's column widths / row heights, so hide "Column Width" (user 2026-09-16) and
  // its row analog "Row Height" for consistency (both are resize ops blocked elsewhere).
  'sheet.command.set-worksheet-col-width': { hidden: true },             // "Column Width" stepper
  'sheet.command.set-row-height': { hidden: true },                      // "Row Height" stepper
};


// ── value formatting (mirrors SdmsSpreadsheet so the display matches) ───────
/* OOXML escapes any control character as `_xHHHH_`, so a multi-line heading arrives as
   "Numeric_x000a_Result_x000a_(mg/L)". Only the CARRIAGE-RETURN form was handled, so the
   LINE-FEED form leaked into every wrapped header on screen (client 2026-08-24) — and,
   less visibly, broke header MATCHING: the normaliser strips whitespace but not an escape
   sequence, so `startsWith('numericresult')` never matched a two-line "Numeric Result".
   Decode the general form. `_x005f_` is OOXML's own escape for a literal underscore that
   would otherwise begin an escape run; decoding it to "_" in the same left-to-right pass
   leaves the remainder of that run as literal text, which is exactly the intent. */
function cleanStr(s: string): string {
  return s
    .replace(/_x([0-9A-Fa-f]{4})_/g, (_m, hex: string) => {
      const code = parseInt(hex, 16);
      return code === 0x5f ? '_' : String.fromCharCode(code);
    })
    .replace(/\r/g, '');            // CR, raw or just decoded; \n is KEPT so headers stack
}
const pad2 = (n: number) => String(n).padStart(2, '0');

// decimalsFromNumFmt / fmtNumber live in sdmsPersistText.ts — the SAVE renders a number exactly as
// this view does (OG stores what the sheet shows, defect 9 2026-09-28), so both share one formatter.
/* Excel's BUILT-IN date formats 14 and 22 carry NO format code in the file - only a
   numFmtId - and Excel/DevExpress render them with the SYSTEM short date, not with
   any literal pattern.  ExcelJS hands us its own placeholder text for those ids
   ("mm-dd-yy", "m/d/yy h:mm"), and Univer, having no notion of "built-in", printed
   that placeholder literally: "08-05-26" where LIMS shows "08/05/2026", and
   "8/7/26 9:42" where LIMS shows "8/7/2026 9:42".  Found by the 2026-08-24 sweep of
   every template against the real DevExpress v20.1 engine: 2,978 visible cells across
   templates 8060 / 8070 / 8072 / 8086 / 8088 / 8096 / 8101.  Substitute the US short
   date these labs run - which is also the app-canonical MM/DD/YYYY (S14b.6).

   KEY CORRECTED 2026-08-26.  The id-22 entry was written as 'm/d/yy h:mm' and so
   NEVER MATCHED ANYTHING - dead from the day it was added.  ExcelJS's actual
   placeholder for built-in 22 QUOTES the hour: `m/d/yy "h":mm`.  Verified by reading
   the OOXML and the ExcelJS value side by side on QB26072904_075 / RawDataSheet!AT2,
   whose xf carries numFmtId="22" (a BUILT-IN - the workbook's custom numFmts are only
   164-169) while ExcelJS reports `m/d/yy "h":mm`.  ALPACA renders that column
   "7/27/2026 9:03" - the system short date, four-digit year, real minutes - exactly as
   this table intends.  With the wrong key the old code fell through to its hard-coded
   MM/DD/YYYY HH:mm and printed "07/27/2026 09:03" (close enough to hide the bug), and
   once fmtDate started honouring format codes it printed "7/27/26 h:07": the quoted h
   is a literal, so by Excel's own rule the trailing mm is the MONTH and the minutes
   vanish.  That is what Excel does with this string - but the string is an ExcelJS
   INVENTION, not the template's format, so honouring it was honouring the wrong
   thing.  1,057 cells across templates 8114 and 8072.
   NOTE the trap this leaves: a genuine CUSTOM `m/d/yy h:mm;@` (numFmtId 164, Sheet1's
   AnalysisDate on the same workbook) IS honoured literally by DevExpress -> "7/27/26
   14:12".  Built-in and custom differ only by ExcelJS's quoting and the `;@` suffix,
   so match these keys EXACTLY; never normalise them together. */
const BUILTIN_DATE_PATTERN: Record<string, string> = {
  'mm-dd-yy':        'mm/dd/yyyy',      // built-in 14
  'm/d/yy "h":mm':   'm/d/yyyy h:mm',   // built-in 22 - ExcelJS quotes the hour
  'm/d/yy h:mm':     'm/d/yyyy h:mm',   // defensive: unquoted, should another ExcelJS emit it
};
function univerPattern(fmt: string): string { return BUILTIN_DATE_PATTERN[fmt] ?? fmt; }

/** The FIRST section of a format code (`positive;negative;zero;text`) — the only one
 *  that applies to a date. The `;` must be located OUTSIDE quotes and escapes. */
export function firstSection(code: string): string {
  for (let i = 0; i < code.length; i++) {
    const ch = code[i]!;
    if (ch === '\\') { i++; continue; }
    if (ch === '"') { i++; while (i < code.length && code[i] !== '"') i++; continue; }
    if (ch === ';') return code.slice(0, i);
  }
  return code;
}

/* Is the token at `i` followed (past separators and literals) by SECONDS?  Excel's
   rule for the ambiguous `m`: it means MINUTES when it sits next to an hour or a
   seconds token, and MONTH otherwise. */
function nextTokenIsSeconds(f: string, i: number): boolean {
  for (let j = i; j < f.length; j++) {
    const c = f[j]!;
    if (c === '\\') { j++; continue; }
    if (c === '"') { j++; while (j < f.length && f[j] !== '"') j++; continue; }
    const l = c.toLowerCase();
    if (l === 'y' || l === 'm' || l === 'd' || l === 'h' || l === 's') return l === 's';
  }
  return false;
}

const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS_ABBR   = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL   = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/* CONSUMED, never printed: we render 24-hour app-wide (standing rule 3), so an
   AM/PM marker is dropped rather than emitted as the literal text "AM/PM". */
const AMPM_RE = /^(AM\/PM|am\/pm|A\/P|a\/p)/;

/**
 * Render a Date through its Excel FORMAT CODE, the way DevExpress renders it.
 *
 * WHY THIS EXISTS. fmtDate used to hard-code MM/DD/YYYY HH:mm and read the format
 * only to decide whether a date part, a time part, or both appeared. That is right
 * for `mm/dd/yyyy hh:mm` (1,568 cells / 27 templates) so it went unnoticed — but
 * template 8114 (PFAS_533) formats AnalysisDate as the CUSTOM numFmtId 164
 * `m/d/yy h:mm;@`, which DevExpress renders literally as `7/27/26 14:12` while we
 * printed `07/27/2026 14:12` (client 2026-08-26). The style's `n.pattern` could not
 * save it: a Date has no numericValue(), so the cell is emitted as TEXT (`t: 1`)
 * carrying this pre-formatted string, and Univer never number-formats text.
 * Measured across one workbook per template: 821 such cells in 11 templates, plus
 * `mm/dd/yy;@` (124 / 6) and `m/d/yyyy;@` (23 / 5) padded and year-widened the same
 * way — so this is a general format-code renderer, not a per-template special case.
 *
 * WHAT IT DELIBERATELY DOES NOT DO:
 *  - AM/PM. `h:mm AM/PM` exists (1,128 cells, 1 template), but those values sit on
 *    the 1899 time-only epoch and take the isEpoch branch below, so it is moot
 *    today. If it ever applies, 24-hour wins per standing rule 3 rather than
 *    silently introducing AM/PM.
 *  - Built-in ids. ExcelJS invents PLACEHOLDER text for the built-ins that carry no
 *    format code (14 → "mm-dd-yy", 22 → "m/d/yy h:mm"), which Excel/DevExpress
 *    instead render with the SYSTEM short date. Those are substituted by
 *    univerPattern() BEFORE tokenising (the 2026-08-24 decision), so `mm-dd-yy`
 *    (462 cells / 5 templates) still renders MM/DD/YYYY. Note the placeholder for
 *    id 22 differs from the real custom code above ONLY by the `;@` suffix.
 */
export function formatExcelDateCode(d: Date, code: string): string {
  const f = firstSection(code);
  let out = '';
  let prevWasHour = false;
  for (let i = 0; i < f.length;) {
    const ch = f[i]!;
    if (ch === '\\') { if (i + 1 < f.length) out += f[i + 1]; i += 2; continue; }
    /* A QUOTED literal is text, never tokens — `m/d/yy "h":mm` (1,057 cells / 2
       templates) puts a literal h in quotes, which must not read as an hour. */
    if (ch === '"') { i++; while (i < f.length && f[i] !== '"') { out += f[i]; i++; } i++; continue; }
    if (ch === '[') { while (i < f.length && f[i] !== ']') i++; i++; continue; }
    if (ch === '*' || ch === '_') { i += 2; continue; }
    const ampm = AMPM_RE.exec(f.slice(i));
    if (ampm) { i += ampm[0].length; continue; }
    const lower = ch.toLowerCase();
    if (lower === 'y' || lower === 'm' || lower === 'd' || lower === 'h' || lower === 's') {
      let n = 0;
      while (i < f.length && f[i]!.toLowerCase() === lower) { n++; i++; }
      if (lower === 'y') out += n <= 2 ? pad2(d.getUTCFullYear() % 100) : String(d.getUTCFullYear());
      else if (lower === 'h') { out += n >= 2 ? pad2(d.getUTCHours()) : String(d.getUTCHours()); prevWasHour = true; continue; }
      else if (lower === 's') out += n >= 2 ? pad2(d.getUTCSeconds()) : String(d.getUTCSeconds());
      else if (lower === 'd') {
        out += n >= 4 ? DAYS_FULL[d.getUTCDay()]
          : n === 3 ? DAYS_ABBR[d.getUTCDay()]
            : n === 2 ? pad2(d.getUTCDate()) : String(d.getUTCDate());
      } else if (prevWasHour || nextTokenIsSeconds(f, i)) {
        out += n >= 2 ? pad2(d.getUTCMinutes()) : String(d.getUTCMinutes());
      } else {
        out += n >= 4 ? MONTHS_FULL[d.getUTCMonth()]
          : n === 3 ? MONTHS_ABBR[d.getUTCMonth()]
            : n === 2 ? pad2(d.getUTCMonth() + 1) : String(d.getUTCMonth() + 1);
      }
      prevWasHour = false;
      continue;
    }
    out += ch; i++;
  }
  // `mm/dd/yyyy hh:mm\n` (9 cells / 2 templates) carries a trailing newline.
  return out.replace(/\s+/g, ' ').trim();
}

/** Emit static date cells as Excel serials (see the emission site). `false` restores the pre-2026-09-24
 *  pre-formatted TEXT emission. */
const DATES_AS_SERIALS = true;

/** A JS Date (UTC wall-clock, as ExcelJS reads a serial) → its Excel serial. */
export function excelSerialOf(d: Date): number {
  return (d.getTime() - Date.UTC(1899, 11, 30)) / 86_400_000;
}

/** The Univer pattern for a date serial: the cell's own code (built-ins substituted), made 24-hour
 *  (standing rule 3 — `AM/PM` / `A/P` removed, `h` kept) and, for a time-only value on the 1899 epoch,
 *  reduced to the clock (`hh:mm[:ss]`) so it never shows "12/30/1899". No usable code → the app's
 *  `mm/dd/yyyy hh:mm` / `mm/dd/yyyy` default, as fmtDate uses. */
export function datePatternFor(numFmt: string | undefined, d: Date): string {
  const f = univerPattern(numFmt ?? '');
  const first = firstSection(f);
  const withSeconds = /s/i.test(first.replace(/"[^"]*"/g, ''));
  if (d.getUTCFullYear() === 1899) return withSeconds ? 'hh:mm:ss' : 'hh:mm';
  if (!/[ymdhs]/i.test(first.replace(/"[^"]*"/g, ''))) {
    const hasTime = d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0 || d.getUTCSeconds() !== 0;
    return hasTime ? 'mm/dd/yyyy hh:mm' : 'mm/dd/yyyy';
  }
  return first.replace(/\s*(AM\/PM|A\/P)\s*/gi, '').trim() || 'mm/dd/yyyy hh:mm';
}

export function fmtDate(d: Date, fmt: string | undefined): string {
  // Invalid Date (e.g. an Analysis-Date cell whose value never resolved to a
  // real date) → blank, instead of formatting NaN parts into "NaN/NaN/NaN NaN:NaN".
  if (isNaN(d.getTime())) return '';
  // Substitute the ExcelJS placeholders for Excel's BUILT-IN date ids FIRST, so a
  // built-in keeps rendering the system short date (see univerPattern).
  const f = univerPattern(fmt ?? '');
  const isEpoch = d.getUTCFullYear() === 1899;
  const time = () => { const t = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`; return /s/i.test(f) ? `${t}:${pad2(d.getUTCSeconds())}` : t; };
  const date = () => `${pad2(d.getUTCMonth() + 1)}/${pad2(d.getUTCDate())}/${d.getUTCFullYear()}`;
  // A time-only value sits on the 1899-12-30 epoch: show the clock, never
  // "12/30/1899", whatever the cell's format code asks for.
  if (isEpoch) return time();
  // No usable code (undefined / "General") → the long-standing US default.
  if (!/[ymdhs]/i.test(firstSection(f))) return date();
  const out = formatExcelDateCode(d, f);
  return out === '' ? date() : out;
}
function toDisplay(v: unknown, numFmt: string | undefined): string {
  if (v == null) return '';
  if (v instanceof Date)      return fmtDate(v, numFmt);
  if (typeof v === 'number')  return fmtNumber(v, numFmt);
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  if (typeof v === 'string')  return cleanStr(v);
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (Array.isArray(o.richText)) return cleanStr((o.richText as Array<{ text?: string }>).map(t => t.text ?? '').join(''));
    if ('error'  in o) return String(o.error);
    if ('result' in o) return toDisplay(o.result, numFmt);
    if ('text'   in o) return cleanStr(String(o.text));
    return '';
  }
  return '';
}

/** The effective value of an ExcelJS cell — ALWAYS read a cell through this,
 *  never `cell.value` directly.
 *
 *  ExcelJS builds a formula cell's `cell.value` as `{ formula, result }` but
 *  OMITS the `result` key when the cached result is FALSY.  A formula whose
 *  cached value is exactly 0 therefore arrives as `{ formula }`, `toDisplay`
 *  falls through its object branch to `return ''`, and the cell renders BLANK —
 *  this is why "Numeric Result" showed nothing for a 0 result while legacy
 *  ALPACA GoLive showed "0.00" (bug report 2026-08-14).
 *
 *  `cell.result` keeps the real cached value, so the zero survives, and it is
 *  still `undefined` for a genuinely-empty ("") cached result — so 0 and ""
 *  stay distinguishable with NO guessing.  VERIFIED against the live workbook
 *  QB26081223_042: G7 (XML `<v>0</v>`) → `cell.result === 0`, while the RPD %
 *  / Rec % cells (XML `<v />`) → `cell.result === undefined`.
 *
 *  NOTE the neighbouring `computeMissingQcZeros` comment claims DevExpress
 *  omits the cached `<v>` for a zero.  That is NOT so — the sheet XML does
 *  contain `<v>0</v>`; it is ExcelJS that drops it from `cell.value`.  That
 *  heuristic is left untouched here (it only ADDS zeros for the QC % columns)
 *  but is now redundant for any cell whose zero this accessor recovers. */
function cellValue(cell: ExcelJS.Cell): unknown {
  return cell.type === ExcelJS.ValueType.Formula ? cell.result : cell.value;
}

/**
 * The cell's value as a REAL NUMBER when it is one, else null.
 *
 * WHY THIS EXISTS (client report 2026-08-17: "IS % Of AvgResponse shows green in
 * LIMS but red in Intelligence"). Every cell used to be emitted to Univer as a
 * pre-formatted STRING with `t: CellValueType.STRING`. Under Excel semantics —
 * which Univer's engine follows — TEXT ranks above EVERY NUMBER, so a conditional
 * format comparing that cell against a number inverted:
 *
 *     template rule           text "103.0"              result
 *     between 60,130   →  >=60 true, <=130 FALSE   →  green never fires
 *     greaterThan 130  →  text > number = TRUE     →  RED fires
 *
 * i.e. the whole column painted red whatever the value. Emitting the number with
 * `t: NUMBER` makes the comparison numeric and the template's own rule decides.
 *
 * The DISPLAY is unaffected: the cell's `numFmt` is already carried into the Univer
 * style as `n.pattern`, so Univer formats 103 as "103.0" exactly as `fmtNumber` did.
 *
 * Deliberately NARROW — only a genuine ExcelJS number qualifies:
 *   • a numeric-looking STRING ("2" in the DF column) stays TEXT, because it is text
 *     in the workbook and Excel/DevExpress compare it as text too. Converting it
 *     would change how the template's own rules evaluate.
 *   • PERCENT patterns stay on the string path: `fmtNumber` multiplies by 100 and
 *     appends "%", and handing Univer the raw fraction risks a different rendering.
 *   • DATES stay on the string path: Univer would need a serial number, and
 *     `fmtDate` already reproduces the template's date/time output.
 */
function numericValue(cell: ExcelJS.Cell, override?: unknown): number | null {
  const fromJs = cellValue(cell);
  // `override` is the cached result read from the file for cells ExcelJS drops — see
  // valueOf() at the emission site.
  const v = (fromJs === null || fromJs === undefined || fromJs === '') ? override : fromJs;
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  /* PERCENT cells used to be EXCLUDED here (returned null) so they stayed on the
     string path, on the untested worry that Univer might render the raw fraction
     differently.  That exclusion is what painted every %Recovery / %RPD column the
     WRONG colour: the cell arrived as TEXT, Excel semantics rank text above every
     number, so a "between 70% and 130%" rule could never fire and the "greaterThan
     130%" rule won - pink where ALPACA shows green (client 2026-08-24, QB26052810_032
     Calibration D4:D9, and the same in every template with a percent-formatted
     column).  The worry was measured and is unfounded: across the 2026-08-24 sweep
     every percent-formatted cell renders identically to DevExpress once the
     precision below is applied.  numFmt still reaches Univer as n.pattern, so 1.2
     still displays "120%".

     Excel also carries 15 SIGNIFICANT DECIMAL DIGITS and rounds for display from
     THAT, not from the raw IEEE double: 0.98499999999999988 is 0.985 to Excel and
     shows "99%"; 25.249999999999996 is 25.25 and shows "25.3".  Formatting the
     untrimmed double gives "98%" and "25.2" - the only two value-rendering
     disagreements with DevExpress left in 49,383 formatted cells.  Trim to Excel
     precision so both match.

     Still deliberately NARROW: a numeric-looking STRING stays TEXT (it is text in
     the workbook and DevExpress compares it as text too), and DATES are naturally
     excluded because ExcelJS hands them back as a Date, not a number. */
  return Number(v.toPrecision(15));
}

/** A copy of `style` with any numeric format dropped — see the emission site: a value
 *  we render as TEXT must not carry one, or Univer formats it and Excel does not. */
function withoutNumFmt(style: UniverStyle | undefined): UniverStyle {
  if (!style) return {};
  const { n: _drop, ...rest } = style as Record<string, unknown>;
  return rest;
}

// ── colours (ARGB / theme+tint / indexed) ──────────────────────────────────
const THEME_RGB: Record<number, [number, number, number]> = {
  0: [255, 255, 255], 1: [0, 0, 0], 2: [231, 230, 230], 3: [68, 84, 106],
  4: [68, 114, 196], 5: [237, 125, 49], 6: [165, 165, 165], 7: [255, 192, 0],
  8: [91, 155, 213], 9: [112, 173, 71],
};
const INDEXED_RGB: Record<number, [number, number, number]> = {
  9: [255, 255, 255], 64: [0, 0, 0], 10: [255, 0, 0], 11: [0, 255, 0],
  13: [255, 255, 0], 42: [204, 255, 255], 43: [204, 255, 204], 35: [204, 255, 204],
  50: [153, 204, 0], 17: [255, 255, 204],
};
function applyTint(ch: number, tint: number): number {
  if (!tint) return ch;
  return tint < 0 ? Math.round(ch * (1 + tint)) : Math.round(ch * (1 - tint) + 255 * tint);
}
const toHex = ([r, g, b]: [number, number, number]) =>
  '#' + [r, g, b].map(x => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('').toUpperCase();
function resolveColor(c: { argb?: string; theme?: number; tint?: number; indexed?: number } | undefined): string | null {
  if (!c) return null;
  if (typeof c.argb === 'string' && c.argb.length >= 6) return `#${c.argb.length === 8 ? c.argb.slice(2) : c.argb}`.toUpperCase();
  if (typeof c.theme === 'number' && THEME_RGB[c.theme]) { const b = THEME_RGB[c.theme]!; const t = c.tint ?? 0; return toHex([applyTint(b[0], t), applyTint(b[1], t), applyTint(b[2], t)]); }
  if (typeof c.indexed === 'number' && INDEXED_RGB[c.indexed]) return toHex(INDEXED_RGB[c.indexed]!);
  return null;
}

// ── Univer style for one ExcelJS cell ───────────────────────────────────────
// Univer horizontal align: 1 = left, 2 = center, 3 = right.
type UniverStyle = Record<string, unknown>;
/** Canvas text measurement, memoised — one 2d context for the whole map, and each
 *  (text, font) pair measured once. Without the cache this runs per cell per boot. */
const textWidthCache = new Map<string, number>();
let measureCtx: CanvasRenderingContext2D | null | undefined;
function textWidthPx(text: string, fontPx: number, family: string): number {
  const key = `${fontPx}|${family}|${text}`;
  const hit = textWidthCache.get(key);
  if (hit !== undefined) return hit;
  if (measureCtx === undefined) {
    try { measureCtx = document.createElement('canvas').getContext('2d'); }
    catch { measureCtx = null; }            // headless (tests) — fall back to wrapping
  }
  if (!measureCtx) return Number.POSITIVE_INFINITY;
  measureCtx.font = `${fontPx}px ${family}`;
  const w = measureCtx.measureText(text).width;
  textWidthCache.set(key, w);
  return w;
}

/** True when the cell holds ONE word that fits `colPx`, i.e. wrapping it could only
 *  produce a mid-word break. Unknown width, multiple words, or a word too wide → false,
 *  so the caller keeps Excel's wrap. */
function fitsOneLine(cell: ExcelJS.Cell, colPx: number | undefined): boolean {
  if (!colPx || colPx <= 0) return false;
  /* Read through cellValue/toDisplay like every other read in this file — NEVER
     `cell.text`. ExcelJS's `text` GETTER throws on a MERGED cell whose master value is
     null: MergeValue.toString() does `this._master.value.toString()` with no guard
     (exceljs/lib/doc/cell.js:730). The `?? fallback` here could never fire, because the
     exception is raised evaluating the left operand. One such cell aborted the whole
     workbook conversion, so the sheet rendered COMPLETELY BLANK — reported on
     QB26072912_014 (Total Phosphate, template 8076), whose Sheet1 holds 925 populated
     cells and mapped to nothing. Introduced with this helper on 2026-08-26 and shipped
     in 2899b48; the file's own cellValue() doc says to always read this way. */
  const text = toDisplay(cellValue(cell), cell.numFmt).replace(/_x000d_/gi, ' ').trim();
  if (!text || /\s/.test(text)) return false;                       // blank or multi-word
  const size = typeof cell.font?.size === 'number' ? cell.font.size : 11;
  const family = cell.font?.name || 'Calibri';
  // A little slack for the cell's own left/right padding.
  return textWidthPx(text, size, family) <= colPx - 8;
}

function cellStyle(cell: ExcelJS.Cell, singleLine = false, colPx?: number): UniverStyle | undefined {
  const st: UniverStyle = {};
  const fill = cell.fill as ExcelJS.FillPattern | undefined;
  if (fill?.type === 'pattern' && fill.pattern === 'solid') {
    const bg = resolveColor(fill.fgColor);
    if (bg && bg !== '#FFFFFF') st.bg = { rgb: bg };
  }
  const font = cell.font;
  if (font) {
    const cl = resolveColor(font.color as never);
    if (cl) st.cl = { rgb: cl };
    if (font.bold) st.bl = 1;
    if (typeof font.size === 'number') st.fs = font.size;
    if (font.name) st.ff = font.name;
  }
  const al = cell.alignment?.horizontal;
  if (al === 'left') st.ht = 1; else if (al === 'center') st.ht = 2; else if (al === 'right') st.ht = 3;
  // Vertical align (Univer vt: 1=top, 2=middle, 3=bottom) + text wrap (tb: 3).
  const av = cell.alignment?.vertical;
  if (av === 'top') st.vt = 1; else if (av === 'middle') st.vt = 2; else if (av === 'bottom') st.vt = 3;
  // Wrap long headers like ALPACA — EXCEPT where the row states a height that
  // only fits one line. Excel wraps and then CLIPS to the stated height, so
  // DevExpress shows a single line; Univer's smaller line height let a sliver of
  // the second line through, which read as a half-cut word rather than a clean
  // truncation (client 2026-08-20, the Instrument cell "PerkinElmer UV & Color
  // Lambda 365+" in an 18pt row). Rendering it unwrapped clips at the same place
  // DevExpress does.
  /* A SINGLE WORD that fits its column is never wrapped — a mid-word break is not
     something Excel or DevExpress produce unless the word genuinely exceeds the column.
     "IsReport" rendered as "IsRepor" / "t" while LIMS shows it on one line (client
     2026-08-25, QB26081212_008 Free Residual Chlorine). Measured: the column is 69px and
     the word needs 37.4px at 11px Calibri, so it fits with room to spare — the break was
     Univer laying it out in less width than the column actually has.
     Scoped tightly, because most wrapping here is CORRECT: across 40 live workbooks 132
     multi-word headers wrap legitimately ("Spike Amount (mg/L)", "RPD CtrlLimit") exactly
     as LIMS wraps them, while 55 single-word ones broke mid-word. Only the latter change.
     The word must still FIT — a single word genuinely wider than its column keeps wrapping,
     so nothing that relies on wrapping to stay readable is clipped instead. */
  /* CLIP (2), not overflow (1): headers sit shoulder to shoulder, so if the width
     calculation is ever a pixel optimistic a clipped tail is recoverable while text
     spilling across the neighbouring header is not. The word is only unwrapped when it
     already fits, so in practice nothing is clipped. */
  if (cell.alignment?.wrapText && !singleLine) {
    st.tb = fitsOneLine(cell, colPx) ? 2 : 3;
  }
  // NUMBER FORMAT (fix 2026-08-13): carry the template's numFmt onto EVERY cell, not
  // just formula cells.  The entry columns (e.g. Conductivity Numeric Result) are EMPTY
  // in the template, so they previously arrived with no pattern — a typed `1` rendered
  // as "1" where OG shows "1.00".  Univer applies a numeric pattern only to numbers, so
  // this is inert on the text/literal cells that carry pre-formatted display strings.
  /* A number format applies to NUMBERS, never to text — Excel and DevExpress show a
     TEXT cell verbatim whatever its numFmt says. Several SDMS templates store numeric
     LOOKING values as text while still carrying a pattern: on QB26051911_074 the Spike
     Amount cells are the string "1" with numFmt "0.0". Handing Univer that pattern for
     a text cell rendered "1.0" where LIMS shows "1" (client 2026-08-18). So the pattern
     is attached only when the cell can actually be formatted by it: a real number/date,
     or a FORMULA whose result type is only known once the engine runs. */
  const fmtV = cell.type === ExcelJS.ValueType.Formula ? cell.result : cell.value;
  // An EMPTY entry cell (blank in the template, filled by the analyst or re-merge) must
  // also carry the pattern, or a value later typed/merged into it renders unformatted —
  // MV Reading / Numeric Result showed "1"/"10" where OG shows "1.000"/"10.000" (client
  // 2026-09-15). Univer applies a numeric pattern only to numbers, so the pattern stays
  // inert until a number lands; a numeric-LOOKING TEXT value still has fmtV as a non-empty
  // string (not empty, not number), so it keeps the 2026-08-18 "verbatim text" behaviour.
  const isEmpty = fmtV == null || (typeof fmtV === 'string' && fmtV.trim() === '');
  const formattable =
    hasFormula(cell) || typeof fmtV === 'number' || fmtV instanceof Date || isEmpty;
  if (formattable && cell.numFmt && cell.numFmt !== 'General' && cell.numFmt !== '@')
    st.n = { pattern: univerPattern(cell.numFmt) };
  // Borders: the ALPACA data range carries real borders; map each defined side
  // to Univer's bd (t/b/l/r).  Univer border style: thin=1, hair=2, dotted=3,
  // dashed=4, double=7, medium=8, thick=13.  Stored black is softened to grey.
  const bd: Record<string, unknown> = {};
  const side = (b: { style?: string; color?: unknown } | undefined) => {
    if (!b || !b.style) return undefined;
    const styleMap: Record<string, number> = {
      hair: 2, thin: 1, dotted: 3, dashed: 4, dashDot: 5, dashDotDot: 6,
      double: 7, medium: 8, mediumDashed: 9, mediumDashDot: 10, thick: 13, slantDashDot: 5,
    };
    // ALPACA/LIMS draws the data-range grid in solid BLACK — force black on
    // every defined border side (no softening to grey).
    return { s: styleMap[b.style] ?? 1, cl: { rgb: '#000000' } };
  };
  const bdr = cell.border;
  if (bdr) {
    const t = side(bdr.top), bo = side(bdr.bottom), l = side(bdr.left), r = side(bdr.right);
    if (t) bd.t = t; if (bo) bd.b = bo; if (l) bd.l = l; if (r) bd.r = r;
  }
  if (Object.keys(bd).length) st.bd = bd;
  return Object.keys(st).length ? st : undefined;
}

// ── "B12" range → Univer merge range ────────────────────────────────────────
function cellRef(ref: string): { r: number; c: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec(ref.trim().toUpperCase()); if (!m) return null;
  let c = 0; for (const ch of m[1]!) c = c * 26 + (ch.charCodeAt(0) - 64);
  return { r: parseInt(m[2]!, 10) - 1, c: c - 1 };
}
function parseMerge(ref: string): { startRow: number; startColumn: number; endRow: number; endColumn: number } | null {
  const [a, b] = ref.split(':'); const s = cellRef(a ?? ''); const e = b ? cellRef(b) : s;
  if (!s || !e) return null;
  return { startRow: Math.min(s.r, e.r), startColumn: Math.min(s.c, e.c), endRow: Math.max(s.r, e.r), endColumn: Math.max(s.c, e.c) };
}
function widthToPx(w: number | undefined): number { return !w || w <= 0 ? 88 : Math.round(w * 7 + 5); }

/* Univer's own gutters: the row-number column down the left and the column-letter
   strip across the top. Cell (0,0) starts after both, so any overlay positioned in
   grid coordinates has to add them. These match Univer's defaults, and are the same
   46px the chart float's CSS was hand-tuned to before it became anchor-driven. */
/** Above this many NEVER-COMPUTED formula cells on one sheet, load it as VALUES instead of
 *  handing the whole sheet to Univer's formula engine. See the note at `sheetNeedsFormulas`. */
const MAX_RECALC_FORMULA_CELLS = 200;

const UNIVER_ROW_HEADER_PX = 46;
const UNIVER_COL_HEADER_PX = 20;

function colLetter(n: number): string {
  let s = ''; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = (n - 1 - m) / 26; } return s || 'A';
}

/** Rows below the last used row that a bounded whole-column ref must still cover. */
export const WHOLE_COL_HEADROOM_ROWS = 200;

/**
 * Rewrite whole-COLUMN references (`AF:AF`, `$B:$B`) to end at `lastRow`, leaving every
 * other reference untouched. See the call site for why this exists.
 *
 * Deliberately narrow, because a wrong rewrite here would silently change a QC RESULT
 * rather than merely be slow:
 *   • only a colon between two COLUMN-ONLY refs matches — `A1:B10` carries digits and is
 *     left alone, as is a lone `AF8`;
 *   • quoted STRING literals are stepped over, so a formula containing "a:b" or a
 *     criterion like ">=1:2" is never touched;
 *   • a sheet-qualified ref keeps its prefix: `Sheet1!A:A` -> `Sheet1!A$1:A$220`;
 *   • `$` anchoring on the column letters is preserved, and the rows are anchored so the
 *     range cannot drift if Univer shifts the formula.
 */
export function boundWholeColumnRefs(formula: string, lastRow: number,
  /** The row a range on ANOTHER sheet may run to (that sheet's data + headroom), by sheet name; undefined = use
   *  `lastRow`. A summary sheet's `INDEX(Sheet1!K272:K315, …)` / `MATCH(…, Sheet1!A:A, 0)` point at Sheet1's
   *  rows, and clipping them to the SUMMARY's own few rows cut them off: PFAS 8114's Sample-Oriented Summary
   *  showed the same sample on every row past Sheet1 row ~270, `#N/A` results and blank Analyzed Dates
   *  (parity run 2026-09-30). */
  lastRowOfSheet?: (sheetName: string) => number | undefined,
): string {
  if (!formula.includes(':')) return formula;
  const own = Math.max(1, Math.floor(lastRow));
  // `pre` = the ref's sheet qualifier incl. the `!` ("Sheet1!", "'CCV1'!"), or undefined for this sheet.
  const endFor = (pre: string | undefined): number => {
    if (!pre || !lastRowOfSheet) return own;
    const raw = pre.slice(0, -1);
    const name = raw.startsWith("'") ? raw.slice(1, -1).replace(/''/g, "'") : raw;
    const r = lastRowOfSheet(name);
    // Never tighter than before: a range only ever GROWS, and only when the other sheet holds more rows (a
    // smaller cap could change a function that counts blanks).
    return r === undefined ? own : Math.max(own, Math.floor(r));
  };
  const SHEET = String.raw`((?:'(?:[^']|'')+'|[A-Za-z_][\w.]*)!)?`;

  const clipCode = (code: string): string =>
    code
      // (1) WHOLE-COLUMN: `AF:AF`, `$B:$B` — no row numbers at all.
      .replace(
        new RegExp(SHEET + String.raw`(\$?)([A-Z]{1,3})\s*:\s*(\$?)([A-Z]{1,3})(?![A-Z0-9_])`, 'g'),
        (whole: string, pre: string | undefined, d1: string, c1: string, d2: string, c2: string,
         offset: number, src: string) => {
          // A digit right before means this is the tail of `A1:B10`, not a column ref.
          if (!pre && /[0-9]/.test(src.slice(Math.max(0, offset - 1), offset))) return whole;
          return `${pre ?? ''}${d1}${c1}$1:${d2}${c2}$${endFor(pre)}`;
        },
      )
      // (2) EXPLICIT FULL-HEIGHT: `AG$1:AG$1048452`. This is the form these templates
      //     actually use — measured, after (1) alone changed nothing on 3 of 4 real
      //     workbooks. Clip ONLY the end row, and only when it reaches past the data;
      //     a range that already ends inside the used rows is left exactly as written.
      .replace(
        new RegExp(SHEET + String.raw`(\$?[A-Z]{1,3}\$?)(\d+)\s*:\s*(\$?[A-Z]{1,3}\$?)(\d+)`, 'g'),
        (whole: string, pre: string | undefined, a: string, r1: string, b: string, r2: string) => {
          const end = endFor(pre);
          return Number(r2) > end ? `${pre ?? ''}${a}${r1}:${b}${end}` : whole;
        },
      );

  // Split on quoted literals KEEPING them (capture group), then rewrite only the code.
  return formula
    .split(/("(?:[^"]|"")*")/)
    .map((part, i) => (i % 2 === 1 ? part : clipCode(part)))   // odd chunks are literals
    .join('');
}

/** A1 column letters → 1-based index (AO → 41). Inverse of colLetter; used by the
 *  THM self-column constant-fold resolver to read a merged cell by its A1 ref. */
function colLettersToNum(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

/** AutoFilter A1 range for a sheet: the template's own autoFilter, else the
 *  sample data-table header row detected by "SysSampleCode". */
export function filterRange(ws: ExcelJS.Worksheet): string | null {
  const af = (ws as unknown as { autoFilter?: unknown }).autoFilter;
  const addr = (p: unknown): string | null => {
    if (!p) return null;
    if (typeof p === 'string') return p;
    const o = p as { row?: number; column?: number; address?: string };
    if (typeof o.row === 'number' && typeof o.column === 'number') return `${colLetter(o.column)}${o.row}`;
    return o.address ?? null;
  };
  /* A template autoFilter is its HEADER row only (VOC 8086 Sequence "B9:H9", Summary "B9:J9"); the merge
     puts the data under it. Univer refuses a one-row filter with an error toast ("The selected range only
     has one row and not valid for filter", user 2026-09-28), so extend it down to the last row holding a
     value in its columns — the range a filter covers in OG. */
  const extendDown = (range: string): string => {
    const m = /^\$?([A-Z]+)\$?(\d+):\$?([A-Z]+)\$?(\d+)$/i.exec(range);
    if (!m || m[2] !== m[4]) return range;
    const toNum = (s: string) => { let n = 0; for (const ch of s.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64); return n; };
    const r0 = Number(m[2]), c1 = toNum(m[1]!), c2 = toNum(m[3]!);
    let last = r0;
    ws.eachRow({ includeEmpty: false }, (row, rn) => {
      if (rn <= r0) return;
      for (let c = c1; c <= c2; c++) {
        const cell = row.getCell(c);
        if (toDisplay(cellValue(cell), cell.numFmt).trim() !== '') { last = rn; break; }
      }
    });
    return `${m[1]}${r0}:${m[3]}${last}`;
  };
  if (typeof af === 'string' && af.includes(':')) return extendDown(af);
  if (af && typeof af === 'object') {
    const o = af as { from?: unknown; to?: unknown };
    const from = addr(o.from), to = addr(o.to);
    if (from && to) return extendDown(`${from}:${to}`);
  }
  const rows = Math.max(1, ws.rowCount), cols = Math.max(1, ws.columnCount);
  const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
  for (let r = 1; r <= Math.min(rows, 20); r++)
    for (let c = 1; c <= cols; c++) {
      const cell = ws.getRow(r).getCell(c);
      if (norm(toDisplay(cellValue(cell), cell.numFmt)) === 'syssamplecode') {
        /* END ON THE LAST ROW THAT HOLDS DATA, not ws.rowCount. Univer draws a border
           around the filter range, so any row included past the data renders as a
           stray rule under the table — an "extra line" with nothing in it (client
           2026-08-25, QB26081212_008: data ends at row 16, ws.rowCount is 17, and the
           synthesised range A9:AP17 drew a line across row 17).
           ws.rowCount is the last row ExcelJS sees ANY cell on, including ones that
           carry only leftover formatting and no value — the same trap as bounding a
           whole-column reference to it. Column A holds SysSampleCode on every data
           row, so the last non-empty A is exactly where the table ends. */
        let lastRow = r;
        for (let rr = r + 1; rr <= rows; rr++) {
          const a = ws.getRow(rr).getCell(1);
          if (toDisplay(cellValue(a), a.numFmt).trim() !== '') lastRow = rr;
        }
        /* AND END ON THE LAST COLUMN THAT HOLDS DATA, for exactly the same reason.
           ws.columnCount is the sheet's DIMENSION, not its used range: it counts any
           column ExcelJS has seen a cell on, including ones carrying only leftover
           formatting. Univer draws one filter caret per COLUMN of the range, so the
           surplus renders as a row of empty dropdown buttons stretching past the table
           — which ALPACA does not draw (client 2026-08-26, QB26072111_060 / VOC
           template 8086, Sample-Oriented Summary: data ends at column 67 (BO) but
           ws.columnCount is 159 (FC), so 93 empty carets were drawn from BP onward).
           Scan the DATA BLOCK rather than the header row alone: the last column can
           carry values under a blank header (BO holds the Counts figures here), and
           that column is still part of the table. eachCell skips empty cells, so this
           costs a pass over populated cells only, not rows x 159. */
        let lastCol = 1;
        for (let rr = r; rr <= lastRow; rr++)
          ws.getRow(rr).eachCell({ includeEmpty: false }, (cell, colNumber) => {
            if (toDisplay(cellValue(cell), cell.numFmt).trim() !== '') lastCol = Math.max(lastCol, colNumber);
          });
        return `${colLetter(1)}${r}:${colLetter(lastCol)}${lastRow}`;
      }
    }
  return null;
}

/** 1-based row of the sample data-table header ("SysSampleCode"), else -1.
 *  Everything from row 1 through this row (title + Test/Method/Instrument/Remark
 *  metadata + the column headers) is frozen so only the data rows scroll —
 *  ALPACA's SDMS view keeps that block pinned. */
function headerRow(ws: ExcelJS.Worksheet): number {
  const rows = Math.max(1, ws.rowCount), cols = Math.max(1, ws.columnCount);
  const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
  for (let r = 1; r <= Math.min(rows, 20); r++)
    for (let c = 1; c <= cols; c++) {
      const cell = ws.getRow(r).getCell(c);
      if (norm(toDisplay(cellValue(cell), cell.numFmt)) === 'syssamplecode') return r;
    }
  return -1;
}

/** Deterministic QC highlights (ported from the ExcelJS HTML render) so the
 *  green cues appear under Univer even when they come from conditional
 *  formatting rather than a static fill.  Returns "r,c" (1-based) → hex bg. */
function computeQcGreens(ws: ExcelJS.Worksheet, qcZeros?: Set<string>): Map<string, string> {
  const GREEN = '#C6EFCE', RED = '#FFC7CE';
  const out = new Map<string, string>();
  const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
  const rows = Math.max(1, ws.rowCount), cols = Math.max(1, ws.columnCount);
  const text = (r: number, c: number) => { const cell = ws.getRow(r).getCell(c); return toDisplay(cellValue(cell), cell.numFmt); };
  let hdr = -1;
  for (let r = 1; r <= Math.min(rows, 20) && hdr < 0; r++)
    for (let c = 1; c <= cols; c++) { const t = norm(text(r, c)); if (t === 'syssamplecode' || t.includes('recovery')) { hdr = r; break; } }
  if (hdr < 0) return out;
  const H: string[] = []; for (let c = 1; c <= cols; c++) H[c] = norm(text(hdr, c));
  const findCol = (pred: (h: string) => boolean) => { for (let c = 1; c <= cols; c++) { const h = H[c]; if (h && pred(h)) return c; } return -1; };
  const qcCol = findCol(h => h === 'qcstatus');
  const recCol = findCol(h => h.includes('rec') && h.includes('%') && !h.includes('limit') && !h.includes('ctrl'));
  const rpdCol = findCol(h => h.includes('rpd') && h.includes('%') && !h.includes('limit') && !h.includes('ctrl'));
  const recLim = findCol(h => h.includes('rec') && (h.includes('limit') || h.includes('ctrl')));
  const rpdLim = findCol(h => h.includes('rpd') && (h.includes('limit') || h.includes('ctrl')));
  const recoveryCol = findCol(h => h.includes('recovery'));
  const num = (t: string) => { const n = parseFloat(t); return isNaN(n) ? null : n; };
  const rng = (t: string): [number, number] | null => { const m = t.match(/(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)/); return m ? [parseFloat(m[1]!), parseFloat(m[2]!)] : null; };
  const within = (valCol: number, limCol: number, r: number) => {
    if (valCol < 0) return;
    // A dropped-zero QC cell (§computeMissingQcZeros — RPD or Rec) reads blank
    // from the cache but its true value is 0 — evaluate the limit against 0 so an
    // in-range value gets the green cue (RPD 0 ≤ 20 → green; a Rec 0 is out of a
    // 90–110 range → correctly NOT green).
    const v = qcZeros?.has(`${r},${valCol}`) ? 0 : num(text(r, valCol));
    if (v == null) return;
    const lim = limCol >= 0 ? text(r, limCol) : ''; const g = rng(lim);
    let ok = false; if (g) ok = v >= Math.min(...g) && v <= Math.max(...g); else { const s = num(lim); if (s != null) ok = v <= s; }
    if (ok) out.set(`${r},${valCol}`, GREEN);
  };
  for (let r = hdr + 1; r <= rows; r++) {
    if (qcCol >= 0) { const v = text(r, qcCol).trim().toLowerCase(); if (v === 'pass') out.set(`${r},${qcCol}`, GREEN); else if (v === 'fail') out.set(`${r},${qcCol}`, RED); }
    within(recCol, recLim, r); within(rpdCol, rpdLim, r);
    if (recoveryCol >= 0 && num(text(r, recoveryCol)) != null) out.set(`${r},${recoveryCol}`, GREEN);
  }
  return out;
}

/** Does this ExcelJS cell hold a FORMULA?  Covers both shapes: the `formula`
 *  getter (normal + shared-formula slave cells, which ExcelJS translates) and the
 *  value-object form `{ formula }` / `{ sharedFormula }`. */
function hasFormula(cell: ExcelJS.Cell): boolean {
  const f = (cell as unknown as { formula?: unknown }).formula;
  if (typeof f === 'string' && f.length > 0) return true;
  const v = cell.value;
  return v !== null && typeof v === 'object'
    && ('formula' in (v as object) || 'sharedFormula' in (v as object));
}

/** SampleType values for which the RPD formula yields a NUMBER (a QC-comparison
 *  row: Dup/Duplicate use the duplicate branch, LCSD/MSD use the recovery-RPD
 *  branch).  For every other row the formula yields "" (blank). */
const RPD_QC_TYPES = new Set(['dup', 'duplicate', 'lcsd', 'msd', 'lcsd_filtered']);

/** SampleTypes (exact) for which the Rec % formula yields a NUMBER via its
 *  spike-recovery / calibration branch (AX/N*100). */
const REC_EXACT_TYPES = new Set(['lcs', 'lcsd', 'lcs_filtered', 'lcsd_filtered', 'ccv', 'loq', 'icv']);

/** Whether the Rec % formula produces a number for this (normalised) SampleType.
 *  Mirrors the template formula: SEARCH("MS"/"MSD") (→ any type containing "ms"),
 *  the exact LCS/CCV/LOQ/ICV set, and the CCV Low/Mid/High variants. */
const recProducesNumber = (st: string): boolean =>
  st.includes('ms') || REC_EXACT_TYPES.has(st)
  || st.includes('ccvlow') || st.includes('ccvmid') || st.includes('ccvhigh');

/** DevExpress omits the cached <v> for a formula result of exactly 0 (and for
 *  ""), so ExcelJS returns such cells as `{ formula }` with NO `result` key —
 *  indistinguishable from a genuinely-blank "" result by the cache alone.  The
 *  QC % columns are where this matters: on a QC row the formula returns a NUMBER
 *  (RPD is 0 when the dup and its parent are both non-detect, e.g. Result "<0.04"
 *  → ISNUMBER(G)=FALSE → 0; Rec is 0 when the spike isn't recovered), which must
 *  render as "0.0" to match legacy ALPACA; on any other row it returns "" and
 *  must stay blank.  We disambiguate by row type (SampleType) — never by the
 *  missing cache — so only genuine QC rows get the zero.  Returns "r,c" (1-based)
 *  of the RPD % and Rec % cells that lost their cached zero. */
function computeMissingQcZeros(ws: ExcelJS.Worksheet, uncached?: Set<string>): Set<string> {
  const out = new Set<string>();
  const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
  const rows = Math.max(1, ws.rowCount), cols = Math.max(1, ws.columnCount);
  const text = (r: number, c: number) => { const cell = ws.getRow(r).getCell(c); return toDisplay(cellValue(cell), cell.numFmt); };
  let hdr = -1;
  for (let r = 1; r <= Math.min(rows, 20) && hdr < 0; r++)
    for (let c = 1; c <= cols; c++) { const t = norm(text(r, c)); if (t === 'syssamplecode') { hdr = r; break; } }
  if (hdr < 0) return out;
  const H: string[] = []; for (let c = 1; c <= cols; c++) H[c] = norm(text(hdr, c));
  const findCol = (pred: (h: string) => boolean) => { for (let c = 1; c <= cols; c++) { const h = H[c]; if (h && pred(h)) return c; } return -1; };
  const rpdCol = findCol(h => h.includes('rpd') && h.includes('%') && !h.includes('limit') && !h.includes('ctrl'));
  const recCol = findCol(h => h.includes('rec') && h.includes('%') && !h.includes('limit') && !h.includes('ctrl'));
  const typeCol = findCol(h => h === 'sampletype');
  const numResCol = findCol(h => h.startsWith('numericresult'));
  if (typeCol < 0) return out;
  /* A formula cell whose cached value DevExpress DROPPED — it omits `<v>` entirely for a
     zero result, so the cell really is uncached in the file.
     ExcelJS alone CANNOT establish that: it omits `result` for `<v/>` too, and `<v/>`
     means the opposite — computed, answer "". Keying on `!('result' in raw)` therefore
     called every deliberately-blank cell a dropped zero, gave it the value 0, and since
     0 sits inside the RPD control limit it painted blank RPD % cells GREEN where ALPACA
     leaves them white (client 2026-08-24). `uncached` comes from the raw XML and knows
     the difference; without it (unreadable workbook) the old behaviour stands. */
  const droppedZero = (r: number, c: number): boolean => {
    const raw = ws.getRow(r).getCell(c).value as Record<string, unknown> | null;
    const looksDropped = !!raw && typeof raw === 'object' && typeof raw.formula === 'string' && !('result' in raw);
    if (!looksDropped) return false;
    return uncached ? uncached.has(`${r},${c}`) : true;
  };
  for (let r = hdr + 1; r <= rows; r++) {
    const st = norm(text(r, typeCol));
    // Both formulas return "" for a row with no numeric result yet — require a
    // populated NumericResult so an un-analyzed QC row stays blank (formula parity).
    if (numResCol >= 0 && text(r, numResCol).trim() === '') continue;
    if (rpdCol >= 0 && RPD_QC_TYPES.has(st) && droppedZero(r, rpdCol)) out.add(`${r},${rpdCol}`);
    if (recCol >= 0 && recProducesNumber(st) && droppedZero(r, recCol)) out.add(`${r},${recCol}`);
  }
  return out;
}

// ── ExcelJS workbook → Univer IWorkbookData (loosely typed) ─────────────────
/** Also returns `locks`: per sheet NAME, the set of `"row,col"` (0-based, Univer
 *  coordinates) cells that carry a formula in the source workbook.  Those are the
 *  cells OG protects — see the SDMS_PROTECTED_MSG note and the edit guard below. */
// Exported for tests only (vitest drives the real conversion on real merged
// workbooks — see the SDMS ISTD investigation 2026-08-14); the app itself uses
// it solely via <SdmsUniverSheet>.
export async function excelToUniver(buf: ArrayBuffer, withFormulas = true, dfOverlay?: SdmsDfOverlay, qcBatchId?: string,
  /** Called after each sheet is mapped (done, total) — the host panel's "Building the sheet" percent. When
   *  given, the mapping yields a macrotask per sheet so that percent can paint (user 2026-09-28). */
  onProgress?: (done: number, total: number) => void): Promise<{
  data: Record<string, unknown>;
  filters: Record<string, string>;
  locks: Record<string, Set<string>>;
  /** Per-sheet Excel conditional-formatting blocks + the merged data band they
   *  apply to, applied AFTER createWorkbook (the CF facade needs a live range). */
  cfs: Record<string, { blocks: unknown[]; bandStart: number; bandEnd: number }>;
  /** Per-sheet Excel DATA VALIDATION list rules (the in-cell dropdowns), applied
   *  AFTER createWorkbook like the CF blocks — the validation facade also needs a
   *  live FRange. */
  dvs: Record<string, SheetDvRule[]>;
  /** Per-sheet CACHED results of formula cells, keyed `row,col` (0-based). Univer
   *  recomputes a cached cell once an uncached neighbour makes it dirty, and its
   *  answer can differ from the one DevExpress stored — these are what we put back. */
  cachedFormulaValues: Record<string, Record<string, { v: unknown; t: number }>>;
  /** Per-sheet pixel box of the template's embedded chart, from its own
   *  `twoCellAnchor` measured against the widths/heights THIS build emitted. The
   *  SDMS view floats a reconstructed Recharts curve there (Univer cannot render an
   *  embedded Excel chart), so a hardcoded box came out the wrong size — see
   *  sdmsChartAnchor.ts. Absent for a sheet with no drawing. */
  chartBoxes: Record<string, AnchorBox>;
}> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  // Each sheet's last row that holds a value, by lower-cased name — the bound for a whole-column / full-height
  // range that points at THAT sheet from another one (boundWholeColumnRefs `lastRowOfSheet`).
  const lastValueRowBySheet = new Map<string, number>();
  for (const w of wb.worksheets) {
    let last = 0;
    w.eachRow({ includeEmpty: false }, (_row, n) => { if (n > last) last = n; });
    lastValueRowBySheet.set(w.name.toLowerCase(), last);
  }
  const visibleWs = wb.worksheets.filter(w => ((w.state as string | undefined) ?? 'visible') === 'visible');
  // Which sheets truly need recalculating (read from the file - see sdmsFormulaCache.ts).
  const cacheInfo = await readFormulaCacheInfo(buf);
  const uncachedCells = cacheInfo?.uncached ?? null;
  // A sheet needs recalculating only if it holds a formula cell that was never computed.
  const sheetsNeedingRecalc = withFormulas || !uncachedCells
    ? null
    : new Set([...uncachedCells].filter(([, set]) => set.size).map(([name]) => name));
  const locks: Record<string, Set<string>> = {};
  const cfs: Record<string, { blocks: unknown[]; bandStart: number; bandEnd: number }> = {};
  const dvs: Record<string, SheetDvRule[]> = {};
  const cachedFormulaValues: Record<string, Record<string, { v: unknown; t: number }>> = {};
  /** The widths/heights each sheet ENDED UP with, so the chart anchor is measured
   *  against what Univer really renders rather than against Excel's own numbers. */
  const geom: Record<string, { colW: number[]; rowH: number[] }> = {};

  const styles: Record<string, UniverStyle> = {};
  const styleKeyToId = new Map<string, string>();
  let styleSeq = 0;
  /* EMPTY STYLED CELLS (speed, 2026-10-01). The templates carry large pre-styled EMPTY grids — VOC's two summary
     sheets alone are ~133,000 such cells — and each one rebuilt its style and JSON-keyed it here. An empty,
     unmerged, unhighlighted cell's style depends only on its ExcelJS style object (cells sharing an <xf> share it),
     on whether its row is single-line, and on where it sits in the data box; so the FIRST such cell takes the normal
     path below and records its style id, and every later one with the same three reuses it. Same output by
     construction — the cache only ever returns what the normal path produced for an identical input. */
  const emptyStyleIds = new Map<object, Map<number, string | undefined>>();
  const addStyle = (s: UniverStyle | undefined): string | undefined => {
    if (!s) return undefined;
    const key = JSON.stringify(s);
    let id = styleKeyToId.get(key);
    if (!id) { id = `s${styleSeq++}`; styleKeyToId.set(key, id); styles[id] = s; }
    return id;
  };

  const sheets: Record<string, unknown> = {};
  const sheetOrder: string[] = [];
  const filters: Record<string, string> = {};

  const mapSheet = (ws: ExcelJS.Worksheet, idx: number): void => {
    const sheetId = `sheet-${idx}`;
    sheetOrder.push(sheetId);
    const totalCols = Math.max(1, ws.columnCount);
    const totalRows = Math.max(1, ws.rowCount);
    // Template-faithful defaults for columns/rows the file DOESN'T size explicitly.
    // Excel falls back to the sheet's defaultColWidth / defaultRowHeight (Calibri 11:
    // 8.43 chars ≈ 64px, 15pt ≈ 20px), NOT the arbitrary 88px/22px this code used —
    // which over-widened every unsized column and under-sized unsized rows across all
    // templates (users reported columns/cells not matching the template). (2026-09-16)
    const wsProps = (ws as unknown as { properties?: { defaultColWidth?: number; defaultRowHeight?: number } }).properties;
    const defColWidthPx  = wsProps?.defaultColWidth && wsProps.defaultColWidth > 0 ? widthToPx(wsProps.defaultColWidth) : 64;
    const defRowHeightPx = wsProps?.defaultRowHeight && wsProps.defaultRowHeight > 0 ? Math.round(wsProps.defaultRowHeight * 1.34) : 20;
    const qcZeros = computeMissingQcZeros(ws, uncachedCells?.get(ws.name));
    const greens = computeQcGreens(ws, qcZeros);

    const cellData: Record<number, Record<number, unknown>> = {};
    const columnData: Record<number, unknown> = {};
    const rowData: Record<number, unknown> = {};


    /** Rendered width of each column, 1-based — read below by fitsOneLine() to decide
     *  whether a single-word cell can stay on one line instead of breaking mid-word. */
    const colPxByIndex: number[] = [];
    for (let c = 1; c <= totalCols; c++) {
      const col = ws.getColumn(c);
      const cd: Record<string, unknown> = {};
      // Template-hidden columns: use Univer's hidden flag (hd:1) — it fully
      // collapses the column (no width).  (Zero-width instead left a clamped
      // sliver with the header text wrapped vertically, which looked worse.)
      if (col.width) cd.w = widthToPx(col.width);
      colPxByIndex[c] = (cd.w as number | undefined) ?? defColWidthPx;
      // Hide template-hidden cols AND any config column past the data range.
      // The FILE's own flag decides, and nothing else — which is what ALPACA does:
      // it hides spreadsheet columns nowhere in code, so what you see is what the
      // template author marked hidden.
      //
      // This used to ALSO hide everything past the autofilter, to suppress a
      // "Result Rounding" config block some templates park to the right of the data.
      // That over-reached: on Anions_IC (template 1014) the filter ends at BA, and the
      // helper columns BB..BH are ALREADY marked hidden="1" in the file, while BI
      // (UCL) and BJ (QCRole) are NOT — the author hid what should be hidden and left
      // the rest. We hid those two anyway, and the client saw them in ALPACA and not
      // here (2026-08-20). Two further templates lose real columns the same way: 8062
      // (IS Report, AnalysisDate, Analyst, EnteredDate, EnteredBy, MDL, UQL, MCL) and
      // 8086 (DupSource, SpikeSource, AnalysisDate, Analyst, …).
      //
      // The rule was also self-inconsistent: it only reached columns inside the used
      // range, so EMPTY columns past it (BK, BL, BM) rendered anyway while real data
      // two columns earlier did not. Checked all 38 templates in use — no unhidden
      // config block exists in any saved sheet, so nothing is unmasked by this.
      if (col.hidden) cd.hd = 1;
      if (Object.keys(cd).length) columnData[c - 1] = cd;
    }

    // Data-table bounds: the "SysSampleCode" header row down to the last
    // populated SysSampleCode row, across the columns the header spans.  Every
    // cell inside this box gets the SAME thin black border on all four sides so
    // the grid is uniform (LIMS look) — overriding the template's partial borders
    // and Univer's cool-grey default gridlines so no line reads blue.
    const hr = headerRow(ws);
    let lastDataCol = 0, lastDataRow = hr;
    if (hr > 0) {
      for (let c = 1; c <= totalCols; c++)
        if (toDisplay(cellValue(ws.getRow(hr).getCell(c)), ws.getRow(hr).getCell(c).numFmt).trim() !== '') lastDataCol = c;
      for (let r = hr; r <= totalRows; r++)
        if (toDisplay(cellValue(ws.getRow(r).getCell(1)), ws.getRow(r).getCell(1).numFmt).trim() !== '') lastDataRow = r;
    }
    // ── DF overlay column map (see SdmsDfOverlay) ────────────────────────────
    // Resolve the SysSampleCode / Parameter / DF columns BY HEADER LABEL off the
    // same header row the grid box uses — never a hardcoded index, so a
    // re-templated sheet still maps (or maps to nothing and the overlay no-ops).
    let ovSysCol = 0, ovParamCol = 0, ovDfCol = 0;

    if (dfOverlay && dfOverlay.size > 0 && hr > 0) {
      const normH = (s: string) => s.replace(/\s+/g, '').toLowerCase();
      for (let c = 1; c <= totalCols; c++) {
        const hc = ws.getRow(hr).getCell(c);
        const h = normH(toDisplay(cellValue(hc), hc.numFmt));
        if (h === 'syssamplecode') ovSysCol = c;
        else if (h === 'parameter') ovParamCol = c;
        else if (h === 'df')        ovDfCol = c;
      }
    }
    const overlayActive = ovSysCol > 0 && ovParamCol > 0 && ovDfCol > 0;

    /* DOES THIS SHEET NEED ITS FORMULAS?
       Read-only normally ships VALUES ONLY (SDMS_LAZY_FORMULAS) - fast, and fine
       while the workbook carries cached results. When it does NOT, value-only is
       WRONG, and not only for the uncached cell itself: stripping formulas turns
       its neighbours into GENUINELY EMPTY cells, so an ISBLANK() test flips.
       That is the QCStatus bug (client 2026-08-18, Ammonia_ISE). Its formula opens
         IF(OR(ISBLANK(<RPD%>), ISBLANK(<Rec%>), ISBLANK(<NumericResult>)), " ", ...)
       and in Excel/DevExpress ISBLANK is FALSE for a cell holding a FORMULA even
       when that formula returns "". Emitting only the uncached cells left their
       dependencies empty, ISBLANK went TRUE, and those rows returned " " - which is
       why the first attempt lit up only the two rows whose inputs happened to hold
       real values. So the decision is PER SHEET: if any formula cell lacks a cached
       result, hand Univer the whole formula graph and let it compute, exactly as
       DevExpress recalculates on load. Fully-cached sheets keep the fast path. */
    /* READ FROM THE FILE, not inferred from ExcelJS. A formula cell with `<v/>` HAS a
       cached result — DevExpress computed it and the answer was the empty string — while
       a cell with no `<v>` at all was never computed. ExcelJS reports `undefined` for
       both, so the old test ("does any formula cell display empty?") counted every
       deliberately-blank cell as uncached: 820 of 1,227 on the Metals_ICP_OES template,
       none of them genuinely uncached. That made Univer recompute the whole sheet, which
       a headless measurement clocked at 185 SECONDS — the sheet "hanging" on scroll
       (client 2026-08-24) — and left conditional formatting testing cells that had not
       computed yet, which is why the QC colours were missing. See sdmsFormulaCache.ts.
       `null` means the workbook could not be read; fall back to the old heuristic rather
       than risk a sheet that genuinely needs computing rendering blank. */
    /* PER CELL, not per sheet. DevExpress recalculates on load, so a cell whose cached
       answer is EMPTY can come back with a value once the merge has written new data —
       that is why skipping recalculation wholesale left 125 cells blank across 8 templates
       where LIMS shows a value. But a cell with a real cached value cannot change, so
       recomputing it is pure cost: on this template that is 407 of 1,227 cells, and asking
       Univer for all of them took 185 SECONDS. Hand Univer ONLY the cells whose answer is
       still open, and give it the cached value for the rest. */
    /* NOT used to decide what to compute — see the note at `needsCompute`. Handing Univer
       the 820 cells whose cached answer is empty was tried and measured: 647 SECONDS,
       worse than recomputing all 1,227 (185 s), because cost tracks a handful of
       whole-column AVERAGEIFS rather than the number of formulas. The cells those
       formulas were meant to fill are recovered from the file instead (valueOf below),
       which costs nothing. */
    const unresolvedHere: Set<string> | null = null;
    /* ExcelJS LOSES some cached results. A formula returning a STRING in a cell whose
       number format is a DATE ("mm/dd/yyyy hh:mm") comes back as `result: null` — it tries
       to coerce the text to a Date and gives up — even though the file plainly holds
       `<v>08/13/2026 12:37</v>` and LIMS shows it. That is 125 blank cells across 8
       templates (client 2026-08-24). The XML pass already read those values, so fall back
       to them whenever ExcelJS hands us nothing. */
    const rawCached = cacheInfo?.cachedValues.get(ws.name);
    const valueOf = (cell: ExcelJS.Cell, r: number, c: number): unknown => {
      const v = cellValue(cell);
      /* Key off what the value RENDERS AS, not off null-ness. ExcelJS hands back the whole
         `{ formula, result: null }` object for these cells — non-null, so a null check
         passes it straight through, and only toDisplay later resolves it to "". */
      if (toDisplay(v, cell.numFmt) !== '') return v;
      const raw = rawCached?.get(`${r},${c}`);
      if (!raw) return v;
      if (raw.t === undefined) {                       // no `t` attribute = numeric result
        const n = Number(raw.v);
        return Number.isFinite(n) ? n : raw.v;
      }
      return raw.v;                                    // "str" (literal text) or "e" (error)
    };
    let sheetNeedsFormulas = false;
    if (!withFormulas) {
      if (unresolvedHere) {
        sheetNeedsFormulas = false;               // decided per cell at the emission site
      } else if (sheetsNeedingRecalc) {
        /* CAP THE WHOLE-SHEET RECALCULATION — a frozen sheet is worse than a blank cell.
           One uncached cell puts the ENTIRE sheet into live-formula mode, and cost tracks a
           handful of whole-column AVERAGEIFS rather than the formula count, so a sheet with
           enough of them locks the tab: QB26080509_042 (Metals_Na_ICP_OES, 975 uncached of
           1,539 formula cells) painted and then ignored scrolling entirely, because Univer's
           engine had the CPU (client 2026-08-25).
           This is RARE and now measured, not guessed — scanning all 1,747 workbooks on GoLive,
           exactly THREE have any uncached formula cell: QB26080509_042 (975), QB26081203_032
           (358) and QB26082104_000 (97). The other 1,744 are fully cached, load as values and
           are untouched by this branch. So the cap costs nothing on every real batch and only
           bites the outliers.
           200 splits those three deliberately: the two that freeze are read as values, while
           97 still computes. Over the cap the cells whose answer was never cached render blank
           — which is what ALPACA shows for this workbook anyway, since DevExpress cannot load
           it — instead of hanging the form.
           NOT a substitute for fixing the data: a workbook lands here only because its formulas
           were never computed and cached; recomputing and re-saving it removes the cause. The
           warning names the sheet and the count so that is actionable rather than invisible. */
        const uncachedHere = cacheInfo?.uncached.get(ws.name)?.size ?? 0;
        const overCap = uncachedHere > MAX_RECALC_FORMULA_CELLS;
        if (overCap) {
          console.warn(
            `[SDMS] "${ws.name}": ${uncachedHere} uncached formula cells exceeds the ` +
            `${MAX_RECALC_FORMULA_CELLS}-cell recalculation cap — loading VALUES ONLY. ` +
            `Cells never computed by DevExpress will render blank. Recompute and re-save ` +
            `this workbook to restore them.`);
        }
        sheetNeedsFormulas = !overCap && sheetsNeedingRecalc.has(ws.name);
      } else {
        outer:
        for (let r = 1; r <= totalRows; r++) {
          const row = ws.getRow(r);
          for (let c = 1; c <= totalCols; c++) {
            const cell = row.getCell(c);
            if (!hasFormula(cell)) continue;
            if (toDisplay(cellValue(cell), cell.numFmt) === '') { sheetNeedsFormulas = true; break outer; }
          }
        }
      }
    }

    const GRID_BD = { s: 1, cl: { rgb: '#000000' } };

    const merges = (ws.model?.merges as string[] | undefined) ?? [];
    const mergeData = merges.map(parseMerge).filter(Boolean);
    /* Cells a merge COVERS (the whole span except its anchor), 1-based.
       The uniform data-table grid below gives every cell in the box all four borders —
       these included — so Univer drew a vertical rule through the middle of a merged
       header, splitting "SysSampleCode" in two where the header is merged A3:D3 (client
       2026-08-24, QB26081902_032 Raw Data). A merged cell is ONE cell: only the anchor
       carries the border, and Univer stretches it across the span. */
    type MergeSpan = { startRow: number; startColumn: number; endRow: number; endColumn: number };
    const mergeSpanOf = new Map<string, MergeSpan>();
    for (const m of mergeData as MergeSpan[]) {
      for (let mr = m.startRow; mr <= m.endRow; mr++)
        for (let mc = m.startColumn; mc <= m.endColumn; mc++)
          mergeSpanOf.set(`${mr + 1},${mc + 1}`, m);
    }

    for (let r = 1; r <= totalRows; r++) {
      const row = ws.getRow(r);
      // Row heights (Excel points → px) so the title + wrapped header rows match
      // ALPACA instead of collapsing to one line.
      const rd: Record<string, unknown> = {};
      if (row.hidden) rd.hd = 1;
      // An EXPLICIT row height in the file is FINAL — Excel does not auto-fit a row
      // that carries one (`customHeight`), it clips instead. ExcelJS only populates
      // `row.height` when the file specifies it, so this doubles as the test.
      const hasExplicitHeight = typeof row.height === 'number' && row.height > 0;
      if (hasExplicitHeight) rd.h = Math.round(row.height * 1.34);
      /* Auto-grow rows whose WRAPPED cells need more than one line — but ONLY when
         the file left the height unset. Overriding a stated height is what made our
         rows taller and UNEVEN against LIMS (client 2026-08-18): measured on
         QB26051911_074 the workbook says 18.75pt for every data row and 41.25pt for
         the header, and we emitted 25px / 40px / 55px — the 40s being rows this
         estimate decided would wrap.
         It over-fires for a second reason worth knowing: the char-per-line maths
         uses the ANCHOR column's width, but SiteName is MERGED across C:E, so the
         text has ~3x the room this estimate credits it with. Honouring the stated
         height sidesteps both; the estimate now only helps rows that genuinely have
         no height of their own. */
      let wrapH = 0;
      // The METADATA BLOCK above the data header may grow past its stated height.
      // The template gives the Instrument row 18pt with wrapText on, so
      // "PerkinElmer UV & Color Lambda 365+" is clipped to "PerkinElmer UV & Color"
      // in DevExpress and the analyst cannot read the instrument (client 2026-08-21,
      // who asked for the full name over an exact clip). DELIBERATE DEVIATION from
      // ALPACA, and scoped to those few rows: the DATA rows keep their stated heights,
      // which is what stopped them rendering tall and uneven (2026-08-18).
      const mayGrow = hasExplicitHeight === false || r < hr;
      for (let c = 1; mayGrow && c <= totalCols; c++) {
        const wc = row.getCell(c);
        if (!wc.alignment?.wrapText) continue;
        const wt = toDisplay(cellValue(wc), wc.numFmt);
        if (wt.length < 2) continue;
        const colW = (columnData[c - 1] as { w?: number } | undefined)?.w ?? 80;
        const perLine = Math.max(4, Math.floor((colW - 8) / 6.4));   // ~chars/line
        const lines = Math.min(4, Math.ceil(wt.length / perLine));
        if (lines > 1) wrapH = Math.max(wrapH, lines * 18 + 4);      // ~18px/line
      }
      if (mayGrow && wrapH > (typeof rd.h === 'number' ? (rd.h as number) : 0)) rd.h = wrapH;
      // FONT auto-fit for a row the file left UNSIZED: Excel grows such a row to fit its
      // tallest font, so a big-font banner (e.g. the row-1 FIELD("TEST") title at 22pt)
      // shows fully. Without this the row fell back to the default height and the title
      // was clipped vertically (user 2026-09-16, TOC template 9119 — "TOC" cut off).
      // Scoped to rows WITHOUT an explicit height (data/metadata rows keep their stated
      // heights) and only fires above the 11pt default, so ordinary rows are untouched.
      if (!hasExplicitHeight) {
        let maxFontPt = 0;
        row.eachCell({ includeEmpty: false }, (wc) => {
          const fs = (wc.font?.size as number | undefined) ?? 0;
          if (fs > maxFontPt) maxFontPt = fs;
        });
        if (maxFontPt > 11) {
          const fontFitPx = Math.round(maxFontPt * 1.33 * 1.34);   // font pt → row pt → px
          if (fontFitPx > (typeof rd.h === 'number' ? (rd.h as number) : 0)) rd.h = fontFitPx;
        }
      }
      // A row that still cannot fit a second line renders UNWRAPPED, so it clips
      // cleanly like DevExpress instead of showing a half-cut second line.
      const singleLineRow = (rd.h as number) > 0 && (rd.h as number) < 34;
      if (Object.keys(rd).length) rowData[r - 1] = rd;
      // This DATA row's overlay value, resolved once (not per column): the DF the
      // sheet is actually bound to for this SysSampleCode + Parameter.
      let rowDfOverlay: string | null = null;
      if (overlayActive && r > hr) {
        const sysC = row.getCell(ovSysCol), parC = row.getCell(ovParamCol);
        const key = dfOverlayKey(
          toDisplay(cellValue(sysC), sysC.numFmt),
          toDisplay(cellValue(parC), parC.numFmt),
        );
        rowDfOverlay = dfOverlay!.get(key) ?? null;
      }
      for (let c = 1; c <= totalCols; c++) {
        const cell = row.getCell(c);
        // DF cell on a data row → render the BOUND field, not the blob's cell.
        const dfOv = rowDfOverlay !== null && c === ovDfCol ? rowDfOverlay : null;
        if (dfOv !== null) {
          const n = Number(dfOv);
          const sid0 = addStyle(cellStyle(cell, singleLineRow, colPxByIndex[c]) ?? {});
          (cellData[r - 1] ??= {})[c - 1] = Number.isFinite(n) && dfOv.trim() !== ''
            ? { v: n,     t: 2, ...(sid0 ? { s: sid0 } : {}) }
            : { v: dfOv,  t: 1, ...(sid0 ? { s: sid0 } : {}) };
          continue;
        }
        // OG protects every FORMULA cell (SpreadsheetEntryController.cs:3757-3786:
        // unlock $A:$XFD, then `if (cell.HasFormula) … Locked = true`, then
        // `Protect(...)`).  Record the coordinate NOW — before the `continue` below
        // that skips empty/unstyled cells, since a formula whose cached result is
        // blank must still be protected.
        // Only the merge's top-left cell holds the formula: ExcelJS hands every OTHER cell of a merge the master's
        // value, formula included, but the live sheet keeps none there. Locking it made each recompute of the
        // master read as a paste that wiped a formula — Phosphate 8076's Calibration "y = …" line (F6:G6) warned
        // "protected sheet" on every paste into the A column (user report 2026-10-12). Selecting it selects the
        // merge, whose master stays locked, so nothing becomes editable.
        const mergeSlave = cell.isMerged && cell.master.address !== cell.address;
        if (hasFormula(cell) && !mergeSlave) (locks[ws.name] ??= new Set<string>()).add(`${r - 1},${c - 1}`);
        // An EMPTY cell (not a merged-cell part, no QC highlight / zero, no cached formula result) → its style id
        // from emptyStyleIds when an identical one was already mapped (see there).
        const emptyKey = cell.type === ExcelJS.ValueType.Null && cell.style
          && !qcZeros.has(`${r},${c}`) && !greens.has(`${r},${c}`)
          && !mergeSpanOf.has(`${r},${c}`) && !rawCached?.has(`${r},${c}`)
          ? (singleLineRow ? 4 : 0) + (hr > 0 && r >= hr && r <= lastDataRow && c <= lastDataCol ? (r > hr ? 2 : 1) : 0)
          : -1;
        const emptyIds = emptyKey >= 0 ? emptyStyleIds.get(cell.style as object) : undefined;
        if (emptyIds?.has(emptyKey)) {
          const known = emptyIds.get(emptyKey);
          if (known) (cellData[r - 1] ??= {})[c - 1] = { s: known };
          continue;
        }
        // A QC-comparison RPD cell whose cached zero DevExpress dropped renders
        // the formatted zero ("0.0") instead of blank (legacy parity).
        const text = qcZeros.has(`${r},${c}`)
          ? fmtNumber(0, cell.numFmt)
          : toDisplay(valueOf(cell, r, c), cell.numFmt);
        let style = cellStyle(cell, singleLineRow, colPxByIndex[c]);
        /* Default horizontal align for a cell the template leaves unset.
           Excel's "General" alignment resolves by the cell's TYPE, never by what
           its text looks like: a NUMBER or DATE goes RIGHT, TEXT goes LEFT - even
           text that is nothing but digits. Long text therefore still overflows
           LEFT->RIGHT and shows its START ("D:\..."), matching LIMS.

           This used to regex the DISPLAY TEXT and right-align anything
           numeric-looking, which pushed whole columns of digit-only TEXT to the
           right - reported for VendorLot on QB26052810_032 (client 2026-08-24).
           Settled against the DevExpress v20.1 engine on that batch rather than
           by eye:
               Reagents!F5  "260316"   valueType=Text     General  -> LEFT
               Calibration!A4  0.02    valueType=Numeric  General  -> RIGHT
               Calibration!D4  1.2     valueType=Numeric  Right    -> RIGHT
           An earlier comment here claimed some calibration cells are numeric TEXT
           that "must still align with the numeric rows above them". DevExpress
           left-aligns those too, so that was us deviating from ALPACA to look
           tidier. A column that genuinely needs right-aligned text carries an
           explicit alignment in the template, which the branch above honours.

           Same signal the emission below uses for `t: 2` vs `t: 1`, so the type a
           cell is COMPARED as and the side it is ALIGNED to can never disagree. */
        if (text !== '' && style?.ht === undefined) {
          const raw = cellValue(cell);
          const numericType = numericValue(cell) !== null || raw instanceof Date;
          style = { ...(style ?? {}), ht: numericType ? 3 : 1 };
        }
        const green = greens.get(`${r},${c}`);   // QC highlight overrides the fill
        if (green) style = { ...(style ?? {}), bg: { rgb: green }, cl: { rgb: green === '#C6EFCE' ? '#006100' : '#9C0006' } };
        // NO static IsReport painter here. A 2026-08-10 stopgap hardcoded
        // True→green-fill / False→red-fill while conditional formatting was
        // still unmapped — and once the template's REAL CF rules landed
        // (sdmsConditionalFormat.ts) they composed ON TOP of it: cyan CF font
        // over the stopgap's green bg, which is exactly the "IsReport all
        // green" defect chased on 2026-08-13. The template's own rule for
        // IsReport is FONT-ONLY (True→cyan, False→orange, dxf fill:null) and
        // OG renders it unfilled — verified against tpl 8099 + user's OG
        // side-by-side. CF now owns this column; do not re-add a painter.
        // Uniform data-table grid — applied BEFORE addStyle so even empty in-box
        // cells carry a style and render their border.
        if (hr > 0 && r >= hr && r <= lastDataRow && c <= lastDataCol) {
          const withBd: UniverStyle = { ...(style ?? {}), bd: { t: GRID_BD, b: GRID_BD, l: GRID_BD, r: GRID_BD } };
          // Data rows (below the header): CLIP long text to the cell so the
          // Parameter chemical names don't spill into the Surrogate column
          // (LIMS parity).  The header row keeps its wrap; metadata rows above
          // hr are outside this box, so the Data File Path still overflows.
          if (r > hr && withBd.tb === undefined) withBd.tb = 2;
          style = withBd;
        }
        /* EXCEL'S OVERFLOW RULE, for every cell — not just those inside a detected data
           box. A long value spills into its neighbour only while that neighbour is TRULY
           BLANK; a neighbour holding even an empty string is occupied, and the text clips
           at the column edge. The Reagents sheet has no SysSampleCode header, so it never
           formed a data box and never got the clip above — which is why the Reagent Name
           ran across Description and Preptype where ALPACA clips it (client 2026-08-24,
           QB26081902_032 Reagents!B3, whose C3 and D3 both hold ""). Marking the
           neighbours occupied was not enough on its own: Univer decides overflow from the
           cell's OWN wrap strategy, so state it. Left alone when the neighbour really is
           blank, so the metadata Data File Path still overflows as it does in ALPACA. */
        if (style?.tb === undefined && text !== '' && c < totalCols) {
          const right = row.getCell(c + 1);
          const rightValue = cellValue(right);
          if (rightValue !== null && rightValue !== undefined) style = { ...(style ?? {}), tb: 2 };
        }
        /* A merged cell is ONE cell: it shows the span's OUTER edges and nothing inside.
           Drop only the INTERIOR edges, per cell — never the whole border. Stripping the
           covered cells outright was the first attempt and it took the merged block's TOP
           edge with it, because Univer does not stretch the anchor's border across the
           span; the horizontal line above "Remarks" came from those covered cells
           (client 2026-08-24). Keeping every outer edge and dropping every inner one
           gives what Excel draws, for merges of any width or height. */
        const span = mergeSpanOf.get(`${r},${c}`);
        if (span && style?.bd) {
          const bd = { ...(style.bd as Record<string, unknown>) };
          if (c > span.startColumn + 1) delete bd.l;      // not the leftmost column
          if (c < span.endColumn + 1)   delete bd.r;      // not the rightmost
          if (r > span.startRow + 1)    delete bd.t;      // not the top row
          if (r < span.endRow + 1)      delete bd.b;      // not the bottom
          if (Object.keys(bd).length) style = { ...style, bd: bd as UniverStyle['bd'] };
          else { const { bd: _none, ...rest } = style; style = rest; }
        }
        const sid = addStyle(style);
        if (emptyKey >= 0) {
          let ids = emptyStyleIds.get(cell.style as object);
          if (!ids) { ids = new Map(); emptyStyleIds.set(cell.style as object, ids); }
          ids.set(emptyKey, sid);
        }
        // ── LIVE FORMULAS ──────────────────────────────────────────────────
        // Emit the REAL formula (Univer `f`) for formula cells instead of the
        // template's stale cached text, so Univer's formula engine RECALCULATES
        // as the analyst types.  Without this the sheet was a static snapshot:
        // Conductivity Result / Rec % / RPD % / QCStatus never populated and the
        // template's cached `#VALUE!` was rendered verbatim (user 2026-08-12).
        // The engine is present — UniverSheetsCorePreset registers
        // UniverFormulaEnginePlugin + UniverSheetsFormulaPlugin(+UI), and the
        // functions these templates use (IF/OR/IFERROR/ISBLANK/VALUE/AVERAGEIFS/
        // ROUND) all exist in @univerjs/engine-formula 0.25.1.
        // NB: `v` IS seeded from the cached result (see the note at the emission
        // below) so the cell is never blank while the engine is idle; the formula
        // stays and overwrites it on recompute.  `numFmt` is carried into
        // the style (Univer `n.pattern`) so a computed number keeps the template's
        // formatting (e.g. 1 -> "1.00"); it only affects these numeric cells.
        if (hasFormula(cell)) {
          const raw = String((cell as unknown as { formula?: unknown }).formula ?? '');
          if (raw) {
            let fml = (raw.startsWith('=') ? raw : `=${raw}`)
              .replace(/_x000d_/gi, '')     // Excel's escaped CR (seen in this template)
              .replace(/[\r\n]+/g, ' ');    // multi-line formulas -> one line for the parser
            // BARE PASSTHROUGH `=AB7` → `=IF(AB7="","",AB7)`. The RPD CtrlLimit cell
            // (Q7=AB7, the only bare single-ref formula in these templates) relies on
            // DevExpress `FIELD()` semantics, where a NULL merge field yields the empty
            // STRING "", so `=AB7` renders BLANK. Univer/Excel instead read `=<empty
            // cell>` as 0, so RPD CtrlLimit showed "0" on every non-Dup row where the
            // RPD upper limit is NULL, while OG shows blank (RPDHCLimit is NULL for
            // Cal/CCV/CCB/Sample, 20 only for Dup — DB-verified). Gating the passthrough
            // like the template's own O7 (Rec CtrlLimit `IF(OR(Y7="",Z7=""),"",…)`)
            // blanks the empty case and passes a real limit through unchanged. (fix
            // 2026-08-13, user OG side-by-side)
            const bare = /^=\s*(\$?[A-Z]{1,3}\$?\d+)\s*$/.exec(fml);
            if (bare) fml = `=IF(${bare[1]}="","",${bare[1]})`;
            /* BOUND WHOLE-COLUMN REFS TO THE USED RANGE — the edit-mode cost, and the
               reason "edit and update takes more time" (client 2026-08-25, QB26081709_074).
               These templates compute RPD with whole-column aggregates:
                   AVERAGEIFS(AF:AF, B:B,"SAMPLE", U:U,U8, E:E,E8)
               On that batch 32 formulas carry 96 such calls. Each names FOUR ranges of
               1,048,576 rows, so the engine both SCANS a million rows per range and — worse
               — treats every cell in those columns as a dependency, meaning ANY edit
               anywhere invalidates all 32 at once. That is why the whole sheet lurches on
               each keystroke while View mode (values only) is instant.
               ALPACA never pays this: it does not evaluate these in the spreadsheet engine
               at all. FormulaEngineHost compiles each template into a cached delegate
               pipeline and, on an edit, runs ExecuteSampleGroup over the edited row plus its
               Dup/MS/MSD dependents — "(rows × formulas) delegate calls (microseconds)" —
               with the whole-sheet pass reserved for the manual Recalculate ribbon button.
               We cannot re-point Univer at a different engine, but we CAN scope what it
               considers: clipping each column to the rows that exist shrinks the scan from
               ~1,048,576 to the used range AND shrinks the dependency set to the same rows.
               SAFE: rows past the used range hold nothing, and AVERAGEIFS/SUMIFS/COUNTIFS
               ignore empty cells, so every result is identical — verified by comparing the
               recomputed values against DevExpress's own cached ones before shipping.
               Headroom is added so a row typed just below the data still counts. */
            /* lastDataRow, NOT totalRows: ws.rowCount is the sheet DIMENSION (1,048,576 on
               these templates), so bounding to it clips nothing. Measured — with totalRows
               the emitted bound came out at 1,048,575 on three of four real workbooks, i.e.
               the fix did nothing at all. lastDataRow is the last row that actually holds
               data (column A, computed above), which is the range these aggregates mean. */
            // A range on ANOTHER sheet is bounded by THAT sheet's rows (its last row holding a value), never
            // by this one's — a summary sheet's lookups into Sheet1 were cut off at the summary's own size.
            fml = boundWholeColumnRefs(fml, Math.max(lastDataRow, hr) + WHOLE_COL_HEADROOM_ROWS, name => {
              if (name.toLowerCase() === ws.name.toLowerCase()) return undefined;
              const last = lastValueRowBySheet.get(name.toLowerCase());
              return last === undefined ? undefined : last + WHOLE_COL_HEADROOM_ROWS;
            });
            // Break the FALSE circular dependency Univer builds from a whole-column ref
            // (X:X) inside a formula that lives in column X — e.g. the THM "Numeric Result":
            // an UNTAKEN `IF(D="THMs", SUMIFS(J:J,…), …)` branch makes component rows cycle
            // with the "THMs" summary row, so Univer resolves the cyclic cells to 0 and the
            // Rec%/Dev%/QCStatus formulas wrongly compute 0.0/-100.0/Fail where OG is blank.
            // The merge has already filled the IF's condition cell, so constant-fold the DEAD
            // aggregate branch — reproducing DevExpress's lazy evaluation. Removes only a
            // provably-dead self-column aggregate; every other formula is byte-identical.
            fml = foldDeadSelfColumnBranches(fml, colLetter(c), (ref) => {
              const rm = /^([A-Z]{1,3})(\d+)$/.exec(ref);
              if (!rm) return undefined;
              const rc = ws.getRow(parseInt(rm[2]!, 10)).getCell(colLettersToNum(rm[1]!));
              // Only STATIC (non-formula) scalar values are known at merge time.
              if (rc.type === ExcelJS.ValueType.Formula) return undefined;
              const rv = rc.value;
              return rv == null || typeof rv === 'object' ? undefined : (rv as string | number | boolean);
            });
            // Retarget the THM cross-sheet "parent sample" lookup (column AG) to the SAME merged
            // sheet. OG renders THMs as one sheet PER sample, so AG's INDIRECT("'"&AF&"'!…")
            // resolves; AI merges to ONE sheet so that INDIRECT → #REF! → AG blank → RPD%/Rec%
            // blank. Rewrite it to a same-sheet AVERAGEIFS keyed on SysSampleCode=SampleID +
            // Parameter (see rewriteCrossSheetParentLookup). No-op on every non-THM formula.
            fml = rewriteCrossSheetParentLookup(fml, (ref) => {
              const rm = /^([A-Z]{1,3})(\d+)$/.exec(ref);
              if (!rm) return undefined;
              const rc = ws.getRow(parseInt(rm[2]!, 10)).getCell(colLettersToNum(rm[1]!));
              if (rc.type === ExcelJS.ValueType.Formula || rc.value == null || typeof rc.value === 'object') return undefined;
              const name = String(rc.value).trim().toUpperCase();
              return wb.worksheets.some(w => w.name.trim().toUpperCase() === name);
            });
            // A LIVE self-column total (THM "THMs" / HAA "THAA5" summary row: SUMIFS over its own column
            // incl. its own cell) is a true cycle Univer resolves to 0, stalling everything that reads the
            // column. Exclude the own row from the total — value unchanged, cycle gone. (2026-09-24)
            fml = excludeSelfRowFromSelfColumnAggregates(fml, colLetter(c), r);
            // Univer reads `""` in a comparison as an EMPTY CELL, so `0=""` is TRUE and every
            // `IF(J8="","",VALUE(J8))` guard blanks a real 0 (Excel/DevExpress: FALSE). `x=""` →
            // `LEN(x)=0` gives Excel's answer for blank / "" / 0 / text alike. (2026-09-26)
            fml = rewriteEmptyStringComparisons(fml);
            /* SEED THE CACHED RESULT ALONGSIDE THE FORMULA.
               Emitting `f` alone leaves the cell BLANK until Univer's engine
               evaluates it — and on this batch (QB26081503_005) it never did:
               every formula column (Channel1/Channel2 Result, NumericResult,
               ISTD …) rendered empty while columns A-D, which are plain values,
               rendered fine. The GoLive build shows the numbers over a
               BYTE-IDENTICAL workbook, so the values were always there.

               `v` is what Excel itself stores and what OG/DevExpress display, so
               seeding it shows the same numbers immediately. The formula stays,
               so when the engine does run it recomputes and replaces this — the
               seed is a floor, not a substitute. A momentarily stale value is
               strictly better than a blank column on a QC record. (2026-08-16) */
            const cached = toDisplay(valueOf(cell, r, c), cell.numFmt);
            // A NUMERIC cached result is seeded as a NUMBER (see numericValue): the
            // template's conditional formats compare these cells against numbers, and
            // a text seed makes every one of those comparisons take Excel's
            // text-ranks-above-numbers branch. numFmt lives in `fsid`, so the rendered
            // text is unchanged.
            // NOTE: deliberately NOT the `qcZeros` override that `text` (above) applies.
            // This branch's `cached` never had it either, and adding it here turned the
            // genuinely-BLANK Rec % of an internal-standard row into "0.0" (client
            // screenshot 2026-08-17). Those cells' formula returns "" by design —
            // `IF(OR(…,AQ<row>="1"),"",…)` short-circuits on ISTD rows — so `numericValue`
            // correctly yields null for them and the cell stays empty, matching LIMS.
            const cachedNum = numericValue(cell, valueOf(cell, r, c));
            /* NUMBER FORMAT FOLLOWS THE EMITTED TYPE — Excel applies a numeric format to
               NUMBERS, never to text, and DevExpress renders a text cell verbatim whatever
               its numFmt says. cellStyle() already enforces that for plain cells, but this
               formula branch attached the pattern unconditionally on the grounds that a
               formula's result type "is only known once the engine runs". For a CACHED
               result it is known right here — three lines down we type the cell from it.
               TSS RPD CtrlLimit(%) is the case: T11 is =AE11, AE11 is the TEXT "10", so the
               cached result is the STRING "10" under numFmt "0.00000". We emitted it as
               text AND handed Univer the pattern, and Univer — unlike Excel — formats any
               text that happens to parse as a number: "10.00000" where LIMS shows "10"
               (client 2026-08-25, QB26081913_014). Its neighbour S11 escaped only by luck:
               "85 - 115" carries the SAME pattern but cannot be parsed as a number.
               So the pattern goes on only when the value can actually be formatted by it —
               a number, or a cell we leave blank for the engine to fill. A format with an
               '@' section is a genuine TEXT format and is kept. */
            const emitsText = cachedNum === null && cached !== '';
            const fsid = !cell.numFmt || cell.numFmt === '@'
              ? sid                                    // '@' is the TEXT format — inert here,
                                                       // and cellStyle() skips it too
              : emitsText && !cell.numFmt.includes('@')
                ? addStyle(withoutNumFmt(style))
                : addStyle({ ...(style ?? {}), n: { pattern: cell.numFmt } });
            /* `withFormulas === false` (read-only) emits the VALUE ONLY, so the
               engine has nothing to evaluate and the sheet paints as fast as the
               pre-2026-08-14 build. The lock above is already recorded, so
               formula-cell protection is identical either way. */
            /* …EXCEPT when the workbook carries NO cached result for the cell.
               Then value-only leaves it permanently BLANK, while LIMS shows a value
               because DevExpress RECALCULATES on load. Reported for QCStatus on the
               Ammonia_ISE template (client 2026-08-18): on batch QB26051911_074 all 22
               QCStatus cells are formulas and 10 have no cached result, so the column
               read empty here and "Pass" in LIMS — same database, different rendering.
               Emitting the formula for JUST those cells lets Univer compute them while
               the ~95% that ARE cached keep the fast value-only path. */
            // `unresolvedHere` is the per-cell decision described above: emit the formula
            // only where the cached answer is not a real value, so Univer computes exactly
            // the cells whose answer can still change.
            const needsCompute = unresolvedHere ? unresolvedHere.has(`${r},${c}`) : sheetNeedsFormulas;
            (cellData[r - 1] ??= {})[c - 1] = {
              ...(withFormulas || needsCompute ? { f: fml } : {}),
              ...(cachedNum !== null
                ? { v: cachedNum, t: 2 }              // NUMBER — comparable
                : cached !== '' ? { v: cached, t: 1 } // TEXT / date / percent
                : {}),
              ...(fsid ? { s: fsid } : {}),
            };
            // Remember what DevExpress computed, so a later recalculation cannot silently
            // replace it (see the restore pass after boot).
            if (cachedNum !== null) (cachedFormulaValues[ws.name] ??= {})[`${r - 1},${c - 1}`] = { v: cachedNum, t: 2 };
            else if (cached !== '') (cachedFormulaValues[ws.name] ??= {})[`${r - 1},${c - 1}`] = { v: cached, t: 1 };
            continue;
          }
        }
        // A static DATE is emitted as its Excel SERIAL (a NUMBER) under a date pattern — what DevExpress
        // holds (every OG blob: AnalyzedDate 46252.35, TSS G2 46274 / G3 0.404…) — so date ARITHMETIC in
        // the templates works (TSS T3 `G2+G3` = the start date-time; Univer cannot add date TEXT) and the
        // cell compares as the number OG compares. Until 2026-09-24 dates went out as pre-formatted TEXT;
        // the pattern now does that job (Univer renders Excel date codes, `m/d/yy h:mm;@` included) with
        // the two rules fmtDate enforced kept: never AM/PM (standing rule 3), and a time-only value on the
        // 1899 epoch shows the clock, never "12/30/1899". (global parity fix #4)
        const rawDate = cellValue(cell);
        if (DATES_AS_SERIALS && rawDate instanceof Date && !isNaN(rawDate.getTime())) {
          const dsid = addStyle({ ...(style ?? {}), n: { pattern: datePatternFor(cell.numFmt, rawDate) } });
          (cellData[r - 1] ??= {})[c - 1] = { v: excelSerialOf(rawDate), t: 2, ...(dsid ? { s: dsid } : {}) };
          continue;
        }
        if (text === '' && !sid) continue;
        // An EMPTY cell is emitted STYLE-ONLY — no `v`/`t`.  Writing `v:'' , t:1` would
        // type the cell as TEXT, so a number the analyst types into an entry cell would
        // stay text and the numFmt pattern (e.g. "1.00") would never apply. (2026-08-13)
        // Same NUMBER-vs-TEXT rule as the formula seed above: a genuine number is
        // emitted typed so the template's conditional formats compare numerically.
        const plainNum = qcZeros.has(`${r},${c}`) ? 0 : numericValue(cell, valueOf(cell, r, c));
        /* A cell holding an EMPTY STRING is OCCUPIED, and that is not a distinction
           without a difference: Excel spills a long value into its neighbour only while
           the neighbour is truly blank, so an ""-valued cell CLIPS the text beside it.
           Emitting nothing for those cells let the Reagent Name run straight across
           Description and Preptype, where ALPACA clips it at the column edge (client
           2026-08-24, QB26081902_032 Reagents!B3, whose C3/D3 both hold ""). Emit the
           empty string so Univer sees the cell as taken. */
        const holdsEmptyString = text === '' && cellValue(cell) === '';
        (cellData[r - 1] ??= {})[c - 1] = text === ''
          ? (holdsEmptyString
              ? { v: '', t: 1, ...(sid ? { s: sid } : {}) }
              : { ...(sid ? { s: sid } : {}) })
          : plainNum !== null
            ? { v: plainNum, t: 2, ...(sid ? { s: sid } : {}) }
            : { v: text, t: 1, ...(sid ? { s: sid } : {}) };
      }
    }

    // ── QC Batch ID: fill the header cell the merge left blank ───────────────
    // The templates carry FIELD("QCBATCHID") in a header cell, but the merge runs
    // BEFORE the save mints the id, so it writes a blank and nothing goes back to
    // fill it — the cell is empty in every sheet stored to date. ALPACA hides this
    // because its SDMS screen RE-MERGES from the template on Retrieve; we render the
    // stored bytes, so we fill it here at RENDER time instead (client 2026-08-19).
    //
    // Display only — the stored workbook is never modified by this. Located by the
    // adjacent LABEL, which is how every template lays it out (a 'QCBatchID' caption
    // with the value in the cell to its right: C2/D2 on the TOC template, M4/N4 on
    // Alkalinity). Only ever fills a cell that is EMPTY, so a sheet that already has
    // the id — or one saved since the save-time stamp landed — is left untouched.
    if (qcBatchId) {
      const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
      const lastMeta = hr > 0 ? hr - 1 : Math.min(totalRows, 20);
      outer2: for (let r = 1; r <= lastMeta; r++) {
        for (let c = 1; c < totalCols; c++) {
          const label = ws.getRow(r).getCell(c);
          if (norm(toDisplay(cellValue(label), label.numFmt)) !== 'qcbatchid') continue;
          const target = ws.getRow(r).getCell(c + 1);
          if (toDisplay(cellValue(target), target.numFmt).trim() !== '') break outer2;
          // cellData is 0-BASED (the emission above writes cellData[r-1][c-1]); the
          // target is the cell to the RIGHT of the label, so its 0-based column is c.
          const cd = (cellData[r - 1] ??= {}) as Record<number, unknown>;
          const existing = (cd[c] ?? {}) as Record<string, unknown>;
          cd[c] = { ...existing, v: qcBatchId, t: 1 };
          break outer2;
        }
      }
    }

    // (merges are parsed before the cell loop — see `mergeData` above)

    const fr = filterRange(ws);
    // Univer refuses a one-row filter with a visible error toast ("The selected range only has one row
    // and not valid for filter", user 2026-09-28) — a header with no data rows under it gets no filter.
    const frRows = fr ? /^[A-Z]+(\d+):[A-Z]+(\d+)$/i.exec(fr) : null;
    if (fr && (!frRows || Number(frRows[2]) > Number(frRows[1]))) filters[ws.name] = fr;

    // In-cell dropdowns (client 2026-08-20: "some columns have a dropdown with data
    // in LIMS, Intelligence not having dropdown"). They are ordinary Excel data
    // validation carried IN the workbook - e.g. this Colilert template holds
    //     N11:N18 AF11:AG18 J3 -> $AI$2:$AI$5      (Analyst)
    //     J11:J18              -> $O$2:$AD$2       (Result)
    //     G11:G18              -> "Absent,Present" (inline)
    // - and we simply never read them, so every such cell rendered as plain text.
    const dvRules = collectDataValidations(wb, ws);
    if (dvRules.length) dvs[ws.name] = dvRules;

    // Excel conditional formatting — kept RAW here and applied after the workbook
    // exists (the CF facade works off a live FRange). The rules are authored on the
    // template's single DETAIL row; the band they must cover is that row through the
    // last used row of the merged sheet.
    // GUARDED by the kill-switch (2026-08-13): this extraction runs INSIDE
    // excelToUniver, so a throw here aborts the whole mapping — the component's
    // `map failed` catch then returns early and the container stays EMPTY, i.e. the
    // sheet (and even the formula bar) never renders.  The switch previously only
    // gated the preset + the apply step, leaving this on; now `false` disables every
    // CF code path so the mapping behaves exactly as it did before the CF work.
    // Wrapped in try/catch as well, so re-enabling CF can never blank the sheet.
    const cfBlocks = !SDMS_CF_ENABLED
      ? undefined
      : (ws as unknown as { conditionalFormattings?: unknown[] }).conditionalFormattings;
    try {
    if (Array.isArray(cfBlocks) && cfBlocks.length) {
      const refRows = cfBlocks
        .map(b => /(\d+)/.exec(String((b as { ref?: string }).ref ?? ''))?.[1])
        .filter(Boolean).map(n => parseInt(n as string, 10) - 1);
      if (refRows.length) {
        // bandStart = the DETAIL row the merge repeats — NOT min(refRows). These templates
        // also carry CF on HEADER rows (e.g. the Slope-Range checks on rows 3-6 of Ammonia_ISE),
        // and taking the min pulled bandStart onto a header row: every detail-row rule then
        // failed the `onBand` test in applySdmsConditionalFormats (its row !== bandStart) and
        // painted only its OWN (first) band row — colour on row 1, none below (user report
        // 2026-09-09). The detail row carries by far the most CF-covered cells (every QC column:
        // Result / Rec% / RPD% / QCStatus / …), so pick the row with the greatest total sqref
        // column-span; fall back to the old min if nothing parses. (reduce, not Math.min(...spread)
        // — a per-band-row rule set can be huge and blow the argument limit; 2026-08-13.)
        const spanByRow = new Map<number, number>();
        for (const b of cfBlocks) {
          for (const area of String((b as { ref?: string }).ref ?? '').split(/\s+/)) {
            const m = /^\$?([A-Z]{1,3})\$?(\d+)(?::\$?([A-Z]{1,3})\$?(\d+))?$/.exec(area.trim());
            if (!m) continue;
            const toNum = (s: string) => { let n = 0; for (const ch of s) n = n * 26 + (ch.charCodeAt(0) - 64); return n; };
            const c1 = toNum(m[1]!); const c2 = m[3] ? toNum(m[3]) : c1;
            const r0 = parseInt(m[2]!, 10) - 1;
            const rEnd = m[4] ? parseInt(m[4], 10) - 1 : r0;
            // ONLY single-row areas vote for the detail row. A whole-column / multi-row area
            // (e.g. 1014's `AK1:AZ1048576`, 16 cols tall × the whole sheet) is NOT a detail-row
            // rule; counting its span pulled bandStart onto row 1, so every detail-row rule
            // (Rec%/RPD% on row 8) failed the `onBand` test and never extended down the band —
            // 25 rules applied, no QC colour on the sample rows. (user 2026-09-16, Anions 1014)
            if (rEnd !== r0) continue;
            spanByRow.set(r0, (spanByRow.get(r0) ?? 0) + Math.abs(c2 - c1) + 1);
          }
        }
        let bandStart = refRows.reduce((a, b) => (b < a ? b : a), refRows[0] as number);
        let bestSpan = -1;
        for (const [r, span] of spanByRow) { if (span > bestSpan) { bestSpan = span; bandStart = r; } }
        // The sheet KNOWS its detail row: the one under the "SysSampleCode" header (`hr` is 1-based, so
        // the 0-based first data row is `hr`). Prefer it whenever rules sit on it — the span vote alone
        // picked VOC 8086's header row 1 (AE1:AJ1 + AK1 = 7 columns beat N8 / O8 / AQ8 / BA8 = 4), so the
        // Rec % / QCStatus colours stopped at the first data row on every run sheet (user 2026-09-28).
        if (hr > 0 && spanByRow.has(hr)) bandStart = hr;
        // The band ends at the last DATA row, NOT ws.rowCount: these templates carry
        // author notes below the data (rows 8-10 in 9116: "Result needs to be
        // configured…"), and running the rules over them painted colour under the
        // grid. Column A holds SysSampleCode on every data row and is empty on the
        // note rows, so it delimits the band exactly.
        // MEMORY (fix 2026-08-13): this used to be an index loop
        //   `for (let r = bandStart; r <= ws.rowCount; r++) ws.getRow(r + 1).getCell(1)`
        // and it was the cause of the blank sheet.  ExcelJS materialises rows/cells
        // LAZILY, so `getRow(n).getCell(1)` CREATES them — and `ws.rowCount` on these
        // templates reflects the sheet dimension, not the data, so the loop allocated
        // hundreds of thousands of row+cell objects and blew the heap.  The console said
        // it outright: `[SDMS] conditional-format extraction skipped out of memory`,
        // followed by `Uncaught out of memory` as the whole page ran out — which is why
        // the sheet came up blank ONLY with CF enabled.
        // `eachRow` visits only rows that ALREADY exist, so nothing is allocated.
        let bandEnd = bandStart;
        ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          const r = rowNumber - 1;                 // eachRow is 1-based; bands are 0-based
          if (r < bandStart) return;
          const v = row.getCell(1)?.value;
          if (v !== null && v !== undefined && String(v).trim() !== '') bandEnd = r;
        });
        cfs[ws.name] = { blocks: cfBlocks, bandStart, bandEnd };
      }
    }
    } catch (e) {
      // Never let CF extraction abort the mapping — losing the colours is acceptable,
      // losing the whole sheet is not.
      // eslint-disable-next-line no-console
      console.warn('[SDMS] conditional-format extraction skipped', e);
    }

    // Freeze the header block (rows 1..headerRow) so only data rows scroll.  ADD
    // a first-column freeze ONLY on the main data grid — the sheet whose column A
    // header is "SysSampleCode" — so that column stays visible on horizontal
    // scroll (ALPACA parity).  The Sequence / Reagents / Sample-Oriented sub-
    // sheets have merged titles ("Run Batch Sequence") and different first
    // columns, so freezing THEIR column A cut through the merged lines — hence
    // the column freeze is scoped to the SysSampleCode sheet only.
    // xSplit/ySplit = # frozen cols/rows; startColumn/startRow = first SCROLLABLE.
    const firstColHeader = hr > 0
      ? toDisplay(cellValue(ws.getRow(hr).getCell(1)), ws.getRow(hr).getCell(1).numFmt)
          .trim().toLowerCase().replace(/\s+/g, '')
      : '';
    /* ...and NOT when that header cell is merged across several columns. Freezing column A
       alone puts the freeze divider INSIDE the merged cell, and Univer draws that divider
       as a full-height vertical band — which is the line that appeared to split
       "SysSampleCode" down the whole sheet (client 2026-08-24, QB26081902_032 Raw Data,
       header merged A3:D3). ALPACA does not freeze that sheet at all. Sheet1, where the
       freeze earns its keep, has an unmerged SysSampleCode column and is unaffected. */
    const headerASpan = hr > 0 ? mergeSpanOf.get(`${hr},1`) : undefined;
    const headerAIsMerged = !!headerASpan && headerASpan.endColumn > headerASpan.startColumn;
    const freezeFirstCol = firstColHeader.startsWith('syssample') && !headerAIsMerged;
    // Freeze through the FULL header block, not just the SysSampleCode row: some
    // templates (e.g. Colilert-18) have a 2-row column header + a filter row
    // BELOW the SysSampleCode row before the data starts.  freezeRow = the row
    // just above the FIRST DATA row.  A data row's column A is a real sample code
    // — non-empty AND not a repeat of the "SysSampleCode" header (which ExcelJS
    // propagates down a vertical header merge, so a plain non-empty test would
    // stop at the merged header row).  For single-header templates this equals hr.
    const hdrLabelA = hr > 0
      ? toDisplay(cellValue(ws.getRow(hr).getCell(1)), ws.getRow(hr).getCell(1).numFmt).trim().toLowerCase()
      : '';
    let freezeRow = hr;
    if (hr > 0)
      for (let r = hr + 1; r <= totalRows; r++) {
        const a = toDisplay(cellValue(ws.getRow(r).getCell(1)), ws.getRow(r).getCell(1).numFmt).trim();
        if (a !== '' && a.toLowerCase() !== hdrLabelA) { freezeRow = r - 1; break; }
      }
    const freeze = hr > 0
      ? { xSplit: freezeFirstCol ? 1 : 0, ySplit: freezeRow, startRow: freezeRow, startColumn: freezeFirstCol ? 1 : 0 }
      : { xSplit: 0, ySplit: 0, startRow: -1, startColumn: -1 };

    // ── Merged header ⇒ ONE filter caret (SiteName) ──────────────────────────
    // Univer's autofilter draws a dropdown per COLUMN of the filter range, so a
    // header merged across C:E rendered THREE carets under "SiteName" where Excel
    // and OG show one (client 2026-08-17). The count follows VISIBLE columns, so
    // collapsing the covered ones is the only lever short of forking the preset.
    //
    // Collapsing ALONE regressed the row lines: SiteName is merged C:E on every
    // DATA row too, and Univer stops drawing a merged cell's borders once the
    // range spans zero-width hidden columns, so the horizontal rules vanished down
    // the whole column (reverted 2026-08-17). Dropping those merges as well fixes
    // both — with the covered columns hidden the merge is visually redundant, and
    // the anchor falls back to a plain cell carrying the same per-cell GRID_BD
    // border as every other cell in the box.
    //
    // GUARD: collapse only a header merge that repeats on EVERY data row with the
    // identical span. That is the signature of one logical field drawn wide; a
    // GROUP header ("QC Results" over three real columns) has no per-row merges,
    // and collapsing it would hide live data.
    /* RETIRED (2026-08-24). This whole pass existed for ONE reason: Univer drew a filter
       caret per COLUMN, so a header merged across C:E showed three. That is now fixed
       properly, in Univer itself — patches/@univerjs+sheets-filter-ui+0.25.1.patch skips a
       column covered by a header merge — so hiding the columns buys nothing and costs:
         • Univer paints a sliver at each hidden-column boundary, and on Raw Data (header
           merged A3:D3) that sliver cut a vertical line straight through the word
           "SysSampleCode" (client 2026-08-24, QB26081902_032);
         • the merges have to be dropped along with it, since Univer stops drawing a merged
           cell's borders once the span contains zero-width columns;
         • it could not fire at all where the covered columns hold metadata (Ammonia_ISE),
           so the behaviour differed by template for no reason the analyst can see.
       Keeping the merge intact renders what Excel renders. Set to true to re-enable, but
       check the caret patch first — that is the problem this was solving. */
    const SDMS_COLLAPSE_MERGED_HEADERS = false;
    if (SDMS_COLLAPSE_MERGED_HEADERS && hr > 0 && lastDataCol > 0 && lastDataRow > hr) {
      type Span = { startRow: number; startColumn: number; endRow: number; endColumn: number };
      const spans = mergeData as Span[];
      const rowsWith = (from: number, to: number) => {
        const rows = new Set<number>();
        for (const m of spans)
          if (m.startRow === m.endRow && m.startColumn === from && m.endColumn === to) rows.add(m.startRow);
        return rows;
      };
      const collapsed: Array<{ from: number; to: number }> = [];
      for (const m of spans) {
        if (m.startRow !== hr - 1 || m.endRow !== hr - 1) continue;   // header row only
        if (m.endColumn <= m.startColumn) continue;                   // horizontal only
        if (m.endColumn > lastDataCol - 1) continue;                  // inside the data box
        const rows = rowsWith(m.startColumn, m.endColumn);
        let everyRow = true;
        for (let r0 = hr - 1; r0 <= lastDataRow - 1 && everyRow; r0++) if (!rows.has(r0)) everyRow = false;
        // ...and NOTHING may live in the covered columns outside that repeated merge.
        // The metadata block ABOVE the header uses the very same cells for real values:
        // on Ammonia_ISE, SiteName is merged C:E all the way down, but D2/E2 hold
        // 'Entered By' + the operator code and D3:E6 the Slope readings. Collapsing on the
        // data box alone hid those columns and wiped five populated cells (client
        // 2026-08-18). The data box is NOT the whole sheet - scan every row.
        let coveredClean = true;
        for (let r = 1; r <= totalRows && coveredClean; r++) {
          if (rows.has(r - 1)) continue;                       // row IS the repeated merge
          for (let c = m.startColumn + 2; c <= m.endColumn + 1; c++) {   // covered cols, 1-based
            const cc = ws.getRow(r).getCell(c);
            if (toDisplay(cellValue(cc), cc.numFmt).trim() !== '') { coveredClean = false; break; }
          }
        }
        if (everyRow && coveredClean) collapsed.push({ from: m.startColumn, to: m.endColumn });
      }
      for (const s of collapsed) {
        let total = 0;
        for (let c0 = s.from; c0 <= s.to; c0++) {
          const cd = (columnData[c0] ??= {}) as Record<string, unknown>;
          total += typeof cd.w === 'number' ? cd.w : 88;
          if (c0 > s.from) { cd.hd = 1; cd.w = 0; }
        }
        (columnData[s.from] as Record<string, unknown>).w = total;
      }
      if (collapsed.length)
        for (let i = spans.length - 1; i >= 0; i--) {
          const m = spans[i]!;
          if (collapsed.some(s => m.startColumn >= s.from && m.endColumn <= s.to)) spans.splice(i, 1);
        }
    }

    const sheetRowCount = Math.max(totalRows + 12, 60);
    const sheetColCount = Math.max(totalCols + 6, 34);
    // A HIDDEN column occupies no width, so it must contribute 0 to the anchor span.
    geom[ws.name] = {
      colW: Array.from({ length: sheetColCount }, (_, i) => {
        const cd = columnData[i] as { w?: number; hd?: number } | undefined;
        return cd?.hd === 1 ? 0 : (cd?.w ?? 88);
      }),
      rowH: Array.from({ length: sheetRowCount }, (_, i) =>
        (rowData[i] as { h?: number } | undefined)?.h ?? defRowHeightPx),
    };

    sheets[sheetId] = {
      id: sheetId,
      name: ws.name,
      rowCount: sheetRowCount,
      columnCount: sheetColCount,
      defaultColumnWidth: defColWidthPx,
      defaultRowHeight: defRowHeightPx,
      freeze,
      // Neutral canvas gridlines (Univer's default is a cool blue-grey) so the
      // area outside the black data grid never reads blue either.
      showGridlines: 1,
      gridlinesColor: '#D9D9D9',
      cellData, columnData, rowData, mergeData,
    };
  };
  // One sheet at a time; with a progress callback, yield after each so the host panel paints the percent.
  for (let idx = 0; idx < visibleWs.length; idx++) {
    mapSheet(visibleWs[idx]!, idx);
    if (onProgress) { onProgress(idx + 1, visibleWs.length); await new Promise(res => setTimeout(res, 0)); }
  }

  /* Where the template's own embedded chart sits. Univer drops embedded Excel
     charts, so the SDMS view floats a reconstructed curve — and a FIXED box cannot
     match a chart whose size comes from the cells it spans (client 2026-08-24: the
     graph rendered smaller than ALPACA's). Anchor indices are workbook order, which
     includes hidden sheets, so resolve through wb.worksheets and keep only sheets
     that were actually emitted. */
  const chartBoxes: Record<string, AnchorBox> = {};
  try {
    for (const a of await readChartAnchors(buf)) {
      const ws = wb.worksheets[a.sheetIndex];
      const g = ws ? geom[ws.name] : undefined;
      if (!ws || !g) continue;
      chartBoxes[ws.name] = anchorToBox(
        a, c => g.colW[c] ?? 88, r => g.rowH[r] ?? 22,
        UNIVER_ROW_HEADER_PX, UNIVER_COL_HEADER_PX);
    }
  } catch {
    // Unreadable drawing part: the float keeps its CSS default box rather than
    // costing the sheet its chart entirely.
  }

  return { data: { id: 'sdms-workbook', sheetOrder, sheets, styles }, filters, locks, cfs, dvs, cachedFormulaValues, chartBoxes };
}

/** Commit the cell editor the analyst left open: a ribbon button lives outside the Univer canvas, so clicking it
 *  does not end the edit and the typed value stays in the editor overlay. Univer commits on Enter, so dispatch
 *  Enter to the active element, blur it, then give its SheetValueChanged event time to land. */
async function commitOpenEditor(): Promise<void> {
  const active = document.activeElement as HTMLElement | null;
  if (active) {
    for (const type of ['keydown', 'keyup'] as const) {
      try {
        active.dispatchEvent(new KeyboardEvent(type, {
          key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true, cancelable: true,
        } as KeyboardEventInit));
      } catch { /* noop */ }
    }
    try { active.blur(); } catch { /* noop */ }
  }
  await new Promise(r => setTimeout(r, 150));
}

/** One edit captured live from Univer's SheetValueChanged event. */
type TrackedEdit = { sheetName: string; row: number; col: number; value: unknown };

/** Compare Univer's CURRENT model values (per sheet, keyed by NAME) to the
 *  original workbook and write only the CHANGED cells.  Keyed by sheet name (not
 *  index) so it's immune to sheet ordering / hidden-sheet drift; a RAW-value
 *  compare avoids false positives from number formatting.  Date / formula cells
 *  are skipped (users only edit result values, and their raw shapes differ
 *  between Univer and ExcelJS). */
/** 1-based row of a sheet's BAND header — the "SysSampleCode" row directly above the sample rows — scanning
 *  the first 80 rows, else -1. The LAST such row wins: PFAS 533's Sheet1 also has "SysSampleCode" in its row-1
 *  title block (A1 as well as the A7 band header), and taking A1 made every header-area change (the Test /
 *  Method / Matrix rows, the G2 header formula after an import) a "sample row", so the batch-ownership guard
 *  refused the whole save — "4 sheet sample(s) not owned by the batch: SysSampleCode, Test, Method, Matrix"
 *  (user report 2026-10-01, QB26100101_001 / _078). */
function bandHeaderRow(ws: ExcelJS.Worksheet): number {
  const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
  let found = -1;
  const maxScan = Math.min(ws.rowCount || 0, 80);
  // Read ONCE: ExcelJS's columnCount getter walks every row of the sheet, and reading it per column per scanned
  // row made this scan O(rows x columns x 80) - 15.3 of applyModelToXlsx's 15.7 s on a VOC batch, on every Save
  // and on the import's first export (CPU profile 2026-10-01). The cells getCell creates while scanning are empty
  // and within that width, so the hoisted value is what the loop read on every pass (verified on VOC / PFAS / THM /
  // Anions: the getter never changed during the scan).
  const colCount = ws.columnCount || 0;
  for (let rr = 1; rr <= maxScan; rr++) {
    const row = ws.getRow(rr);
    for (let cc = 1; cc <= colCount; cc++)
      if (norm(toDisplay(cellValue(row.getCell(cc)), undefined)) === 'syssamplecode') { found = rr; break; }
  }
  return found;
}

// Exported for tests only (sdmsApplyModel.test.ts); the app uses it via getEditedXlsx.
export async function applyModelToXlsx(
  originalBuf: ArrayBuffer,
  modelBySheet: Record<string, unknown[][]>,
  computedBySheet: Record<string, unknown[][]> = {},
  liveFormulaBySheet: Record<string, (string | undefined)[][]> = {},   // Univer's current formula per cell
  /** Formulas the page set at runtime via setCellFormula, per sheet → "row,col" (0-based) → the formula
   *  AS PASSED (before the Univer-only rewrites). Persisted into <f> — see RUNTIME FORMULA WRITES below. */
  runtimeFormulaBySheet: Record<string, ReadonlyMap<string, string>> = {},
  /** The number format setCellFormula gave a cell (same keys) — Apply Rounding's per-value format. It is the
   *  format the cell SHOWS, so it wins over the stored one for the persisted text and is written into the blob. */
  runtimeNumFmtBySheet: Record<string, ReadonlyMap<string, string>> = {},
  /** Cells written as FIELD text (setCellValue `opts.text`), same keys → the text. Saved as text with exactly
   *  this value, as DevExpress FIELD() leaves it (THM FileResults "9.8"), never re-typed as a number. */
  textCellsBySheet: Record<string, ReadonlyMap<string, string>> = {},
  /** `originalBuf` already parsed by the caller — getEditedXlsx reads the sheet bounds from the same fresh
   *  parse, so the save no longer parses the workbook twice (speed, 2026-09-30). Only READ here. */
  parsed?: InstanceType<typeof ExcelJS.Workbook>,
): Promise<{ buf: ArrayBuffer; changes: SdmsCellChange[]; headerChanges: SdmsCellChange[] }> {
  let wb = parsed;
  if (!wb) { wb = new ExcelJS.Workbook(); await wb.xlsx.load(originalBuf); }
  const fmtOf = (sheetName: string, r0: number, c0: number): string | undefined => runtimeNumFmtBySheet[sheetName]?.get(`${r0},${c0}`);
  const changes: SdmsCellChange[] = [];
  // Edits outside a result band — the header area (Digestion Date D2, H2 / H3) or a sheet with no band (Calibration
  // absorbances). Saved in the sheet but never result rows, so they were in NO audit summary (user report 2026-10-12).
  const headerChanges: SdmsCellChange[] = [];
  /** The caption an analyst reads beside a header cell: the nearest non-empty cell to its LEFT ("Digestion Date"). */
  const captionLeftOf = (ws: ExcelJS.Worksheet, r1: number, c1: number): string => {
    for (let cc = c1 - 1; cc >= 1; cc--) {
      const cap = ws.getRow(r1).getCell(cc);
      if (cap.value instanceof Date || (cap.value && typeof cap.value === 'object' && 'formula' in (cap.value as object))) continue;
      const t = toDisplay(cellValue(cap), undefined).trim();
      if (t && Number.isNaN(Number(t))) return t;
    }
    return '';
  };
  const accepted: Record<string, SheetCellEdit[]> = {};
  const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
  for (const [sheetName, vals] of Object.entries(modelBySheet)) {
    const ws = wb.getWorksheet(sheetName);
    if (!ws || !Array.isArray(vals)) continue;
    // Only a RESULT sheet's rows are SampleInfo rows. A summary sheet (Parameter-Oriented Summary) has its own
    // "SysSampleCode" header, so its column A (parameter names, "File Name", "Valid?") was reported as sample
    // codes and the batch-ownership guard refused the save after an import (user report 2026-09-28). Its
    // edits are still saved into the sheet; they just aren't reported as table changes.
    const reportsRows = isResultSheet(sheetName, wb.worksheets.indexOf(ws));
    // Locate the data-table header row ("SysSampleCode") + the Parameter column,
    // and build a header-text-per-column map — so each change carries the row's
    // Parameter and the field's header text.  The backend maps (SysSampleCode,
    // Parameter, field) → the SpreadSheetEntry_SampleInfo row + result column.
    const headerRow = bandHeaderRow(ws);
    let paramCol = -1;
    const headerByCol = new Map<number, string>();
    if (headerRow > 0) {
      const hdr = ws.getRow(headerRow);
      for (let cc = 1; cc <= (ws.columnCount || 0); cc++) {
        const h = toDisplay(cellValue(hdr.getCell(cc)), undefined).trim();
        if (h) headerByCol.set(cc, h);
        if (norm(h) === 'parameter') paramCol = cc;
      }
    }
    for (let r = 0; r < vals.length; r++) {
      const rowArr = (vals[r] ?? []) as unknown[];
      for (let c = 0; c < rowArr.length; c++) {
        const cell = ws.getRow(r + 1).getCell(c + 1);
        // Dates: the raw form differs between engines (Univer holds a serial, ExcelJS a Date), so a
        // plain compare would flag every date. Keep only a GENUINELY different date — the import's
        // "2026-09-24 09:33" AnalysisDate — written back as a serial so the cell stays a date. Without
        // it a re-mount (the post-import Sequence / Raw Data refresh) reverted the imported date, and the
        // saved blob never carried it (user report 2026-09-28). Not reported as a change: the header/band
        // field writer persists it from the saved sheet.
        if (cell.value instanceof Date) {
          const live = liveDateOf(rowArr[c]);
          if (live && Math.abs(live.getTime() - cell.value.getTime()) >= 60_000) {
            (accepted[sheetName] ??= []).push({ row: r, col: c, value: excelSerialOf(live) });
            if (!(reportsRows && headerRow > 0 && r + 1 > headerRow))   // a header / band-less date the analyst changed
              headerChanges.push({ ref: String((cell as unknown as { address?: string }).address ?? `R${r + 1}C${c + 1}`),
                label: captionLeftOf(ws, r + 1, c + 1), old: toDisplay(cellValue(cell), cell.numFmt), new: toDisplay(live, cell.numFmt) });
          }
          continue;
        }
        if (cell.value && typeof cell.value === 'object' && 'formula' in (cell.value as object)) continue;
        const oldRaw = rawStr(cell.value);
        const fieldText = textCellsBySheet[sheetName]?.get(`${r},${c}`);
        const newRaw = fieldText ?? rawStr(rowArr[c]);
        if (newRaw === oldRaw) continue;   // unchanged (raw compare — no format noise)
        // SAFETY: never blank a cell that had a value.  Merged / identity cells
        // (SysSampleCode, SampleType, Parameter) come back blank from Univer, and
        // writing that blank wiped them — the bug that lost row identities.
        if (newRaw === '' && oldRaw !== '') continue;
        const rowCode = toDisplay(cellValue(ws.getRow(r + 1).getCell(1)), undefined).trim();
        const ref = String((cell as unknown as { address?: string }).address ?? `${sheetName}!R${r + 1}C${c + 1}`);
        const parameter = paramCol > 0 ? toDisplay(cellValue(ws.getRow(r + 1).getCell(paramCol)), undefined).trim() : '';
        const field = headerByCol.get(c + 1) ?? '';
        // Only BAND rows (below the "SysSampleCode" header row) are SampleInfo rows. A header-area edit —
        // the import writes VOC's Data File Path into D2, whose column A reads "Test" — was reported with
        // "Test" / "SysSampleCode" as its sample code and the batch-ownership guard refused the whole save
        // (no blob → empty sheet in G5, no results on reopen, status never set; user report 2026-09-28).
        // Header FIELD values are persisted from the saved sheet by the header/band field writer, not from
        // this list. The derived (recalc) path below already applies the same `r > headerRow` rule.
        if (reportsRows && headerRow > 0 && r + 1 > headerRow)
          // The persisted text is what the cell SHOWS — a typed 9.8 in a 0.00 cell saves "9.80", as OG (defect 9).
          changes.push({ ref, label: rowCode, parameter, field, old: toDisplay(cellValue(cell), cell.numFmt),
            new: fieldText ?? persistText(rowArr[c], fmtOf(sheetName, r, c) ?? cell.numFmt) });
        else
          headerChanges.push({ ref, label: captionLeftOf(ws, r + 1, c + 1), old: toDisplay(cellValue(cell), cell.numFmt),
            new: fieldText ?? persistText(rowArr[c], fmtOf(sheetName, r, c) ?? cell.numFmt) });
        // Record the edit for the SURGICAL writer below. The ExcelJS workbook is
        // READ-ONLY from here on (old values, header / parameter lookup) — assigning
        // cell.value would be pointless now that it is never serialized.
        (accepted[sheetName] ??= []).push({ row: r, col: c, value: newRaw, ...(fieldText !== undefined ? { text: true } : {}) });
      }
    }
  }
  // A DATE value cell the sheet now shows differently is saved from what the sheet holds, tracked edit or not.
  // PFAS 533's imported per-run AnalysisDate showed on screen but reached no tracked edit, so the save kept the
  // batch-create time on every row (side-by-side PFAS, 2026-09-30). Same rule as the edit path above: only a
  // genuinely different date (≥ 1 min), written back as a serial so the cell stays a date.
  for (const [sheetName, grid] of Object.entries(computedBySheet)) {
    const ws = wb.getWorksheet(sheetName);
    if (!ws || !Array.isArray(grid)) continue;
    const done = new Set((accepted[sheetName] ?? []).map(e => `${e.row},${e.col}`));
    ws.eachRow({ includeEmpty: false }, (row, r) => {
      row.eachCell({ includeEmpty: false }, (cell, c) => {
        if (!(cell.value instanceof Date) || done.has(`${r - 1},${c - 1}`)) return;
        const live = liveDateOf(grid[r - 1]?.[c - 1]);
        if (live && Math.abs(live.getTime() - cell.value.getTime()) >= 60_000)
          (accepted[sheetName] ??= []).push({ row: r - 1, col: c - 1, value: excelSerialOf(live) });
      });
    });
  }
  if (import.meta.env.DEV) console.info('[SDMS/Univer] applied %d change(s)%s', changes.length,
    changes.length ? ' — ' + changes.slice(0, 8).map(c => `${c.ref} "${c.old}"→"${c.new}"`).join('  ') : '');
  // ── DEPENDENT FORMULAS: refresh their CACHED value ───────────────────────
  // DevExpress renders the cached value and will not recalculate on load (even
  // with calcPr fullCalcOnLoad="1" — measured), so after an edit LIMS kept showing
  // the OLD number for every formula depending on it: edit F11 to 1.23 and G11
  // still read 104.6 for =F11*20, while Intelligence showed 24.6 because Univer
  // calculates live (client 2026-08-19).
  //
  // Only cells that ALREADY carry a formula are touched, and only their <v> — the
  // <f> is preserved, so the workbook keeps its logic. A cell is rewritten solely
  // when Univer's computed value DIFFERS from the cached one, keeping the diff to
  // the genuinely-affected cells rather than the whole sheet.
  //
  // NOTE this makes OUR engine the source of the numbers LIMS displays for these
  // cells — a deliberate call (option 1, 2026-08-19); the alternative was for
  // ALPACA's viewer to call Workbook.Calculate() on open.
  // ── APPLY-ROUNDING WRAPS → PERSIST INTO <f> (OG RoundCellInPlace :26537 parity) ──
  // Apply Rounding sets a cell's LIVE Univer formula to `=_LIMSRound(inner, sig, cut, dec,
  // rpt, def)`, but the surgical save keeps the ORIGINAL template `<f>` (values only), so the
  // wrap never reached the saved blob and OG/EDD reading it never saw it. OG persists the wrap
  // in `<f>` as its rounding marker ("those wraps round-trip through Save/Retrieve as plain cell
  // formulas"). Mirror that: where the LIVE formula is a `_LIMSRound` wrap not already in the
  // original `<f>`, rewrite the cell's `<f>` to the wrap (+ its computed `<v>`). This fires ONLY
  // for `_LIMSRound`-wrapped cells, so no other save is affected. Ungated on value-change, so a
  // wrap that leaves the numeric value unchanged (e.g. 91 → 91.0) still persists its `<f>`.
  const formulaRewrite: Record<string, SheetFormulaEdit[]> = {};
  const wrappedKeys: Record<string, Set<string>> = {};
  const LIMS_WRAP_RE = /^\s*=?\s*_LIMSRound\s*\(/i;
  for (const [sheetName, fgrid] of Object.entries(liveFormulaBySheet)) {
    const ws = wb.getWorksheet(sheetName);
    if (!ws || !Array.isArray(fgrid)) continue;
    const computed = computedBySheet[sheetName];
    for (let r = 0; r < fgrid.length; r++) {
      const frow = fgrid[r]; if (!Array.isArray(frow)) continue;
      for (let c = 0; c < frow.length; c++) {
        const liveF = frow[c];
        if (typeof liveF !== 'string' || !LIMS_WRAP_RE.test(liveF)) continue;
        const inner = liveF.replace(/^\s*=/, '');
        // Skip if the original cell already carries the same wrap (nothing to change).
        const origV = ws.getRow(r + 1).getCell(c + 1).value as { formula?: string } | null;
        const origF = (origV && typeof origV === 'object' && typeof origV.formula === 'string') ? origV.formula : '';
        if (origF.replace(/^\s*=/, '') === inner) continue;
        (formulaRewrite[sheetName] ??= []).push({ row: r, col: c, formula: inner, value: computed?.[r]?.[c], numFmt: fmtOf(sheetName, r, c) });
        (wrappedKeys[sheetName] ??= new Set()).add(`${r},${c}`);
      }
    }
  }
  // ── RUNTIME FORMULA WRITES → PERSIST INTO <f> ──────────────────────────────────
  // A formula the PAGE set on a cell at runtime (setCellFormula) that is not an Apply-Rounding wrap —
  // Import Files splices the imported value into the cell's FIELD formula (VOC J: VALUE("") →
  // VALUE(9.56)), as OG re-binds dtsample. The surgical save kept the ORIGINAL <f>, so a re-mount (the
  // post-import Sequence / Raw Data refresh) recalculated VALUE("") and the imported results vanished,
  // and the saved <f> never matched OG's VALUE(9.56) (user report 2026-09-28). Persist the formula AS
  // THE PAGE WROTE IT — recorded before the Univer-only load rewrites (LEN(x)=0, bounded ranges…), so
  // none of those ever reach the blob — with its computed <v>. Only where it really changed.
  for (const [sheetName, cells] of Object.entries(runtimeFormulaBySheet)) {
    const ws = wb.getWorksheet(sheetName);
    if (!ws) continue;
    const computed = computedBySheet[sheetName];
    for (const [key, formula] of cells) {
      const [r, c] = key.split(',').map(Number) as [number, number];
      if (LIMS_WRAP_RE.test(formula) || wrappedKeys[sheetName]?.has(key)) continue;   // the wrap pass above owns it
      const origV = ws.getRow(r + 1).getCell(c + 1).value as { formula?: string } | null;
      const origF = (origV && typeof origV === 'object' && typeof origV.formula === 'string') ? origV.formula : '';
      const inner = formula.replace(/^\s*=/, '');
      if (origF && !formulaDiffers(origF, inner)) continue;
      (formulaRewrite[sheetName] ??= []).push({ row: r, col: c, formula: inner, value: computed?.[r]?.[c], numFmt: fmtOf(sheetName, r, c) });
      (wrappedKeys[sheetName] ??= new Set()).add(key);
    }
  }

  const recalc: Record<string, SheetCellEdit[]> = {};
  for (const [sheetName, grid] of Object.entries(computedBySheet)) {
    const ws = wb.getWorksheet(sheetName);
    if (!ws || !Array.isArray(grid)) continue;
    // Same header lookup the edit path uses, so a DERIVED change carries the
    // label / parameter / field the backend needs to resolve its column.
    const dHeaderRow = bandHeaderRow(ws);   // the band header, as the edit path above
    let dParamCol = -1;
    const dReportsRows = isResultSheet(sheetName, wb.worksheets.indexOf(ws));   // see reportsRows above
    const dHeaderByCol = new Map<number, string>();
    {
      if (dHeaderRow > 0) {
        const hdr = ws.getRow(dHeaderRow);
        for (let cc = 1; cc <= (ws.columnCount || 0); cc++) {
          const h = toDisplay(cellValue(hdr.getCell(cc)), undefined).trim();
          if (h) dHeaderByCol.set(cc, h);
          if (norm(h) === 'parameter') dParamCol = cc;
        }
      }
    }
    const edited = new Set((accepted[sheetName] ?? []).map(e => `${e.row},${e.col}`));
    ws.eachRow({ includeEmpty: false }, (row, r) => {
      row.eachCell({ includeEmpty: false }, (cell, c) => {
        const v = cell.value as { formula?: string; result?: unknown } | null;
        if (!v || typeof v !== 'object' || typeof v.formula !== 'string') return;
        if (edited.has(`${r - 1},${c - 1}`)) return;          // the user's own edit wins
        const now = grid[r - 1]?.[c - 1];
        if (now === undefined) return;
        // Cache already correct — unless the text matches but the TYPE does not: a TEXT result (PFAS Numeric Result
        // "1.108974717") cached as a NUMBER by an earlier save is rewritten as text (patchFormulaCache, 2026-09-30).
        if (rawStr(now) === rawStr(v.result) && !(typeof now === 'string' && typeof v.result === 'number')) return;
        // A `_LIMSRound` wrap cell is persisted as a FORMULA rewrite (<f>+<v>) by the pass
        // above — skip the value-only cache write here so we don't keep its old template <f>.
        // The `changes` report below still fires so the backend gets the rounded value.
        if (!wrappedKeys[sheetName]?.has(`${r - 1},${c - 1}`))
          (recalc[sheetName] ??= []).push({ row: r - 1, col: c - 1, value: now });
        // …and report it as a CHANGE so the backend writes it to
        // SpreadSheetEntry_SampleInfo. Result / Rec % / RPD % / QCStatus are FORMULA
        // cells: editing Numeric Result recalculates them, but the edit path skips
        // formulas, so the record kept the pre-edit value while the sheet showed the
        // new one (client 2026-08-19: 260813004-003 Result 7.2 in the sheet, 4.1 in
        // the table, while Units — a literal — updated fine). ALPACA writes them
        // because its DataTable carries the RECALCULATED value and its loop writes
        // every column; this is the equivalent.
        if (dReportsRows && dHeaderRow > 0 && r > dHeaderRow) {
          const rowCode = toDisplay(cellValue(ws.getRow(r).getCell(1)), undefined).trim();
          const parameter = dParamCol > 0 ? toDisplay(cellValue(ws.getRow(r).getCell(dParamCol)), undefined).trim() : '';
          const field = dHeaderByCol.get(c) ?? '';
          // What the cell SHOWS, through its number format (OG exports the display text — defect 9).
          const newText = persistText(now, fmtOf(sheetName, r - 1, c - 1) ?? cell.numFmt);
          const oldText = rawStr(v.result);
          // A spreadsheet error persists as blank (persistText), so blank → #DIV/0! is no change to report.
          const noChange = newText === '' && (oldText === '' || isSheetError(oldText));
          if (rowCode && field && !noChange)
            changes.push({
              ref: String((cell as unknown as { address?: string }).address ?? `${sheetName}!R${r}C${c}`),
              label: rowCode, parameter, field, old: oldText, new: newText, derived: true,
              ...(ws.getColumn(c).hidden || ws.getRow(r).hidden ? { hidden: true } : {}),
            });
        }
      });
    });
  }
  if (import.meta.env.DEV)
    console.info('[SDMS/Univer] formula caches refreshed =',
      Object.values(recalc).reduce((n, a) => n + a.length, 0));

  // SURGICAL WRITE — see sdmsXlsxPatch.ts. DevExpress REFUSES to open the worksheet
  // XML ExcelJS emits (LoadDocument returns false, and LIMS then shows a blank
  // Sheet1), so the workbook is NEVER re-serialized: only the edited <c> elements
  // inside xl/worksheets/sheetN.xml are rewritten, and every other byte stays as
  // DevExpress wrote it. Do NOT put wb.xlsx.writeBuffer() back here.
  //
  // Then, on every worksheet in the SAME unzip / zip (two extra whole-workbook passes cost ~1 s on VOC, 2026-10-09):
  // 1. Replicate each detail-band conditional-formatting block down the whole data
  //    band, so the QC colouring shows on EVERY row when opened in legacy ALPACA
  //    (its DevExpress merge does this; ExcelJS keeps only the template's row-9 block).
  //    Persist-only — the buffer WE render keeps the single-row CF that our own view
  //    band-expands at render time (sdmsConditionalFormat.ts). See sdmsCfTextRepair.ts.
  // 2. Re-assert the two attributes ExcelJS drops (containsText `text=`, containsBlanks
  //    `stopIfTrue`) across EVERY block of the persisted blob — the band copies above
  //    and any rule the merge round-trip flattened — so ALPACA never colours a blank
  //    Rec%/RPD cell or refuses the workbook. Idempotent: intact rules are untouched.
  const buf = await patchWorkbookCells(originalBuf, accepted, recalc, formulaRewrite,
    xml => restoreCfTextInSheetXml(expandBandCfInSheetXml(xml)));
  return { buf, changes, headerChanges };
}

// LIMS-style protected-cell warning.  ALPACA's SpreadsheetControl shows
// "Warning — The cell or chart you're trying to change is on a protected sheet."
// when you type in a locked cell; Univer's default is "The range is protected,
// and you do not have edit permission…".  Override the permission-dialog locale
// (title + every edit-blocked message) so ours reads like LIMS.
/** How long after a paste / drag-fill / drag-move a locked formula cell losing its formula still counts
 *  as that action's spill (the SheetValueChanged event lands asynchronously, well inside this). */
const SPILL_WINDOW_MS = 2000;

const SDMS_PROTECTED_MSG = "The cell or chart you're trying to change is on a protected sheet.";
// NB the 'sheets-ui' NAMESPACE. Univer's locale file exports
//     { "sheets-ui": { permission: { dialog: { … } } } }
// and an override merged without it lands on a key nothing reads — which is why
// the analyst kept seeing Univer's own "Alert / The range is protected, and you do
// not have edit permission…" instead of ALPACA's wording (client 2026-08-20).
// The SAME dialog strings live in TWO namespaces and Univer draws from both: the
// title came from 'sheets-ui' while the body came from 'sheets', so overriding only
// the first produced a half-translated dialog — ALPACA's "Warning" heading above
// Univer's own "The range is protected…" text (client 2026-08-21).
const SDMS_PERMISSION_DIALOG = {
  alert: 'Warning',
  alertContent: SDMS_PROTECTED_MSG,
  commonErr: SDMS_PROTECTED_MSG,
  editErr: SDMS_PROTECTED_MSG,
  pasteErr: SDMS_PROTECTED_MSG,
  setStyleErr: SDMS_PROTECTED_MSG,
  setRowColStyleErr: SDMS_PROTECTED_MSG,
  copyErr: SDMS_PROTECTED_MSG,
  autoFillErr: SDMS_PROTECTED_MSG,
  formulaErr: SDMS_PROTECTED_MSG,
  moveRangeErr: SDMS_PROTECTED_MSG,
  moveRowColErr: SDMS_PROTECTED_MSG,
  insertRowColErr: SDMS_PROTECTED_MSG,
  removeRowColErr: SDMS_PROTECTED_MSG,
  filterErr: SDMS_PROTECTED_MSG,
  hyperLinkErr: SDMS_PROTECTED_MSG,
  commentErr: SDMS_PROTECTED_MSG,
  operatorSheetErr: SDMS_PROTECTED_MSG,
};

const SDMS_LOCALE_OVERRIDE = {
  'sheets-ui': { permission: { dialog: SDMS_PERMISSION_DIALOG } },
  sheets:      { permission: { dialog: SDMS_PERMISSION_DIALOG } },
  // The formula progress labels are missing from the preset locale, so Univer showed its raw keys
  // ("sheets-formula.progress.calculating"). The bar itself is hidden (sdmsEntry.css); kept for any other caller.
  'sheets-formula': { progress: { analyzing: 'Analyzing…', calculating: 'Calculating…', done: 'Done' } },
};

/** Hide Univer's built-in ribbon — the File / Home / Insert / … tabs AND their toolbar —
 *  while KEEPING the formula bar (the `fx` line). The `formulaBar` prop needs Univer's
 *  `toolbar` enabled (that's the only way this build mounts the formula bar), and enabling
 *  the toolbar drags in the whole ribbon. The CSS in sdmsEntry.css targets the ribbon's
 *  `data-u-comp` hooks, but Univer's current build renders the tab strip in an element the
 *  scoped CSS doesn't reliably catch, so we strip it from the DOM here instead.
 *
 *  Two prongs: (1) hide the known ribbon components outright; (2) — regardless of Univer's
 *  exact component tagging — keep ONLY the formula-bar / name-box branch inside the header
 *  bar and hide every other header-bar child (that's where the tabs live). Idempotent, so
 *  it's safe to re-run whenever Univer rebuilds the header (see the MutationObserver). */
function hideUniverRibbon(container: HTMLElement): boolean {
  let changed = false;
  const hide = (el: Element) => {
    const h = el as HTMLElement;
    if (h.style.display !== 'none') { h.style.display = 'none'; changed = true; }
  };
  container
    .querySelectorAll('[data-u-comp="ribbon-header-menu"],[data-u-comp="ribbon-toolbar"],[data-u-comp="ribbon-toolbar-more"]')
    .forEach(hide);
  // The formula bar (and its cell-reference name box) are the ONLY header-bar children we
  // keep; everything else in the header bar is ribbon chrome.
  const keep = ['formula-bar', 'defined-name', 'defined-name-container']
    .map(k => container.querySelector(`[data-u-comp="${k}"]`))
    .filter((n): n is Element => !!n);
  if (keep.length === 0) return changed;   // no formula bar mounted → don't blank the whole header
  container.querySelectorAll('[data-u-comp="headerbar"]').forEach(hb => {
    Array.from(hb.children).forEach(child => {
      if (!keep.some(k => child.contains(k))) hide(child);
    });
  });
  return changed;
}

// ────────────────────────────────────────────────────────────────────────────
interface UniverHandle { univer: { dispose?: () => void }; }

/** Imperative handle the SDMS ribbon's Update button calls to grab the edited
 *  workbook as .xlsx bytes for saving. */
/** One cell edited in the SDMS sheet — for the audit's Old/New Value AND the
 *  server-side write-back.  `label` = the row's SysSampleCode; `parameter` = the
 *  row's Parameter; `field` = the changed column's header text (e.g.
 *  "Numeric Result (mg/L)").  The backend maps (label, parameter, field) → the
 *  SpreadSheetEntry_SampleInfo row + result column. */
export type SdmsCellChange = { ref: string; label: string; parameter?: string; field?: string; old: string; new: string;
  /** A formula result the sheet recalculated, not a value typed into the cell. Still saved; the server audits it only
   *  when it replaces a value (a reopened sheet has no stored results, so every computed value would read as new). */
  derived?: boolean;
  /** A derived change in a HIDDEN row / column (a template helper such as 8076 AQ = VALUE(D8)): still sent to the
   *  server, but not listed in an audit summary — the analyst never saw or changed it. */
  hidden?: boolean };
export interface SdmsUniverHandle {
  /** `commitEditor` (default true) force-commits an open cell editor by dispatching Enter to the focused
   *  element — right for a ribbon button (Save / Update), WRONG for a background read while the analyst is
   *  in the grid: the synthetic Enter moves the selection a second row down (user 2026-09-23). Background
   *  callers (Engine B on-open / on-edit recompute, dilution IsReport rule) pass `{ commitEditor: false }`. */
  getEditedXlsx: (opts?: { commitEditor?: boolean }) => Promise<{ bytes: ArrayBuffer; changes: SdmsCellChange[];
    /** Edits OUTSIDE a result band (header cells D2 / H2, a Calibration-sheet value): saved in the sheet but not result
     *  rows, so never in `changes` — for the audit summary only. */
    headerChanges?: SdmsCellChange[] } | null>;
  /** BindGrid — force a full-workbook formula recompute. OG's BindGrid reads the sheet
   *  back into the data table and runs the server-side `FormulaEngineHost` over it, then
   *  re-merges (SpreadsheetEntryController :5646). Univer holds the data model and computes
   *  live, so the faithful equivalent is to force the whole workbook to recalculate now:
   *  commit any open cell editor, trigger executeCalculation, then wait for the dependency
   *  cascade to settle. Returns false if the formula facade is unavailable (e.g. read-only
   *  sheet never loaded the engine). `commitEditor: false` skips the commit (and its 150 ms wait) when the
   *  caller has already committed — the Save does once, up front. */
  recalc: (opts?: { commitEditor?: boolean }) => Promise<boolean>;
  /** The last-clicked cell (sheet + 0-based row/col + current display value), or null.
   *  The Qualifier ribbon action targets the cell the analyst pointed at. */
  getActiveCell: () => { sheetName: string; row: number; col: number; value: string } | null;
  /** The CURRENT selection read straight from Univer (0-based, inclusive) with its sheet's
   *  name and 1-based position in the workbook — or null. Unlike getActiveCell it needs no
   *  prior click event, so it works on a read-only sheet too. Toolbox ▸ SDMS Template
   *  Builder maps a field to / sets a mail-merge band on the selection. (2026-09-29) */
  getActiveRange: () => { sheetName: string; sheetIndex: number; startRow: number; startCol: number; endRow: number; endCol: number } | null;
  /** The LIVE computed value of any cell (0-based row/col) as Univer holds it now — a formula
   *  cell yields its current result, not its formula. Null when the sheet/cell is unavailable.
   *  Apply Rounding derives each cell's trailing-zero format from this (OG BuildNumberFormat
   *  rounds the cell's ACTUAL value), and Qualifier pre-fills the Qualifier-column cell from it —
   *  neither can rely on the exported xlsx, whose formula cells carry no cached result on a
   *  freshly merged sheet. (2026-09-22) */
  getCellValue: (sheetName: string, row: number, col: number) => string | null;
  /** The LIVE formula of one cell (0-based), with its leading "=", or null when it holds none / the sheet is
   *  unavailable. Apply Rounding asks it whether a cell is ALREADY wrapped in `_LIMSRound` — the exported xlsx
   *  cannot be trusted for that, and re-rounding an already-rounded value reset its format (PFAS Rec % 99.97 →
   *  "100.0" became "100"; side-by-side QB26100102_001 / QB26100101_001, 2026-10-01). */
  getCellFormula?: (sheetName: string, row: number, col: number) => string | null;
  /** The workbook the sheet was loaded from (with any replaceSheets swaps) — what getEditedXlsx patches. Apply Rounding
   *  reads its rows and formulas from it instead of a full export: the export only differs in values the live sheet
   *  already answers and in formulas set at runtime (getRuntimeFormula). One export saved per Save (~2.8 s on VOC). */
  getBaseXlsx?: () => ArrayBuffer | null;
  /** A formula the page set on this cell at runtime (setCellFormula — an import splice, a rounding wrap), as written
   *  and without its leading "=", or null. getEditedXlsx persists it into the saved <f>. */
  getRuntimeFormula?: (sheetName: string, row: number, col: number) => string | null;
  /** The formula this cell held when the sheet was loaded (or last redrawn), AS CONVERTED — whole-column ranges
   *  clipped to the data, dead THM branches folded, self-row cycles removed — with its leading "=", or null.
   *  Recalculate re-pushes this rather than the raw template text, which skips all of that. */
  getLoadedFormula?: (sheetName: string, row: number, col: number) => string | null;
  /** Commit an open cell editor (the ribbon lives outside the canvas, so a typed value is not committed by the click)
   *  and let its value land — what getEditedXlsx does first. */
  commitEditor?: () => Promise<void>;
  /** The LIVE value of one cell (0-based) AS STORED — before the cell's number format. getCellValue goes through
   *  Univer's display layer, which formats a number first (99.97 in a 0.0 cell reads "100.0"); Apply Rounding
   *  must round the real value, as OG RoundCellInPlace does, or a value near a cut-off rounds twice (PFAS Rec %
   *  "100.0" saved as "100"; side-by-side QB26100102_001 / QB26100101_001, 2026-10-01). Null = unavailable. */
  getCellRawValue?: (sheetName: string, row: number, col: number) => string | null;
  /** The LIVE computed values of a whole sheet's used area (0-based [row][col], null = blank,
   *  numbers/booleans as text) — what OG's DataTableExporter read off the control for the
   *  ribbon "Report" (BindSampleToReport). Null when the sheet is unavailable. (2026-09-22) */
  getSheetValues: (sheetName: string) => (string | number | null)[][] | null;
  /** A worksheet's DISPLAYED text (formatted, e.g. "7.20") over its used area; null when unavailable. */
  getSheetDisplayValues: (sheetName: string) => (string | null)[][] | null;
  /** The workbook's worksheet names, in order. */
  getSheetNames: () => string[];
  /** Replace one GROUP of hover notes: every note this handle placed for `group` before is removed,
   *  then these are set. Groups are independent — 'qc' = OG AddQcCellComments (Result / %Recovery /
   *  RPD), 'qualifier' = the Qualifier-column definitions. Display-only — notes never reach the saved
   *  blob. Returns the count. */
  setQcNotes: (notes: QcCellNote[], group?: string) => number;
  /** Write a value into a cell (Qualifier pick, Engine B write-back). A string lands as TEXT, a
   *  number as a NUMBER (the cell's pattern then formats it). False when read-only/unavailable.
   *  `opts.text`: the value is a FIELD value (an instrument import into a pure FIELD cell) — the saved
   *  workbook keeps it as TEXT, as DevExpress FIELD() writes it, even when it looks like a number.
   *  `opts.system`: a SYSTEM stamp (the QC Batch ID the first save minted) — shown, never recorded as an edit:
   *  the save stamps that cell itself, and an edit would reach the audited cell-change list. */
  setCellValue: (sheetName: string, row: number, col: number, value: string | number, numberFormat?: string,
                 opts?: { text?: boolean; system?: boolean }) => boolean;
  /** Set a cell's FORMULA (Apply Rounding wraps result cells as =_LIMSRound(...)); optional
   *  numberFormat drives trailing-zero display. False when read-only/unavailable. */
  setCellFormula: (sheetName: string, row: number, col: number, formula: string, numberFormat?: string) => boolean;
  /** setCellFormula for many cells at once: ONE write (+ one number-format write) per sheet instead of one per
   *  cell. Returns how many cells were written (0 when read-only/unavailable). */
  setCellFormulas?: (edits: ReadonlyArray<{ sheetName: string; row: number; col: number; formula: string; numberFormat?: string }>) => number;
  /** setCellValue for many cells at once (the import's stamps, 2026-10-01): the SAME per-cell bookkeeping, then ONE
   *  `set-range-values` per contiguous run of cells in a column - so each command's effected range is exactly the
   *  cells written and the SheetValueChanged listeners (dropdown normaliser, onCellEdited) see what the per-cell
   *  path showed them. Every single write makes Univer rebuild the formula data of EVERY sheet
   *  (FormulaDataModel.updateFormulaData -> getFormulaData), ~16 ms a cell on a VOC batch, so 2,941 import stamps
   *  took 46 s and PFAS's 2,145 took 33 s. Returns how many of `edits` were written (0 when read-only/unavailable). */
  setCellValues?: (edits: ReadonlyArray<{ sheetName: string; row: number; col: number; value: string | number; numberFormat?: string;
                   opts?: { text?: boolean; system?: boolean } }>) => number;
  /** Float the pictures an instrument upload carried over their sample sheets (SDMS Import Files —
   *  OG BindImagestoSheet) and remember them so getEditedXlsx persists them into the saved
   *  workbook. Returns the number rendered. */
  addImages: (images: PlacedImage[]) => number;
  /** Replace whole sheets IN PLACE — no re-mount (SDMS Import Files → the Sequence / Raw Data refresh,
   *  user 2026-09-28 "update the sheets without reloading"). `transform` rewrites the workbook the save
   *  path starts from (the base getEditedXlsx patches); each named sheet is then redrawn in the open
   *  workbook from that result — rewritten when it is there, added at the end when it is new. Every
   *  other sheet, and the analyst's edits on it, is untouched. False (nothing changed) when the sheet is
   *  read-only / not loaded or `transform` returns null. */
  replaceSheets: (transform: (base: ArrayBuffer) => Promise<ArrayBuffer | null>, sheetNames: readonly string[]) => Promise<boolean>;
  /** Resolves once the formula engine has gone quiet (no calculation running or starting) — so the host can
   *  keep its "sheet not ready" panel up until values stop changing (e.g. after the on-open Engine B stamps). */
  whenCalcIdle: () => Promise<void>;
}

/** Quiet time with no calculation before the sheet counts as settled (see waitCalcIdle). */
const CALC_QUIET_MS = 300;
/** Never hold the "sheet not ready" panel longer than this on a calculation that will not settle. */
const CALC_IDLE_MAX_MS = 120_000;

/** Polls `state` until no calculation is running and nothing has happened for `quietMs` — or `alive` turns
 *  false, or `maxMs` passes. `state.last` is performance.now() of the last calculation start / end OR cell
 *  write. The quiet time is counted from when the wait STARTS too: a write made just before it (the
 *  cached-value restore) schedules a calculation 100 ms later, but that timer is starved while the main thread
 *  is busy (applying ~1,300 formatting rules), so an old `last` alone let the panel lift before the
 *  calculation had even begun (user 2026-09-28: "Recalculating" chip from 24%). Exported for tests. */
export async function waitUntilCalcIdle(
  state: () => { active: boolean; last: number }, alive: () => boolean,
  quietMs = CALC_QUIET_MS, maxMs = CALC_IDLE_MAX_MS,
): Promise<void> {
  const started = performance.now();
  while (alive() && performance.now() - started < maxMs) {
    const s = state();
    if (!s.active && performance.now() - Math.max(s.last, started) >= quietMs) return;
    await new Promise(res => setTimeout(res, 100));
  }
}

/** Waits for a calculation the caller has just asked for (executeCalculation) to RUN and settle: first until
 *  the engine starts a calculation after `startsBefore` (Univer queues it on a 100 ms timer, so right after
 *  the call nothing is running yet), then until it is quiet (waitUntilCalcIdle). Univer's own waits returned
 *  in that gap — whenComputingCompleteAsync at once, onCalculationResultApplied on the stopped earlier pass —
 *  so a save straight after rounding read the new _LIMSRound cells with no value (QB26092801_001 re-saved
 *  2026-09-29: 446 cells stored without a value, RPD not written). Stops waiting for the start after
 *  `startMs` (nothing to calculate). Returns whether a calculation started. Exported for tests. */
export async function waitForCalcRun(
  state: () => { active: boolean; last: number }, starts: () => number, startsBefore: number, alive: () => boolean,
  startMs = 5_000, quietMs = CALC_QUIET_MS, maxMs = CALC_IDLE_MAX_MS,
): Promise<boolean> {
  const t0 = performance.now();
  while (alive() && starts() === startsBefore && performance.now() - t0 < startMs) await new Promise(res => setTimeout(res, 50));
  const started = starts() !== startsBefore;
  await waitUntilCalcIdle(state, alive, quietMs, maxMs);
  return started;
}

/** One sheet of an excelToUniver snapshot — the fields replaceSheets redraws from. */
type SheetSnap = {
  name?: string;
  rowCount: number;
  columnCount: number;
  cellData?: Record<string, Record<string, Record<string, unknown>>>;
  rowData?: Record<string, { h?: number; hd?: number }>;
  columnData?: Record<string, { w?: number; hd?: number }>;
  mergeData?: unknown[];
};

/**
 * Redraw the sheets of an excelToUniver `snapshot` into the OPEN workbook, in place (replaceSheets).
 * A sheet the workbook has is rewritten: its old merges and every old cell (value AND style) go, then
 * the new cells, merges and row / column sizes land — through mutations, so it is one redraw, never
 * blocked by the analyst's selection guards, and the sheet keeps its id (nothing that points at it
 * breaks). A sheet it lacks is appended; `onNewSheet` gets its id. The active sheet stays active.
 * Returns the names redrawn. Exported for the headless engine test.
 */
export function redrawSheetsInPlace(
  fapi: unknown, snapshot: unknown, onNewSheet?: (unitId: string, sheetId: string) => void,
): string[] {
  type Box = { startRow: number; startColumn: number; endRow: number; endColumn: number };
  type Sh = {
    getSheetId?: () => string;
    getSheet?: () => { getCellMatrix?: () => { forValue?: (cb: (r: number, c: number) => void) => unknown } };
    getMaxRows?: () => number; getMaxColumns?: () => number;
    getMergedRanges?: () => Array<{ getRange?: () => Box }>;
    setRowHeight?: (r: number, h: number) => unknown; setColumnWidth?: (c: number, w: number) => unknown;
    hideRows?: (r: number, n?: number) => unknown; hideColumns?: (c: number, n?: number) => unknown;
    activate?: () => unknown;
  };
  type Wb = {
    getId?: () => string; getActiveSheet?: () => Sh | null; getSheetByName?: (n: string) => Sh | null;
    getSheets?: () => Sh[]; insertSheet?: (name: string, opts: { index: number; sheet: unknown }) => Sh;
  };
  const api = fapi as { syncExecuteCommand?: (id: string, p?: object) => boolean; getActiveWorkbook?: () => unknown } | null;
  const fwb = api?.getActiveWorkbook?.() as Wb | null | undefined;
  const unitId = fwb?.getId?.();
  if (!api?.syncExecuteCommand || !fwb || !unitId) return [];
  const exec = (id: string, params: object) => api.syncExecuteCommand!(id, { unitId, ...params });
  const data = snapshot as { sheetOrder?: string[]; sheets?: Record<string, SheetSnap>; styles?: Record<string, unknown> };
  const active = fwb.getActiveSheet?.() ?? null;
  const done: string[] = [];
  try {
    for (const id of data.sheetOrder ?? []) {
      const snap = data.sheets?.[id];
      if (!snap?.name) continue;
      // Styles inline: the snapshot's style ids belong to ITS conversion, not the open workbook's.
      const cells: Record<number, Record<number, Record<string, unknown>>> = {};
      for (const [r, row] of Object.entries(snap.cellData ?? {}))
        for (const [c, cd] of Object.entries(row ?? {})) {
          const cell = { ...cd };
          if (typeof cell.s === 'string') cell.s = data.styles?.[cell.s] ?? undefined;
          (cells[+r] ??= {})[+c] = cell;
        }
      const live = fwb.getSheetByName?.(snap.name);
      if (!live) {
        // New (this workbook had no such sheet): appended, as the ExcelJS path adds it.
        const ins = fwb.insertSheet?.(snap.name, { index: fwb.getSheets?.()?.length ?? 0, sheet: { ...snap, id: undefined, cellData: cells } });
        const sid = ins?.getSheetId?.();
        if (sid) onNewSheet?.(unitId, sid);
      } else {
        const subUnitId = live.getSheetId?.() ?? '';
        // 1. The old merges and every old cell — value AND style, so nothing of the old layout bleeds through.
        const merges = (live.getMergedRanges?.() ?? []).map(m => m.getRange?.()).filter((b): b is Box => !!b);
        if (merges.length) exec('sheet.mutation.remove-worksheet-merge', { subUnitId, ranges: merges });
        const wipe: Record<number, Record<number, null>> = {};
        live.getSheet?.()?.getCellMatrix?.()?.forValue?.((r, c) => { (wipe[r] ??= {})[c] = null; });
        if (Object.keys(wipe).length) exec('sheet.mutation.set-range-values', { subUnitId, cellValue: wipe });
        // 2. Room for the new content, then its cells, merges and row / column sizes.
        if ((live.getMaxRows?.() ?? 0) < snap.rowCount)
          exec('sheet.mutation.set-worksheet-row-count', { subUnitId, rowCount: snap.rowCount });
        if ((live.getMaxColumns?.() ?? 0) < snap.columnCount)
          exec('sheet.mutation.set-worksheet-column-count', { subUnitId, columnCount: snap.columnCount });
        exec('sheet.mutation.set-range-values', { subUnitId, cellValue: cells });
        if (snap.mergeData?.length) exec('sheet.mutation.add-worksheet-merge', { subUnitId, ranges: snap.mergeData });
        for (const [r, rd] of Object.entries(snap.rowData ?? {})) {
          if (rd?.h) live.setRowHeight?.(+r, rd.h);
          if (rd?.hd) live.hideRows?.(+r, 1);
        }
        for (const [c, cd] of Object.entries(snap.columnData ?? {})) {
          if (cd?.w) live.setColumnWidth?.(+c, cd.w);
          if (cd?.hd) live.hideColumns?.(+c, 1);
        }
      }
      done.push(snap.name);
    }
  } finally {
    try { active?.activate?.(); } catch (e) { console.warn('[SDMS/Univer] re-activate sheet failed', e); }
  }
  return done;
}

type FWorkbookLite = {
  getSheets?: () => Array<{
    getSheetName?: () => string;
    getRange?: (a: string) => { createFilter?: () => unknown; getValues?: () => unknown[][] } | null;
  }>;
  setEditable?: (v: boolean) => unknown;
  getSnapshot?: () => unknown;
  getActiveSheet?: () => { getSheetName?: () => string } | null;
};
/** `DataValidationRenderMode.TEXT` (@univerjs/core). Univer's DEFAULT for a list rule
 *  paints a permanent dropdown arrow - and, in CUSTOM mode, a coloured chip - into
 *  EVERY validated cell, which is not how the sheet looks in ALPACA: there the cell
 *  renders plain and the arrow appears only once you are in the cell (client
 *  2026-08-20, "it should have the same design as LIMS ... otherwise it should remain
 *  as it was previously"). In TEXT mode the render extension returns immediately
 *  (`if (rule.renderMode === DataValidationRenderMode.TEXT) return;`) so nothing is
 *  drawn over the cell, while the picker still opens when the cell is activated.
 *  Spelled as a literal because @univerjs/core is a TRANSITIVE dependency - importing
 *  it directly would pin us to a package we do not declare. */
const DV_RENDER_TEXT = 0;

type FApiLite = { createWorkbook: (d: unknown) => void; getActiveWorkbook?: () => FWorkbookLite | null };

/** The float-DOM body for an imported instrument picture (registered once per Univer instance). */
function SdmsImportImageDom({ data }: { data?: { src?: string } }) {
  return (
    <img src={data?.src ?? ''} alt="" draggable={false}
         style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block', pointerEvents: 'none' }} />
  );
}
const IMPORT_IMAGE_COMPONENT = 'sdms-import-image';

/** Float one picture over `sheetName` at its top-left cell (0-based), sized in px, through the
 *  drawing preset's range-anchored float DOM. Best-effort: false when the facade is unavailable. */
function floatPicture(fapi: FApiLite, sheetName: string, row: number, col: number,
                      widthPx: number, heightPx: number, src: string): boolean {
  try {
    const wb = fapi.getActiveWorkbook?.() as unknown as { getSheetByName?: (n: string) => unknown } | null | undefined;
    const ws = wb?.getSheetByName?.(sheetName) as {
      getRange?: (r: number, c: number, nr?: number, nc?: number) => unknown;
      addFloatDomToRange?: (range: unknown, layer: unknown, anchor: unknown) => unknown;
    } | null | undefined;
    if (!ws?.getRange || !ws.addFloatDomToRange) return false;
    const res = ws.addFloatDomToRange(ws.getRange(row, col, 1, 1),
      { componentKey: IMPORT_IMAGE_COMPONENT, data: { src }, allowTransform: false, eventPassThrough: true },
      { width: Math.max(1, widthPx), height: Math.max(1, heightPx), horizonOffsetAlign: 'left', verticalOffsetAlign: 'top' });
    return !!res;
  } catch (e) { console.warn('[SDMS] picture float failed', e); return false; }
}

export const SdmsUniverSheet = forwardRef<SdmsUniverHandle, {
  buf: ArrayBuffer;
  editable?: boolean;
  /** True when `buf` is a fresh RE-MERGE from the tables/template (AI's normal open path),
   *  whose formula cells carry NO cached results. Value-only mode then can't show computed
   *  columns (Result/Rec%/…) and the blob-oriented recalc CAP wrongly blanks them, so a
   *  re-merged sheet must COMPUTE its formulas even in read-only View — like Enter mode.
   *  Leave false for a DevExpress-saved blob (real caches; keep the fast capped path). */
  remerged?: boolean;
  /** Worksheets locked WHOLESALE (every cell select-only) — OG's post-merge `Worksheet.Protect` on
   *  "Reagents" (ReagentEntrySDMS = No), "Instruments" and "Raw Data" (SpreadsheetEntryController
   *  :3629-3690). Names compared case-insensitively; a name with no such sheet is ignored. The
   *  result sheets keep the per-cell formula locks derived from the workbook. */
  lockedSheets?: readonly string[];
  /** Columns locked on every RESULT sheet from `fromRow` down (0-based) — the Engine B output
   *  columns (Result / %Recovery / RPD / QCStatus) of a formula-bound template, which the merge may
   *  have written as plain values (a pure FIELD cell) and so carry no formula lock of their own. Engine
   *  B's own writes still land (they are handle writes); the analyst cannot type, clear or paste there. */
  lockedColumns?: { cols: readonly number[]; fromRow: number } | null;
  /** Called when the viewer tries to change a read-only sheet — the host shows
   *  the warning. Kept as a callback so this component stays free of app
   *  services (notifications, dialogs). */
  onEditBlocked?: () => void;
  onSheetChange?: (name: string) => void;
  /** Fires when the analyst changes cell values on an EDITABLE sheet (Univer's
   *  SheetValueChanged), with the effected cells (0-based row/col). Skipped during
   *  the read-only/cached-value restore (same guards as the dropdown normaliser),
   *  so it reflects genuine edits. The host debounces it to drive the server-side
   *  Engine B `recompute-sample-group` for templates that bind Fixed formulas; for
   *  the common (unbound) template it's a no-op the host ignores. */
  onCellEdited?: (edits: Array<{ sheetName: string; row: number; col: number }>) => void;
  /** Fires when the analyst INTERACTIVELY opens the cell editor on an editable,
   *  unlocked cell (Univer's BeforeSheetEditStart) — a genuine user action, never
   *  a programmatic write or load-time formula compute. The host uses it to arm
   *  the unsaved-changes guard so a value change is attributed to a real edit. */
  onUserEditStart?: () => void;
  onReady?: () => void;
  /** Progress of loading the workbook in `buf`, for the host's "sheet not ready" panel: 'building'
   *  (mapping + creating the workbook), then 'calculating' with the FIRST calculation's percent (null =
   *  not counted yet). onReady follows once that calculation has settled. */
  onLoadStage?: (stage: 'building' | 'calculating', pct: number | null) => void;
  /** A recalculation AFTER the sheet is ready (an edit, an import): its percent while it runs (null =
   *  not counted yet), then `done` = true once it has finished. */
  onRecalc?: (pct: number | null, done: boolean) => void;
  /** Show Univer's FORMULA BAR above the grid (OG's SDMS has one — it shows the
   *  selected cell's formula, e.g. `=IF(OR(Y7="",Z7=""),"",VALUE(Y7)&" - "&VALUE(Z7))`).
   *  OPT-IN so the Result-Validation preview (`SdmsSpreadsheet`) keeps its current
   *  chrome-free look.  Univer registers the FormulaBar into BuiltInUIPart.HEADER,
   *  and @univerjs/ui renders that part only when `header && toolbar` are BOTH
   *  truthy — so enabling it necessarily also mounts Univer's own ribbon, which
   *  `sdmsEntry.css` hides (`[data-u-comp="ribbon-toolbar"]`) since the SDMS page
   *  already has its own Excel-style ribbon.  Read at createUniver time, so it is
   *  treated as constant for the life of the sheet. */
  formulaBar?: boolean;
  /** Register the `_LIMSRound` custom worksheet function (see limsRoundFn.ts).
   *  Legacy "Apply Rounding" wraps result cells as
   *  `=_LIMSRound(<expr>, sigFig, cutOff, decimals, rptLimit, defaultResult)`, so
   *  every SAVED SDMS sheet contains them (22 in template 9119, including Rec % /
   *  RPD %). Without the function those cells error and everything downstream of
   *  them renders blank — the "formula calculation is not working" report.
   *  OPT-IN for the same reason as `formulaBar`: this component is shared with the
   *  Result-Validation preview (`SdmsSpreadsheet`), and registering a function
   *  changes what that screen computes too. Only the SDMS Result Entry page passes
   *  it. (Result Validation shows the same sheets and would benefit — flagged for a
   *  separate decision, per [[feedback_only_current_forms]].) */
  limsRound?: boolean;
  /** Reports each sheet's embedded-chart box, measured from the template's own
   *  anchor against the widths/heights this build emitted. The host floats its
   *  reconstructed chart there instead of at a hardcoded size. Fires once per load;
   *  a sheet with no drawing is simply absent from the map. */
  onChartBoxes?: (boxes: Record<string, AnchorBox>) => void;
  /** The workbook carrying the embedded CHART parts — the TEMPLATE (the merged batch workbook is
   *  written by ExcelJS, which drops charts). When given, each chart is drawn over its anchor and
   *  follows the live cell values (sdmsLiveCharts, shared with the Template Builder). Omitted =
   *  no charts here (SdmsSpreadsheet draws its own overlay). */
  chartSourceBuf?: ArrayBuffer | null;
  /** DF values the sheet's DF column is BOUND to (see {@link SdmsDfOverlay}) —
   *  keyed by {@link dfOverlayKey}. Optional: without it the DF column renders the
   *  workbook verbatim, which is the behaviour every other caller had. */
  dfOverlay?: SdmsDfOverlay;
  /** Human-readable QC Batch ID. Rendered into the header cell the merge left
   *  blank (see excelToUniver) — display only, the stored workbook is untouched. */
  qcBatchId?: string;
}>(function SdmsUniverSheet({ buf, editable, remerged, lockedSheets, lockedColumns, onEditBlocked, onSheetChange, onCellEdited, onUserEditStart, onReady, onLoadStage, onRecalc, formulaBar, limsRound, dfOverlay, qcBatchId, onChartBoxes, chartSourceBuf }, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<UniverHandle | null>(null);
  const apiRef = useRef<FApiLite | null>(null);
  // Observes the sheet container and re-fits Univer's canvas on a size change (see boot()).
  const resizeObsRef = useRef<ResizeObserver | null>(null);
  // Last cell the analyst clicked (Qualifier ribbon action). Passive cache from CellClicked.
  const selRef = useRef<{ sheetName: string; row: number; col: number } | null>(null);
  // Optional "the workbook has been built" signal (additive — existing callers
  // omit it). Lets a host keep a loading overlay up until the sheet paints,
  // avoiding a white flash on buf swap. Ref so it never re-triggers the effect.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const onLoadStageRef = useRef(onLoadStage);
  onLoadStageRef.current = onLoadStage;
  const onRecalcRef = useRef(onRecalc);
  onRecalcRef.current = onRecalc;
  /** Is a calculation running, and when did the last one start or end (performance.now()). */
  const calcStateRef = useRef({ active: false, last: 0 });
  /** How many calculations have started — recalc() waits for the one it asked for (waitForCalcRun). */
  const calcStartsRef = useRef(0);
  /** Resolves once no calculation has run or started for CALC_QUIET_MS (Univer batches a write's dirty
   *  cells 100 ms before it starts calculating, and the dependency cascade runs several passes). */
  const waitCalcIdle = (alive: () => boolean): Promise<void> => waitUntilCalcIdle(() => calcStateRef.current, alive);
  // Latest `editable` for the (buf-keyed) init closure, without re-creating
  // Univer on every Edit toggle.
  const editableRef = useRef(editable);
  const remergedRef = useRef(remerged);
  remergedRef.current = remerged;
  const onEditBlockedRef = useRef<(() => void) | undefined>(undefined);
  editableRef.current = editable;
  onEditBlockedRef.current = onEditBlocked;
  // Latest DF overlay for the (buf-keyed) init closure — read at conversion time,
  // so a map that arrives with the batch rows is picked up on the next sheet load
  // without re-creating Univer on every render.
  const dfOverlayRef = useRef(dfOverlay);
  dfOverlayRef.current = dfOverlay;
  const qcBatchIdRef = useRef(qcBatchId);
  qcBatchIdRef.current = qcBatchId;
  // Latest onSheetChange for the poll closure; poll handle for cleanup.
  const onSheetChangeRef = useRef(onSheetChange);
  const onChartBoxesRef = useRef(onChartBoxes);
  const chartSourceRef = useRef(chartSourceBuf);
  chartSourceRef.current = chartSourceBuf;
  const onUserEditStartRef = useRef(onUserEditStart);
  onUserEditStartRef.current = onUserEditStart;
  const onCellEditedRef = useRef(onCellEdited);
  onChartBoxesRef.current = onChartBoxes;
  onSheetChangeRef.current = onSheetChange;
  onCellEditedRef.current = onCellEdited;
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Edits captured LIVE from Univer's SheetValueChanged event, keyed by
  // "sheetName:row:col" (0-based).  Reading the model at save-time was racy
  // (getSnapshot/getValues sometimes lagged the just-typed value); the event
  // payload is reliable.  Reset whenever the workbook (buf) reloads.
  const editsRef = useRef<Map<string, TrackedEdit>>(new Map());
  const eventDisposeRef = useRef<{ dispose?: () => void } | null>(null);
  // Disposer for the onCommandExecuted listener that keeps the template-hidden
  // columns hidden (users must not be able to unhide the sheet's helper columns).
  const cmdDisposeRef = useRef<{ dispose?: () => void } | null>(null);
  // Formula-cell protection (OG parity, see the guard below): sheet name → set of
  // "row,col" (0-based) cells that carry a formula and must refuse edits.  Plus the
  // disposer for the edit-guard subscription and a throttle so holding a key down
  // doesn't stack warning dialogs.
  // Does the model currently in Univer carry formulas?  Read-only builds omit
  // them (SDMS_LAZY_FORMULAS); `formulaKey` forces the single rebuild that puts
  // them back when the analyst switches to Edit.
  const hasFormulasRef = useRef(false);
  const [formulaKey, setFormulaKey] = useState(0);
  const locksRef = useRef<Record<string, Set<string>>>({});
  /** Upper-cased names of the wholesale-locked sheets (see the `lockedSheets` prop). */
  const lockedSheetsRef = useRef<Set<string>>(new Set());
  lockedSheetsRef.current = new Set((lockedSheets ?? []).map(n => n.toUpperCase()));
  /** OG's cell protection in one place: a cell on a wholesale-locked sheet, or a formula cell on a
   *  result sheet. Every guard (edit start, clear/paste, paste writer, spill revert) asks this. */
  /** The `lockedColumns` prop, re-read every render. */
  const lockedColumnsRef = useRef<{ cols: readonly number[]; fromRow: number } | null>(null);
  lockedColumnsRef.current = lockedColumns ?? null;
  /** Names of the workbook's RESULT sheets (isResultSheet over the sheet order) — where lockedColumns applies. */
  const resultSheetsRef = useRef<Set<string>>(new Set());
  /** Each sheet's formula cells AS CONVERTED (full `f`), kept before shareFormulas leaves most of them an `si` only:
   *  the protected-cell revert restores a cell's own formula from here. */
  const formulaCellsRef = useRef<Record<string, Record<number, Record<number, { f?: string; v?: unknown }>>>>({});
  /** A cell of a `lockedColumns` column on a result sheet (an Engine B output cell). */
  const isColumnLocked = (sheetName: string, row: number, col: number): boolean => {
    const lc = lockedColumnsRef.current;
    return !!lc && row >= lc.fromRow && lc.cols.includes(col) && resultSheetsRef.current.has(sheetName);
  };
  const isLockedCell = (sheetName: string, row: number, col: number): boolean =>
    lockedSheetsRef.current.has(sheetName.toUpperCase()) || !!locksRef.current[sheetName]?.has(`${row},${col}`)
    || isColumnLocked(sheetName, row, col);
  const protectDisposeRef = useRef<{ dispose?: () => void } | null>(null);
  const clearGuardDisposeRef = useRef<{ dispose?: () => void } | null>(null);
  const sheetBarGuardRef = useRef<(() => void) | null>(null);
  const lastWarnRef = useRef(0);
  /** True while the post-calculation restore is writing cached results back, so
   *  the edit-capture listener does not mistake them for the analyst's own edits
   *  and send them to SpreadSheetEntry_SampleInfo. */
  const restoringRef = useRef(false);
  /** > 0 while one of the handle's OWN writes (setCellValue / setCellFormula — the Engine B
   *  stamp, Apply Rounding, a Qualifier pick, the dilution IsReport rule, import labels) is in
   *  flight. The edit listener must record such a write as an edit so Save persists it, but
   *  must NEVER read it as a paste-spill into a locked formula cell: the Engine B stamp writes
   *  the computed QCStatus over the template's QCStatus formula on purpose (OG QCBatchReportingRecalc
   *  :766 does the same), and reverting it + warning "protected sheet" was what the analyst saw
   *  the moment they clicked into a freshly opened TOC batch (user 2026-09-23). Counted, not
   *  boolean, because the callers loop; released on the next macrotask like restoringRef. */
  //
  // Tracked PER CELL, not by a time window: Univer raises SheetValueChanged AFTER the write returns
  // (not synchronously), so the earlier "until the next tick" counter had already dropped by the time
  // the event arrived — on Save the Engine B stamp into TOC's locked QCStatus formula was then read as
  // a spill, reverted, and the "protected sheet" warning popped (user 2026-09-23, after adding samples
  // through the QC Batch popup).
  //
  // Each written cell is remembered with the VALUE written, for as long as this workbook is loaded (a
  // 10 s expiry was tried and failed: the stamp REPLACES the cell's formula, so any later recompute event
  // touching that cell — after the expiry — found a locked cell with no formula and reverted it, the
  // "random lock message that goes away by itself", console `spill reverted — Sheet1!r19c27` = TOC AB20).
  // The listener accepts an event for a marked cell only while the cell still holds the written value, so
  // a genuine paste/fill spill that CHANGES it is still caught.
  const programmaticCellsRef = useRef<Map<string, string>>(new Map());
  /** Cells a SYSTEM stamp wrote (setCellValue `opts.system` — the minted QC Batch ID), sheet:row:col → value:
   *  never an edit while they still hold it. Cleared with each new workbook. */
  const systemCellsRef = useRef<Map<string, string>>(new Map());
  /** > 0 while a handle write (setCellValue / setCellFormula) is running. Univer raises SheetValueChanged
   *  SYNCHRONOUSLY inside FRange.setValue (syncExecuteCommand → mutation → facade event — proven headless
   *  2026-09-24), so any event seen while this is up IS that write. The value-match mark below is not
   *  enough on its own: the stored value can differ from what was passed (numfmt / type coercion), and
   *  then Engine B's own stamps into its locked output columns were "spills" — reverted with the
   *  protected-sheet warning right after a drag-fill (user 2026-09-24, TOC: F / N / R on every band row). */
  const handleWritingRef = useRef(0);
  const markProgrammaticWrite = (sheetName: string, row: number, col: number, value: unknown) => {
    programmaticCellsRef.current.set(`${sheetName}:${row}:${col}`, String(value ?? ''));
  };
  const isProgrammaticCell = (sheetName: string, row: number, col: number, current?: unknown): boolean => {
    const v = programmaticCellsRef.current.get(`${sheetName}:${row}:${col}`);
    if (v === undefined) return false;
    return current === undefined || String(current ?? '') === v;
  };
  /** False until the analyst first interacts with the sheet (a pointer or key
   *  gesture inside the Univer canvas). The edit/spill listener ignores every
   *  SheetValueChanged until then, because a load recompute is NEVER preceded by a
   *  gesture while a real edit or paste ALWAYS is. Time/compute-based gating cannot
   *  do this: Univer computes lazily after paint (and the Enter-mode lazy-formula
   *  REBUILD recomputes hundreds of cells well after the boot's compute-complete),
   *  so those events were recorded as edits (console "tracked cells = 300") and, on
   *  templates where many cells differ from the cache, tripped the spill-revert ->
   *  a spurious "protected sheet" warning on open (client 2026-09-16,
   *  QB26090309_035 Ortho Phosphate). */
  const userInteractedRef = useRef(false);
  /** When (Date.now()) the analyst last ran a command that can SPILL into cells outside the
   *  selection — a Univer paste variant, drag-fill or drag-move. A locked formula cell that loses its
   *  formula is treated as a spill (reverted + warned) ONLY shortly after one of these; any other
   *  formula loss is not the analyst's doing, so it must never pop the protected-sheet warning at
   *  them (user 2026-09-24: "make sure the cell edit warning does not appear randomly"). */
  const lastSpillCmdRef = useRef(0);
  /** Pictures imported in THIS session (not yet in `buf`) — persisted by getEditedXlsx. */
  const importedImagesRef = useRef<PlacedImage[]>([]);
  /** Formulas the page set through setCellFormula in THIS session (sheet → "row,col" → formula as passed,
   *  before the Univer-only rewrites) — persisted into <f> by getEditedXlsx (import splice, Recalculate). */
  const runtimeFormulasRef = useRef<Map<string, Map<string, string>>>(new Map());
  /** The number format setCellFormula gave a cell (sheet → "row,col" → format) — Apply Rounding's per-value
   *  format (OG RoundCellInPlace BuildNumberFormat). Saved into the blob and used for the persisted text. */
  const runtimeNumFmtsRef = useRef<Map<string, Map<string, string>>>(new Map());
  /** Cells setCellValue wrote as FIELD text (`opts.text`), sheet → "row,col" (0-based) → the text. The save writes
   *  them as text with exactly this value: its cell writer otherwise stores any numeric-looking value as a number,
   *  so THM's FileResults "9.8" (OG: text, shown "9.8") was saved as the number 9.80 (comparison 2026-09-29). */
  const textCellsRef = useRef<Map<string, Map<string, string>>>(new Map());
  /** The workbook the save path starts from once `replaceSheets` has swapped whole sheets in place —
   *  `buf` with those sheets replaced. Null = `buf` itself. Dropped when a new `buf` arrives. */
  const baseOverrideRef = useRef<ArrayBuffer | null>(null);
  /** The `buf` baseOverrideRef belongs to. */
  const baseForBufRef = useRef<ArrayBuffer | null>(null);
  /** Cells that carry a QC hover note this handle placed (sheet:row:col) — cleared before each rebuild. */
  const qcNoteCellsRef = useRef<Map<string, Array<{ sheetName: string; row: number; col: number }>>>(new Map());

  useEffect(() => {
    let disposed = false;
    // Chart ids are unique per sheet instance, so a reloaded batch never shows a stale chart (sdmsLiveCharts).
    const chartPrefix = `sdms-chart-${Math.random().toString(36).slice(2, 10)}`;
    let ribbonObserver: MutationObserver | null = null;
    /** True once onReady has fired for THIS load — calculations before it are the load's own (the
     *  host's "sheet not ready" panel), after it a recalculation (the host's chip). */
    let readyFired = false;
    calcStateRef.current = { active: false, last: performance.now() };
    const calcSubs: Array<{ dispose?: () => void } | undefined> = [];
    userInteractedRef.current = false;
    editsRef.current = new Map();
    importedImagesRef.current = [];   // a new buffer either carries the saved pictures itself or starts clean
    runtimeFormulasRef.current = new Map();   // a new buffer already carries any formula persisted from the last one
    runtimeNumFmtsRef.current = new Map();
    textCellsRef.current = new Map();
    qcNoteCellsRef.current = new Map();   // a new workbook starts without notes (OG adds the QC ones on BindGrid / Save only)
    programmaticCellsRef.current = new Map();   // marks belong to the workbook they were written into
    systemCellsRef.current = new Map();
    // A new `buf` drops the in-place sheet replacements (replaceSheets); a formulaKey rebuild of the SAME
    // buffer keeps them, so it redraws what the analyst was looking at.
    if (baseForBufRef.current !== buf) { baseForBufRef.current = buf; baseOverrideRef.current = null; }
    const loadBuf = baseOverrideRef.current ?? buf;
    (async () => {
      const container = containerRef.current;
      if (!container) return;
      // Derived from the function itself: this used to restate excelToUniver's
      // return shape by hand, which silently went stale the moment a field was
      // added to it (chartBoxes, 2026-08-24).
      onLoadStageRef.current?.('building', null);
      // One macrotask so the host panel paints the new step before the (main-thread heavy) mapping starts.
      await new Promise(res => setTimeout(res, 0));
      if (disposed) return;
      let mapped: Awaited<ReturnType<typeof excelToUniver>>;
      // Emit + compute formulas when the sheet is editable (Enter) OR when `buf` is a fresh
      // re-merge (its formula cells have no cached results, so value-only would blank every
      // computed column — Result/RL/Rec%/QCStatus — and the blob-oriented recalc CAP would
      // wrongly suppress them; a re-merge must compute like Enter). Only a saved-blob buffer
      // (remerged=false) keeps the fast capped value-only path. (user 2026-09-16, View blank Result)
      const withFormulas = !SDMS_LAZY_FORMULAS || !!editableRef.current || !!remergedRef.current;
      hasFormulasRef.current = withFormulas;
      try {
        mapped = await excelToUniver(loadBuf, withFormulas, dfOverlayRef.current, qcBatchIdRef.current,
          (done, total) => { if (!disposed) onLoadStageRef.current?.('building', Math.round((done * 100) / total)); });
      }
      catch (e) { console.warn('[SDMS/Univer] map failed', e); onReadyRef.current?.(); return; }
      locksRef.current = mapped.locks;
      {
        const d = mapped.data as { sheetOrder?: string[]; sheets?: Record<string, { name?: string }> };
        const names = (d.sheetOrder ?? []).map(id => d.sheets?.[id]?.name ?? '').filter(Boolean);
        resultSheetsRef.current = new Set(names.filter((n, i) => isResultSheet(n, i)));
      }
      onChartBoxesRef.current?.(mapped.chartBoxes);
      if (disposed || !containerRef.current) return;
      try {
        // Univer paints several bits of chrome from the theme's cool-blue accent:
        //  • gray.300 (#A8B0BD) → the freeze-handle band at the frozen-row boundary
        //  • primary.* (#2C53F1) → the cell-SELECTION box (current-cell border +
        //    fill handle) and its row/column-header highlight
        // The blue read as stray next to our black grid.  gray stays a neutral
        // grey; primary is the APP'S BRAND ORANGE so the current-cell selection
        // matches the rest of the UI (user request 2026-08-17 — Excel-style
        // orange active-cell border; was neutral grey before that).
        const sdmsTheme = {
          ...defaultTheme,
          gray:    { ...defaultTheme.gray,    300: '#BDBDBD' },
          primary: { ...defaultTheme.primary, 500: '#E8670A', 600: '#D55E09', 700: '#B95208' },
        };
        // CF SAFETY NET (2026-08-13): registering the conditional-formatting preset is
        // the ONE CF step that cannot be wrapped in a try/catch of its own, because it
        // happens inside createUniver — and a failure there is what left the sheet
        // blank when CF was first switched on.  So build Univer WITH CF, and if that
        // throws, wipe the container and rebuild WITHOUT it.  The grid always renders;
        // the colours are best-effort.  (The other two CF steps — extraction inside
        // excelToUniver, and rule application — are individually try/caught.)
        // Core preset carries the FORMULA BAR (opt-in via the `formulaBar` prop — OG
        // parity): Univer puts the FormulaBar in BuiltInUIPart.HEADER and @univerjs/ui
        // renders that part only when `header && toolbar` are BOTH truthy, so both must
        // be enabled to get it.  That also mounts Univer's ribbon, which sdmsEntry.css
        // hides (`[data-u-comp="ribbon-toolbar"]` / `ribbon-header-menu`) because the
        // SDMS page supplies its own ribbon.  Default (Result Validation) keeps header
        // false — unchanged behaviour.  Filter preset adds the AutoFilter dropdowns.
        const buildPresets = (withCf: boolean) => [
          UniverSheetsCorePreset({
            container, contextMenu: true,
            header: !!formulaBar, toolbar: !!formulaBar, formulaBar: !!formulaBar,
            // Our cells carry display TEXT (e.g. "0.5000"), so Univer would flag
            // every numeric-looking string with a "number stored as text" corner
            // mark + the sheets-ui.info.forceStringInfo alert.  Suppress both.
            sheets: { disableForceStringAlert: true, disableForceStringMark: true },
            // A CACHED result is authoritative — recompute ONLY the cells that lack one.
            //
            // DevExpress does not recalculate a formula cell that carries a cached
            // value; it renders the cache. Univer was recomputing ours and disagreeing:
            // on QB26081203_032 the MS rows cache Rec %=95.2 and QCStatus="Pass" and
            // rendered BLANK here, and RPD % cached 0 rendered 0.5 (client 2026-08-21).
            // A different value, not merely a missing one, is what proves it recomputed.
            //
            // CalculationMode.WHEN_EMPTY is Univer's documented default, but state
            // it explicitly: the cells that DO need computing still get it - which is
            // what the 2026-08-18 Ammonia_ISE fix depends on, where QCStatus has no
            // cached result and ALPACA computes it too - while a cached cell is left
            // exactly as DevExpress wrote it. Setting it here also protects against a
            // preset default changing under us.
            formula: { initialFormulaComputing: CalculationMode.WHEN_EMPTY },
            // Hide the destructive context-menu items (Copy Special, Paste Special, Delete,
            // Clear Format/All, Protect Rows And Columns). The preset forwards `menu` to
            // UniverUIPlugin at runtime even though its typed options omit it — cast to include it.
            menu: SDMS_CONTEXT_MENU_HIDE,
          } as Parameters<typeof UniverSheetsCorePreset>[0] & { menu: typeof SDMS_CONTEXT_MENU_HIDE }),
          UniverSheetsFilterPreset(),
          // ALPACA's picker is the bare value list. Univer adds a search box and an
          // "Edit" link (which opens the rule editor — meaningless here, the rules come
          // from the template), so both are turned off (client 2026-08-20: "no need to
          // show the dropdown together with the Edit option").
          UniverSheetsDataValidationPreset({ showEditOnDropdown: false, showSearchOnDropdown: false }),
          // Floating pictures (imported chromatograms) — the range-anchored float DOM lives here.
          UniverSheetsDrawingPreset(),
          // Hover notes on the computed QC cells (display-only — never written to the saved blob).
          UniverSheetsNotePreset(),
          ...(withCf ? [UniverSheetsConditionalFormattingPreset()] : []),
        ];
        const boot = (withCf: boolean) => createUniver({
          locale: LocaleType.EN_US,
          locales: { [LocaleType.EN_US]: merge({}, UniverPresetSheetsCoreEnUS, UniverPresetSheetsFilterEnUS, UniverPresetSheetsCfEnUS, UniverPresetSheetsDvEnUS, UniverPresetSheetsDrawingEnUS, UniverPresetSheetsNoteEnUS, SDMS_LOCALE_OVERRIDE) },
          theme: sdmsTheme,
          presets: buildPresets(withCf),
        });
        let booted: ReturnType<typeof boot>;
        try {
          booted = boot(SDMS_CF_ENABLED);
        } catch (e) {
          if (!SDMS_CF_ENABLED) throw e;          // nothing CF-related to blame — real failure
          // eslint-disable-next-line no-console
          console.warn('[SDMS] conditional-formatting preset failed to register — rendering without QC colours', e);
          container.innerHTML = '';               // drop any half-built Univer DOM
          booted = boot(false);
        }
        const { univer, univerAPI } = booted;
        handleRef.current = { univer: univer as unknown as { dispose?: () => void } };
        const fapi = univerAPI as unknown as FApiLite;
        apiRef.current = fapi;
        // Re-fit the canvas when its container changes size. Full-screen hides/shows the app
        // nav, which widens/narrows the sheet container, but Univer only re-fits its canvas on a
        // WINDOW resize, not a container-only change — so observe the container and synthesize a
        // window resize (rAF-debounced), which is exactly what re-fits it when the browser is
        // resized.
        try {
          resizeObsRef.current?.disconnect();
          let rafId = 0;
          const ro = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
              try { window.dispatchEvent(new Event('resize')); } catch { /* noop */ }
            });
          });
          ro.observe(container);
          resizeObsRef.current = ro;
        } catch { /* ResizeObserver unavailable */ }
        // BEFORE createWorkbook, so `_LIMSRound` resolves on the FIRST calculation
        // pass — registering afterwards leaves the initial render showing the error
        // it was meant to fix.
        // Shared formulas (sdmsSharedFormulas.ts): each column's row-shifted copies of one formula parse ONCE
        // instead of once per row — Univer's AST cache is smaller than a VOC sheet's formula count. The full
        // formulas are kept first for the protected-cell revert.
        {
          const kept: Record<string, Record<number, Record<number, { f?: string; v?: unknown }>>> = {};
          const so = (mapped.data as { sheets?: Record<string, { name?: string; cellData?: Record<number, Record<number, { f?: string; v?: unknown }>> }> }).sheets ?? {};
          for (const sh of Object.values(so)) {
            if (!sh?.name) continue;
            const m: Record<number, Record<number, { f?: string; v?: unknown }>> = {};
            for (const [r, row] of Object.entries(sh.cellData ?? {}))
              for (const [c, cd] of Object.entries(row ?? {}))
                if (cd && typeof cd.f === 'string') (m[+r] ??= {})[+c] = { f: cd.f, v: cd.v };
            kept[sh.name] = m;
          }
          formulaCellsRef.current = kept;
          try { shareFormulas(mapped.data as never); }
          catch (e) { console.warn('[SDMS/Univer] shared formulas skipped', e); }
        }
        fapi.createWorkbook(mapped.data);
        // AFTER createWorkbook, synchronously — NOT before. Executors registered
        // before the first unit exists are wiped when the built-ins land during
        // first-unit boot (probe-verified; registering early is how this fix
        // silently "changed nothing" twice). Registered here they win, and the
        // first ASYNC calculation pass — which hasn't run yet — already uses them.
        // NB: the `univer` INSTANCE, not `univerAPI` — the injector hangs off the
        // instance, and the facade route needs a service the presets build omits.
        if (limsRound) registerLimsRound(univer);

        // Calculation progress for the host: the FIRST calculation feeds its "sheet not ready" panel
        // (onLoadStage), later ones its recalculation chip (onRecalc). Univer's own formula-bar progress
        // bar is hidden (sdmsEntry.css) — its × stopped a calculation half-way, leaving stale results.
        onLoadStageRef.current?.('calculating', null);
        try {
          type CalcInfo = { totalFormulasToCalculate?: number; completedFormulasCount?: number;
            totalArrayFormulasToCalculate?: number; completedArrayFormulasCount?: number };
          type Sub = { dispose?: () => void } | undefined;
          const formula = (fapi as unknown as { getFormula?: () => {
            calculationProcessing?: (cb: (p: CalcInfo) => void) => Sub;
            calculationEnd?: (cb: () => void) => Sub;
          } | undefined }).getFormula?.();
          const pctOf = (p: CalcInfo): number | null => {
            const total = (p.totalFormulasToCalculate ?? 0) + (p.totalArrayFormulasToCalculate ?? 0);
            const done = (p.completedFormulasCount ?? 0) + (p.completedArrayFormulasCount ?? 0);
            return total > 0 ? Math.min(100, Math.round((done * 100) / total)) : null;
          };
          // Monotonic per load / per recalculation: the dependency cascade runs several passes, each
          // counting from 0 again, and a bar that slid backwards would read as stuck.
          // Emitted only when the percent changes: each emit re-renders the host page, and the engine raises
          // many progress events per calculation.
          let loadPct: number | null = null;
          let recalcPct: number | null = null;
          let recalcEmitted = false;
          // What the running calculation is for. Univer evaluates conditional-formatting and data-validation
          // rules with the same engine, lazily as sheets are drawn (the summary sheets carry ~900 rules), and
          // those passes change no cell — so after the load only CELL recalculations reach the host's chip
          // (user 2026-09-28: "Recalculating" still showed after the sheet loaded).
          let calcKind: 'cells' | 'rules' = 'cells';
          let calcStartedAt = 0;
          /** The last commands before a calculation started, and what it recalculates — logged with a slow
           *  recalculation after the load, so an unexpected one names its trigger. */
          const recentCmds: Array<{ id: string; at: number }> = [];
          let calcCause = '';
          const report = (pct: number | null) => {
            if (disposed) return;
            if (readyFired) {
              if (calcKind === 'rules') return;
              const next = pct != null ? Math.max(recalcPct ?? 0, pct) : recalcPct;
              if (recalcEmitted && next === recalcPct) return;
              recalcPct = next; recalcEmitted = true;
              onRecalcRef.current?.(recalcPct, false);
            } else {
              const next = pct != null ? Math.max(loadPct ?? 0, pct) : loadPct;
              if (next === loadPct && pct != null) return;
              loadPct = next;
              onLoadStageRef.current?.('calculating', loadPct);
            }
          };
          type StartParams = { forceCalculation?: boolean; dirtyRanges?: unknown[];
            dirtyNameMap?: Record<string, unknown>; dirtyDefinedNameMap?: Record<string, unknown> };
          const hasKeys = (m?: Record<string, unknown>) => !!m && Object.keys(m).length > 0;
          const onCommand = (fapi as unknown as {
            onCommandExecuted?: (cb: (c: { id: string; params?: unknown }) => void) => Sub;
          }).onCommandExecuted;
          calcSubs.push(
            // The start mutation itself (what the facade's calculationStart wraps) — it carries what is dirty.
            onCommand?.call(fapi, c => {
              if (!/^formula\.|^sheet\.operation\./.test(c.id)) {
                recentCmds.push({ id: c.id, at: performance.now() });
                if (recentCmds.length > 20) recentCmds.shift();
              }
              // Any change to the workbook counts as activity for waitCalcIdle: a cell write, but also the
              // filter / row-visibility / sheet mutations Univer registers as making cells dirty (creating a
              // header filter starts a full cell recalculation — proven headless). Each queues a calculation
              // 100 ms later. UI operations (selection, scrolling) are not activity.
              if (!c.id.startsWith('sheet.operation.')) {
                calcStateRef.current = { active: calcStateRef.current.active, last: performance.now() };
              }
              if (c.id !== 'formula.mutation.set-formula-calculation-start') return;
              const p = (c.params ?? {}) as StartParams;
              calcKind = p.forceCalculation || (p.dirtyRanges?.length ?? 0) > 0 || hasKeys(p.dirtyNameMap)
                || hasKeys(p.dirtyDefinedNameMap) ? 'cells' : 'rules';
              calcStartedAt = performance.now();
              calcStateRef.current = { active: true, last: calcStartedAt };
              calcStartsRef.current++;
              if (readyFired && calcKind === 'cells') {
                const before = [...new Set(recentCmds.filter(r => calcStartedAt - r.at < 2000).map(r => r.id))];
                const sheets = new Set<string>();
                for (const d of (p.dirtyRanges ?? []) as Array<{ sheetId?: string }>) {
                  const nm = (fapi.getActiveWorkbook?.() as unknown as { getSheetBySheetId?: (id: string) => { getSheetName?: () => string } | null } | null)
                    ?.getSheetBySheetId?.(d.sheetId ?? '')?.getSheetName?.();
                  if (nm) sheets.add(nm);
                }
                calcCause = ` — ${(p.dirtyRanges ?? []).length} dirty range(s)${sheets.size ? ` on ${[...sheets].slice(0, 4).join(', ')}${sheets.size > 4 ? '…' : ''}` : ''}${p.forceCalculation ? ', forced' : ''}; after: ${before.slice(-6).join(', ') || 'nothing'}`;
              } else calcCause = '';
              report(null);
            }),
            formula?.calculationProcessing?.(p => report(pctOf(p))),
            formula?.calculationEnd?.(() => {
              const now = performance.now();
              calcStateRef.current = { active: false, last: now };
              if (disposed || !readyFired) return;
              if (now - calcStartedAt >= 500) {
                console.info(`[SDMS] recalculation (${calcKind === 'cells' ? 'cell formulas' : 'formatting / dropdown rules'}) took ${((now - calcStartedAt) / 1000).toFixed(1)} s${calcCause}`);
              }
              recalcPct = null; recalcEmitted = false; onRecalcRef.current?.(100, true);
            }),
          );
        } catch (e) { console.warn('[SDMS/Univer] calculation progress unavailable', e); }

        // AutoFilter dropdowns on each sheet's header row (LIMS parity) — created HERE, straight after the
        // workbook and before its first calculation starts: creating a filter marks its range for
        // recalculation, and made after the load it cost a whole extra pass (5.4 s on a VOC batch, shown as a
        // "Recalculating…" chip after the sheet opened — user 2026-09-28). Created now, that dirt merges into
        // the initial calculation. Still before the read-only lock below.
        try {
          const fwb = fapi.getActiveWorkbook?.();
          for (const fs of fwb?.getSheets?.() ?? []) {
            const rng = mapped.filters[fs.getSheetName?.() ?? ''];
            if (rng) fs.getRange?.(rng)?.createFilter?.();
          }
        } catch (e) { console.warn('[SDMS/Univer] filter apply failed', e); }

        // Lock the sheet to result entry: deny the structural context-menu actions
        // (insert/delete/hide/resize row & column) on every sheet — OG never allows
        // them on an SDMS sheet. Univer's permission check greys the menu items AND
        // blocks the commands. unitId/sheet ids are exactly what the mapper wrote
        // (id 'sdms-workbook', sheetOrder 'sheet-0'…), so Univer honours them verbatim.
        try {
          const wbData = mapped.data as { id?: string; sheetOrder?: string[]; sheets?: Record<string, unknown> };
          const unitId = wbData.id ?? 'sdms-workbook';
          const subUnitIds = wbData.sheetOrder ?? Object.keys(wbData.sheets ?? {});
          restrictSdmsSheetActions(univer, unitId, subUnitIds);
        } catch (e) { console.warn('[SDMS/Univer] sheet-action restriction skipped', e); }

        // Imported instrument pictures (SDMS Import Files — OG BindImagestoSheet): register the
        // float-DOM component that draws them, then show every picture the workbook already carries
        // (a saved batch whose import added chromatograms). Best-effort — a picture must never cost
        // the sheet its rendering.
        try {
          (fapi as unknown as { registerComponent?: (k: string, c: unknown) => unknown })
            .registerComponent?.(IMPORT_IMAGE_COMPONENT, SdmsImportImageDom);
          const pics = await readSheetPictures(loadBuf);
          if (disposed) return;
          let shown = 0;
          for (const p of pics)
            if (floatPicture(fapi, p.sheetName, p.row, p.col, p.widthPx || 480, p.heightPx || 288,
                             `data:${p.mime};base64,${p.base64}`)) shown++;
          // eslint-disable-next-line no-console
          if (pics.length) console.info(`[SDMS] embedded pictures shown: ${shown}/${pics.length}`);
        } catch (e) { console.warn('[SDMS] embedded pictures skipped', e); }

        // Embedded CHARTS (the calibration curve), drawn over their anchors and following the live
        // values — only when the host passes the template that carries them. Best-effort.
        if (chartSourceRef.current) void floatWorkbookCharts(fapi, chartSourceRef.current, mapped.data, chartPrefix);

        // ── Conditional formatting (OG parity: pink "Fail" / blue "True", and
        // Rec %/RPD % green-vs-pink against their control limits). Applied AFTER
        // createWorkbook because the CF facade hangs off a live FRange. Best-effort
        // per sheet — a template with rules we can't express must still render.
        /* DEFERRED until the calculation settles — see the call after the restore pass.
           CF used to be applied here, immediately after createWorkbook, while the formula
           engine was still computing asynchronously. Univer evaluates a CF formula against
           the values present AT THAT MOMENT, so on a sheet whose cells are still empty the
           rules resolve to false and the colour never arrives; a small sheet finishes
           computing fast enough to look fine, which is why this only ever showed up on the
           big Metals/ICP templates (client 2026-08-24, IS % Of AvgResponse). Running it
           after the values are final means CF tests what the analyst actually sees. */
        let cfApplied = false;
        /* ONLY THE SHEET ON SCREEN AT OPEN (speed, 2026-10-01). Every other sheet's rules are added the first
           time it becomes the active tab. The colour rules are display-only (never saved — Save rewrites the
           base xlsx), but Univer evaluates them after adding, and the panel waits for that to settle: VOC adds
           1,358 rules (the two summary sheets alone 926) for ~2.7 s, while the sheet on screen needs 27; PFAS
           528 for ~1.2 s (36 on screen). Same rules, same colours — only WHEN a hidden sheet's rules go in. */
        const cfDone = new Set<string>();
        const activeSheetName = (): string | null => {
          try {
            return (fapi as unknown as { getActiveWorkbook?: () => { getActiveSheet?: () => { getSheetName?: () => string } | null } | null })
              .getActiveWorkbook?.()?.getActiveSheet?.()?.getSheetName?.() ?? null;
          } catch { return null; }
        };
        const applyCfTo = async (names: readonly string[]) => {
        const todo = names.filter(n => !cfDone.has(n) && !!mapped.cfs[n]);
        if (!todo.length || disposed) return;
        for (const n of todo) cfDone.add(n);
        /* READ-ONLY WORKBOOK (View mode): Univer's CF plugin checks every AddCfCommand against the workbook's
           edit permission (ConditionalFormattingPermissionController → blockExecuteWithoutPermission), and the
           command service SWALLOWS that refusal — the facade returns as if the rule was added. The rules are
           added here, after the restore pass has put read-only back, so on a View-mode (validated) batch every
           rule was dropped while the console still reported "27 rule(s) applied": no QC colour at all (VOC
           QB26092901_001, user 2026-09-30). Lift read-only for the adds, as the restore pass does for its
           writes (a permission change lands asynchronously, hence the yield), and put it back straight after —
           the adds are synchronous, and the colours then compute without any further command. */
        const wbPerm = (fapi as unknown as {
          getActiveWorkbook?: () => { setEditable?: (v: boolean) => unknown } | null;
        }).getActiveWorkbook?.();
        const lift = !editableRef.current;
        if (lift) {
          wbPerm?.setEditable?.(true);
          await new Promise(res => setTimeout(res, 0));
          if (disposed) return;
        }
        try {
          if (!SDMS_CF_ENABLED) throw new Error('cf-disabled');
          const wbApi = (fapi as unknown as {
            getActiveWorkbook?: () => { getSheetByName?: (n: string) => unknown } | null;
          }).getActiveWorkbook?.();
          for (const sheetName of todo) {
            const cf = mapped.cfs[sheetName]!;
            const sh = wbApi?.getSheetByName?.(sheetName) as {
              getRange?: (r: number, c: number) => unknown;
            } | null | undefined;
            if (!sh?.getRange) continue;
            // Builder + add live on a Range in this build; anchor at A1 and let each
            // rule carry its own setRanges(...).
            const anchor = sh.getRange(0, 0) as Parameters<typeof applySdmsConditionalFormats>[1];
            // Hand the CF pass this sheet's USED extent so a whole-column /
            // whole-row format is clamped to real cells instead of Excel's
            // theoretical maximum — see the clamp note in sdmsConditionalFormat.
            const meta = Object.values(
              (mapped.data as { sheets?: Record<string, { name?: string; rowCount?: number; columnCount?: number }> }).sheets ?? {},
            ).find(x => x?.name === sheetName);
            const n = applySdmsConditionalFormats(
              cf.blocks as never, anchor, cf.bandStart, cf.bandEnd,
              Math.max(0, (meta?.rowCount ?? 0) - 1), Math.max(0, (meta?.columnCount ?? 0) - 1));
            // Logged ALWAYS, on one line, not only on failure: chasing this bug cost
            // several rounds of "please expand the collapsed console entry", and a plain
            // summary is visible in any screenshot.
            // eslint-disable-next-line no-console
            console.info(`[SDMS-CF] "${sheetName}": ${n} rule(s) applied`);
            if (!n) {
              // eslint-disable-next-line no-console
              console.warn(`[SDMS] no conditional-format rules applied to "${sheetName}" — QC colours will be missing.`);
            }
          }
        } catch (e) {
          if (SDMS_CF_ENABLED) {
            // eslint-disable-next-line no-console
            console.warn('[SDMS] conditional formatting could not be applied; sheet renders without QC colours.', e);
          }
        } finally {
          if (lift) wbPerm?.setEditable?.(!!editableRef.current);
        }
        };
        const runConditionalFormats = async () => {
          // Idempotent, and called from BOTH the success and the failure path of the wait
          // below: deferring must never become a way for the colours to be skipped entirely.
          if (cfApplied) return;
          cfApplied = true;
          const all = Object.keys(mapped.cfs);
          const active = activeSheetName();
          // No readable active sheet → every sheet now, as before.
          await applyCfTo(active === null ? all : [active]);
          if (cfDone.size >= all.length || disposed) return;
          const onCmd = (fapi as unknown as {
            onCommandExecuted?: (cb: (c: { id: string }) => void) => { dispose?: () => void } | undefined;
          }).onCommandExecuted;
          calcSubs.push(onCmd?.call(fapi, c => {
            if (c.id !== 'sheet.operation.set-worksheet-active') return;
            const nm = activeSheetName();
            if (nm) void applyCfTo([nm]);
          }));
        };

        // In-cell DROPDOWNS from the workbook's own data validation (see
        // sdmsDataValidation.ts). Applied here, after createWorkbook, because the
        // validation facade — like the CF one — works off a live FRange.
        //
        // Best-effort per rule: a template we cannot express must still render, so a
        // failure costs the picker on that column, never the sheet. That is the same
        // bargain the CF block above makes, and it is deliberate — the SDMS sheet going
        // blank is the one outcome worth avoiding at any cost.
        try {
          const wbDv = (fapi as unknown as {
            getActiveWorkbook?: () => { getSheetByName?: (n: string) => unknown } | null;
          }).getActiveWorkbook?.();
          const newDv = (fapi as unknown as {
            newDataValidation?: () => {
              requireValueInList: (v: string[], multiple?: boolean) => {
                setOptions: (o: { renderMode: number }) => { build: () => unknown };
              };
            };
          }).newDataValidation;
          let applied = 0;
          const dvCells = new Set<string>();          // `${sheetName}:${row}:${col}`
          const dvOptions = new Map<string, string[]>();   // same key -> that cell's option texts
          const dvMultiple = new Set<string>();             // keys of MULTI-select cells (the Qualifier column)
          for (const [sheetName, rules] of Object.entries(mapped.dvs)) {
            const sh = wbDv?.getSheetByName?.(sheetName) as {
              getRange?: (r: number, c: number, nr?: number, nc?: number) => {
                setDataValidation?: (rule: unknown) => void;
              } | null;
            } | null | undefined;
            if (!sh?.getRange || !newDv) break;
            for (const rule of rules as SheetDvRule[]) {
              // One built rule per RANGE: FDataValidation carries its own ranges once
              // attached, and re-using a single built instance across ranges attaches it
              // only to the last one.
              for (const r of rule.ranges) {
                const range = sh.getRange(
                  r.startRow, r.startColumn,
                  r.endRow - r.startRow + 1, r.endColumn - r.startColumn + 1);
                range?.setDataValidation?.(
                  newDv.call(fapi).requireValueInList(rule.values, !!rule.multiple)
                    .setOptions({ renderMode: DV_RENDER_TEXT }).build());
                applied++;
                for (let rr = r.startRow; rr <= r.endRow; rr++) {
                  for (let cc = r.startColumn; cc <= r.endColumn; cc++) {
                    const key = `${sheetName}:${rr}:${cc}`;
                    dvCells.add(key);
                    dvOptions.set(key, rule.values);
                    if (rule.multiple) dvMultiple.add(key);
                  }
                }
              }
            }
          }
          if (Object.keys(mapped.dvs).length && !applied) {
            // eslint-disable-next-line no-console
            console.warn('[SDMS] the workbook declares dropdowns but none were applied — cells will accept free text.');
          }

          // SINGLE click opens the picker — ALPACA needs one click, Univer needs the cell
          // to be in edit mode (a double click) because TEXT render mode draws no arrow to
          // click on. So we listen for the click ourselves and run Univer's own
          // show-dropdown operation, which is what its arrow would have invoked.
          // Guarded by dvCells so a click on an ordinary cell does nothing.
          const ev = (fapi as unknown as { Event?: Record<string, string> }).Event;
          const addEvent = (fapi as unknown as {
            addEvent?: (n: string, cb: (p: unknown) => void) => { dispose?: () => void };
          }).addEvent;
          if (ev?.CellClicked && addEvent) {
            addEvent.call(fapi, ev.CellClicked, (params: unknown) => {
              const q = params as {
                workbook?: { getId?: () => string };
                worksheet?: { getSheetId?: () => string; getSheetName?: () => string };
                row?: number; column?: number;
              };
              const sheetName = q.worksheet?.getSheetName?.();
              const row = q.row; const column = q.column;
              if (sheetName == null || row == null || column == null) return;
              selRef.current = { sheetName, row, col: column };   // track selection for the Qualifier action
              if (!dvCells.has(`${sheetName}:${row}:${column}`)) return;
              // READ-ONLY sheets (Preview, and View before Edit) must not offer the picker.
              // The workbook is already read-only via setEditable, but opening the dropdown
              // ourselves bypassed that and let a viewer change a cell in Preview (client
              // 2026-08-20). ALPACA blocks the same way — SpreadsheetEntryController wires
              // CellBeginEdit to `e.cancel = s.cpisedit`, set from the SDMSIsWrite
              // permission, so a cell edit is refused outright when the user may not write.
              if (!editableRef.current) { onEditBlockedRef.current?.(); return; }
              const unitId = q.workbook?.getId?.();
              const subUnitId = q.worksheet?.getSheetId?.();
              if (!unitId || !subUnitId) return;
              void (fapi as unknown as {
                executeCommand?: (id: string, p: object) => Promise<unknown>;
              }).executeCommand?.('sheet.operation.show-data-validation-dropdown',
                { unitId, subUnitId, row, column });
            });
          }

          // Keep the picker's own casing, and keep the cell a STRING.
          //
          // Univer recognises "True"/"False" as booleans and types the cell
          // CellValueType.BOOLEAN; @univerjs/core then renders any boolean cell uppercased
          //     if (cell.t === 3) return String(displayValue).toUpperCase();
          // so picking True produced a cell reading TRUE beside merge-written rows still
          // reading True - and the list rule, whose options are the STRINGS "True"/"False",
          // then failed to match the boolean and flagged the cell "Invalid: Input must fall
          // within specified range" (client 2026-08-20).
          //
          // Re-set the cell as an explicit string carrying the option's exact spelling. This
          // is also right for the DATA, not just the display: IsReport is written back to
          // SpreadSheetEntry_SampleInfo as that literal.
          //
          // Self-terminating - the rewrite fires this event again, the value then matches an
          // option exactly and t is no longer BOOLEAN, so the second pass does nothing.
          if (ev?.SheetValueChanged && addEvent) {
            addEvent.call(fapi, ev.SheetValueChanged, (params: unknown) => {
              /* This normaliser exists to fix the CASE of a value the analyst PICKED from a
                 dropdown, so it has no business running when nobody can pick — and running
                 anyway is what put the "protected sheet" dialog on screen the moment a batch
                 was opened (client 2026-08-24, QB26072923_076).
                 The chain: boot ends with setEditable(false); the cached-value restore then
                 resumes from its await, lifts read-only, and writes; each write raises
                 SheetValueChanged; this listener wrote AGAIN through setValue — but by then
                 the restore's `finally` had already put read-only back, so Univer refused the
                 write and popped its own sheet-permission dialog (identified from the DOM:
                 class `sheet-permission-user-dialog`). The analyst had touched nothing.
                 Two guards, either of which alone closes it: our own restore writes are not
                 picks, and a read-only sheet has no picker at all. */
              if (restoringRef.current) return;
              if (!editableRef.current) return;
              const q = params as { effectedRanges?: unknown[] };
              // Surface genuine edits to the host (debounced server-side Engine B
              // recompute for Fixed-bound templates; the host ignores it otherwise).
              if (onCellEditedRef.current) {
                const edits: Array<{ sheetName: string; row: number; col: number }> = [];
                for (const rg of q.effectedRanges ?? []) {
                  const fr = rg as { getSheetName?: () => string; getRange?: () => { startRow: number; startColumn: number; endRow: number; endColumn: number } };
                  const nm = fr.getSheetName?.(); const box = fr.getRange?.();
                  if (!nm || !box) continue;
                  for (let r = box.startRow; r <= box.endRow; r++) edits.push({ sheetName: nm, row: r, col: box.startColumn });
                }
                if (edits.length) onCellEditedRef.current(edits);
              }
              for (const rg of q.effectedRanges ?? []) {
                const fr = rg as {
                  getSheetName?: () => string;
                  getRange?: () => { startRow: number; startColumn: number; endRow: number; endColumn: number };
                };
                const sheetName = fr.getSheetName?.();
                const box = fr.getRange?.();
                if (!sheetName || !box) continue;
                // FRange has no getCell(row, col) — go back through the sheet for each cell.
                const sh = wbDv?.getSheetByName?.(sheetName) as {
                  getRange?: (r: number, c: number, nr?: number, nc?: number) => {
                    getCellData?: () => { v?: unknown; t?: number } | null;
                    setValue?: (v: unknown) => unknown;
                  } | null;
                } | null | undefined;
                if (!sh?.getRange) continue;
                for (let r = box.startRow; r <= box.endRow; r++) {
                  for (let c = box.startColumn; c <= box.endColumn; c++) {
                    const opts = dvOptions.get(`${sheetName}:${r}:${c}`);
                    if (!opts) continue;
                    const cellRange = sh.getRange(r, c, 1, 1);
                    const data = cellRange?.getCellData?.();
                    const v = data?.v;
                    if (v === null || v === undefined || v === '') continue;
                    // A MULTI-select pick: Univer's list-multiple dropdown stores the picks as a JSON array
                    // (serializeListOptions → '["B","J"]'), but OG — and our Qualifiers picker — hold the
                    // comma-joined symbols ("B,J"). Left as JSON it was SAVED as '["*"]' and the Qualifier
                    // hover notes found no symbol (user 2026-09-24). Rewrite to the comma form; Univer's own
                    // deserializeListOptions reads that back (its fallback splits on ","), so the chips and
                    // the picker's ticks are unchanged.
                    if (dvMultiple.has(`${sheetName}:${r}:${c}`)) {
                      const joined = qualifierPickText(String(v), opts);
                      if (joined !== null && joined !== v) cellRange?.setValue?.({ v: joined, t: 1 });
                      continue;
                    }
                    const isBool = data?.t === 3;                  // CellValueType.BOOLEAN
                    // A boolean cell may hold true/false OR 1/0 — normalise before matching.
                    const text = isBool
                      ? (v === true || v === 1 || String(v).toUpperCase() === 'TRUE' ? 'TRUE' : 'FALSE')
                      : String(v);
                    const match = opts.find(o => o.toUpperCase() === text.toUpperCase());
                    if (!match) continue;                          // not one of the options — leave it
                    if (!isBool && match === v) continue;           // already exact
                    cellRange?.setValue?.({ v: match, t: 1 });      // 1 = CellValueType.STRING
                  }
                }
              }
            });
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[SDMS] data-validation dropdowns could not be applied; cells accept free text.', e);
        }

        // PUT THE CACHED RESULTS BACK once Univer's calculation settles.
        //
        // DevExpress renders a formula cell's CACHED result and never recalculates it.
        // Univer must compute the cells that carry NO cache (the 2026-08-18 Ammonia_ISE
        // fix depends on that), but computing them makes their DEPENDENTS dirty, and those
        // recompute over a perfectly good cached value. Where Univer's answer differs from
        // DevExpress's, the sheet silently disagrees with LIMS: on QB26081203_032 the MS
        // rows cached Rec %=95.2 / QCStatus="Pass" and went BLANK, and RPD % cached 0
        // rendered 0.5. The analyst watched the correct values paint and then vanish a
        // moment later (client 2026-08-21) - which is exactly this second pass landing.
        //
        // initialFormulaComputing alone cannot prevent it: that governs the FIRST pass, not
        // the dependency cascade after it. So we let the cascade run and then restore what
        // DevExpress stored, which is the value ALPACA shows.
        //
        // Reads the whole block ONCE (getCellDataGrid) and writes back only the cells that
        // actually differ - normally a handful - so a fully-agreeing sheet costs one read.
        // `restoringRef` keeps the edit-capture listener from recording these as the
        // analyst's own edits, which would send them to SpreadSheetEntry_SampleInfo.
        try {
          const wbRestore = (fapi as unknown as {
            getFormula?: () => { whenComputingCompleteAsync?: () => Promise<unknown> } | undefined;
            getActiveWorkbook?: () => { getSheetByName?: (n: string) => unknown } | null;
          });
          await wbRestore.getFormula?.()?.whenComputingCompleteAsync?.();
          if (!disposed) {
            const wbApi = wbRestore.getActiveWorkbook?.();
            let restored = 0;
            // This runs AFTER the await, by which point the boot has already made the
            // workbook read-only (setEditable below) - so Univer refuses these writes and
            // pops its own "range is protected" dialog at the analyst (client 2026-08-21).
            // Lift it for the restore and put it straight back.
            const wbEdit = wbApi as unknown as { setEditable?: (v: boolean) => unknown } | null | undefined;
            restoringRef.current = true;                  // set BEFORE the lift, so no listener
            wbEdit?.setEditable?.(true);                  // can see a write without the flag
            // Univer propagates a permission change through its own services rather than
            // flipping a plain flag, so a write issued in the same tick can still be judged
            // against the OLD value. Yield once so the lift has certainly landed.
            await new Promise(res => setTimeout(res, 0));
            try {
              for (const [sheetName, cells] of Object.entries(mapped.cachedFormulaValues)) {
                const keys = Object.keys(cells);
                if (!keys.length) continue;
                const sh = wbApi?.getSheetByName?.(sheetName) as {
                  getRange?: (r: number, c: number, nr?: number, nc?: number) => {
                    getCellDataGrid?: () => ({ v?: unknown } | null)[][] | undefined;
                    setValue?: (v: unknown) => unknown;
                  } | null;
                } | null | undefined;
                if (!sh?.getRange) continue;
                let maxR = 0; let maxC = 0;
                for (const k of keys) {
                  const [r, c] = k.split(',').map(Number);
                  if (r! > maxR) maxR = r!;
                  if (c! > maxC) maxC = c!;
                }
                const grid = sh.getRange(0, 0, maxR + 1, maxC + 1)?.getCellDataGrid?.();
                for (const k of keys) {
                  const [r, c] = k.split(',').map(Number);
                  const want = cells[k]!;
                  const now = grid?.[r!]?.[c!]?.v;
                  // Compare as text: 95.2 and "95.2" are the same rendered cell.
                  if (now !== undefined && String(now) === String(want.v)) continue;
                  sh.getRange(r!, c!, 1, 1)?.setValue?.({ v: want.v, t: want.t });
                  restored++;
                }
              }
            } finally {
              /* Hold the lift for one more turn. A write raises SheetValueChanged, and a
                 listener that reacts to it can run AFTER this block — dropping read-only
                 back immediately is precisely what made those trailing writes illegal and
                 raised Univer's permission dialog on open. */
              await new Promise(res => setTimeout(res, 0));
              restoringRef.current = false;
              wbEdit?.setEditable?.(!!editableRef.current);
            }
            if (restored) {
              // eslint-disable-next-line no-console
              console.info(`[SDMS] restored ${restored} cached formula result(s) Univer had recomputed differently`);
            }
            /* NOW apply the conditional formats — the values are final, so the rules test
               what the sheet actually shows rather than a half-computed snapshot. */
            await runConditionalFormats();
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[SDMS] could not restore cached formula results; some cells may differ from LIMS', e);
        }
        // Belt and braces: if the wait above threw, was skipped, or the component was
        // disposed mid-flight, the colours still get applied (the call is idempotent).
        await runConditionalFormats();


        // Strip Univer's built-in ribbon (File / Home / … tabs + toolbar) while keeping the
        // formula bar. Only when `formulaBar` is on (that's what mounts the ribbon at all).
        // Re-run on every header rebuild — Univer re-renders the header bar on some
        // interactions, which would otherwise bring the tabs back.
        if (formulaBar) {
          // Hiding the ribbon moves the formula bar up AFTER Univer measured its editor canvas, which then
          // drew the bar's text offset and half-clipped (user 2026-09-28: "formula bar text is not visible
          // sometimes"). A synthetic resize after each strip that changed the layout makes Univer re-measure.
          let remeasure = 0;
          const stripRibbon = () => {
            if (disposed || !containerRef.current) return;
            if (!hideUniverRibbon(containerRef.current)) return;
            cancelAnimationFrame(remeasure);
            remeasure = requestAnimationFrame(() => { try { window.dispatchEvent(new Event('resize')); } catch (e) { console.warn('[SDMS/Univer] re-measure failed', e); } });
          };
          stripRibbon();
          ribbonObserver = new MutationObserver(stripRibbon);
          ribbonObserver.observe(container, { childList: true, subtree: true });
        }

        // ── FORMULA-CELL PROTECTION (OG parity) ──────────────────────────────
        // OG protects the SDMS sheet so the analyst can only type in ENTRY cells:
        // SpreadsheetEntryController.cs:3757-3786 unlocks $A:$XFD, then locks every
        // cell where `cell.HasFormula`, then `Protect("password", …)`.  Reproduced
        // here by cancelling Univer's BeforeSheetEditStart on those coordinates
        // (collected in excelToUniver).  Without this the analyst could type over a
        // computed column — exactly what happened in the user's 2026-08-12
        // side-by-side, where values landed in the "Temperature after CF applied"
        // formula column and the dependent result/QC columns never populated.
        try {
          const uapi = univerAPI as unknown as {
            addEvent?: (name: string, cb: (p: {
              worksheet?: { getSheetName?: () => string };
              row?: number; column?: number; cancel?: boolean;
              eventType?: unknown; keycode?: unknown;
            }) => void) => { dispose?: () => void };
            Event?: { BeforeSheetEditStart?: string };
          };
          const editEvt = uapi.Event?.BeforeSheetEditStart ?? 'BeforeSheetEditStart';
          protectDisposeRef.current = uapi.addEvent?.(editEvt, (p) => {
            const sheetName = p.worksheet?.getSheetName?.() ?? '';
            /* WHO OPENED THE EDITOR. Univer raises this from exactly one place:
               SetCellEditVisibleOperation with visible=true (sheets-ui/facade.js), i.e. the
               cell EDITOR opening - never a selection, a plain click, or a programmatic
               write. Worth logging because the warning text this handler shows is ALSO
               Univer's own permission dialog's text (our locale override supplies both), so
               the two are indistinguishable on screen. They are not indistinguishable here:
               if the analyst reports the protected-sheet warning and this line did NOT
               print, it was Univer's permission layer refusing a MUTATION, not this handler
               refusing an EDIT - which is exactly how the 2026-08-24 open-a-batch case was
               finally pinned (the DOM named it: class `sheet-permission-user-dialog`). */
            // eslint-disable-next-line no-console
            console.debug('[SDMS] edit-start on %s r%o c%o eventType=%o keycode=%o editable=%o',
              sheetName, p.row, p.column, p.eventType, p.keycode, editableRef.current);
            if (p.row == null || p.column == null) return;
            // A sheet that is not in EDIT mode refuses EVERY cell, not only the formula
            // cells: in View and in Preview nothing is editable until the analyst clicks
            // Edit. Univer's own permission layer used to catch these, but it let the
            // FIRST attempt through and then complained on the next one, so the analyst got
            // one silent free edit before any warning (client 2026-08-20). Refusing here
            // means the very first attempt is refused too, and with our own message.
            const editable = editableRef.current;
            if (editable && !isLockedCell(sheetName, p.row, p.column)) {
              // Genuine user edit on an allowed cell — arm the host's unsaved guard.
              onUserEditStartRef.current?.();
              return;
            }
            p.cancel = true;                              // refuse the edit
            console.warn('[SDMS/protect] edit refused — %s r%o c%o (editable=%o)', sheetName, p.row, p.column, editable);
            const now = Date.now();
            if (now - lastWarnRef.current > 2500) {       // throttle repeat attempts
              lastWarnRef.current = now;
              void warn(SDMS_PROTECTED_MSG, { title: 'Warning' });
            }
          }) ?? null;
        } catch (e) { console.warn('[SDMS/Univer] formula-cell protection failed', e); }

        // ── CLEAR / PASTE PROTECTION (OG parity) ─────────────────────────────
        // The BeforeSheetEditStart guard above only catches the cell EDITOR opening; it does
        // NOT catch direct-mutation commands — "Clear contents" (the Delete key / right-click
        // Clear submenu) and PASTE (Ctrl+V / Ctrl+Shift+V) — which write cells directly and
        // would wipe or overwrite the locked FORMULA cells OG's protected sheet forbids touching.
        // Cancel these commands when the current selection overlaps a locked cell, showing the
        // same protected-sheet warning as typing. A selection of only entry cells clears/pastes
        // normally. (user 2026-09-11) NOTE: paste is checked against the SELECTION anchor range;
        // a single-cell paste whose clipboard SPILLS into an adjacent formula column isn't caught
        // (the clipboard extent isn't on the event) — the direct paste-onto-a-formula-cell case is.
        try {
          const uapi2 = univerAPI as unknown as {
            addEvent?: (name: string, cb: (e: { id?: string; cancel?: boolean }) => void) => { dispose?: () => void };
            Event?: { BeforeCommandExecute?: string };
          };
          const GUARDED_CMDS = new Set([
            'sheet.command.clear-selection-content',
            'sheet.command.clear-selection-all',
            'sheet.command.clear-selection-format',
            'sheet.command.paste',
            'sheet.command.paste-by-short-key',   // Ctrl+V (the DOM-paste path — carries params.textContent)
            'sheet.command.optional-paste',
            'sheet.command.paste-value',
            'sheet.command.paste-formula',
            'sheet.command.paste-format',
            'sheet.command.paste-col-width',
            'sheet.command.paste-besides-border',
          ]);
          // Commands that can write BEYOND the selection (the clipboard / fill extent is not on the
          // event), i.e. the only user actions that can spill into a locked cell. Our own Ctrl+V /
          // context-menu paste writer never writes a locked cell, but it is listed for safety.
          const SPILL_CMDS = new Set([
            'sheet.command.paste', 'sheet.command.paste-by-short-key', 'sheet.command.optional-paste',
            'sheet.command.paste-value', 'sheet.command.paste-format', 'sheet.command.paste-col-width',
            'sheet.command.paste-besides-border', 'sheet.command.paste-formula',
            'sheet.command.auto-fill', 'sheet.command.refill', 'sheet.command.move-range',
          ]);
          const evt = uapi2.Event?.BeforeCommandExecute ?? 'BeforeCommandExecute';
          clearGuardDisposeRef.current = uapi2.addEvent?.(evt, (e) => {
            if (e?.id && SPILL_CMDS.has(e.id)) lastSpillCmdRef.current = Date.now();
            if (!e?.id || !GUARDED_CMDS.has(e.id)) return;
            const wb = fapi.getActiveWorkbook?.() as unknown as {
              getActiveSheet?: () => {
                getSheetName?: () => string;
                getActiveRange?: () => { getRange?: () => { startRow: number; startColumn: number; endRow: number; endColumn: number } } | null;
              } | null;
            } | null | undefined;
            const sh = wb?.getActiveSheet?.();
            const rg = sh?.getActiveRange?.()?.getRange?.();
            if (!rg) return;
            const shName = sh?.getSheetName?.() ?? '';
            let hit = lockedSheetsRef.current.has(shName.toUpperCase());   // a wholesale-locked sheet (Reagents / Instruments / Raw Data)
            // …else any locked cell in the selection: a formula cell or an Engine B output column.
            for (let r = rg.startRow; r <= rg.endRow && !hit; r++)
              for (let c = rg.startColumn; c <= rg.endColumn; c++)
                if (isLockedCell(shName, r, c)) { hit = true; break; }
            if (hit) {                                      // a locked cell in range → block the whole clear/paste (OG parity)
              e.cancel = true;
              console.warn('[SDMS/protect] %s refused on %s (locked cell in selection)', e.id, shName);
              const now = Date.now();
              if (now - lastWarnRef.current > 2500) { lastWarnRef.current = now; void warn(SDMS_PROTECTED_MSG, { title: 'Warning' }); }
              return;
            }
            // All ENTRY cells. Univer's paste applies the clipboard HTML (SheetsClipboardService
            // .legacyPaste), which carries the SOURCE cell's number format — so pasting a date turned
            // an entry cell into a date-formatted cell and every value typed afterwards showed as a
            // date (user 2026-09-16, TOC Numeric Result). Ctrl+V is `paste-by-short-key` (a DOM-paste
            // path that carries the clipboard text in params.textContent); the context-menu Paste is
            // `paste`. For both, CANCEL Univer's paste and write the PLAIN TEXT ourselves — numbers as
            // numbers, everything else as text — WITHOUT touching each cell's style, so the template
            // number format is never altered. Formula cells are skipped. (Earlier attempts on
            // `sheet.command.paste` never fired for Ctrl+V, which uses paste-by-short-key.)
            if (e.id === 'sheet.command.paste-by-short-key' || e.id === 'sheet.command.paste') {
              e.cancel = true;
              const startR = rg.startRow, startC = rg.startColumn;
              const write = (text: string) => {
                if (disposed || !text) return;
                const grid = text.replace(/\r\n?/g, '\n').split('\n').map(line => line.split('\t'));
                while (grid.length > 1 && grid[grid.length - 1]!.length === 1 && grid[grid.length - 1]![0] === '') grid.pop();
                const wb2 = fapi.getActiveWorkbook?.() as unknown as {
                  getSheetByName?: (n: string) => {
                    getRange?: (r: number, c: number, nr: number, nc: number) => { setValue?: (v: unknown) => unknown } | null;
                  } | null;
                } | null | undefined;
                const shObj = wb2?.getSheetByName?.(sh?.getSheetName?.() ?? '');
                if (!shObj?.getRange) return;
                for (let i = 0; i < grid.length; i++) {
                  for (let j = 0; j < grid[i]!.length; j++) {
                    const rr = startR + i, cc = startC + j;
                    if (isLockedCell(shName, rr, cc)) continue;             // never write into a locked cell
                    const raw = grid[i]![j] ?? '';
                    const cell = raw === '' ? { v: null }
                      : (raw.trim() !== '' && Number.isFinite(Number(raw))) ? { v: Number(raw), t: 2 }   // 2 = number
                      : { v: raw, t: 1 };                                                                 // 1 = string (a date stays text — no date format)
                    shObj.getRange?.(rr, cc, 1, 1)?.setValue?.(cell);
                  }
                }
              };
              // Ctrl+V carries the text in the command params (no clipboard permission needed); the
              // context-menu Paste does not, so fall back to reading the clipboard.
              const pText = (e as { params?: { textContent?: string } }).params?.textContent;
              if (typeof pText === 'string') write(pText);
              else void navigator.clipboard.readText().then(write).catch(() => { /* clipboard blocked → no-op */ });
            }
            // Any other guarded command on entry cells (clear-contents, paste-value, …) → allow.
          }) ?? null;
        } catch (e) { console.warn('[SDMS/Univer] clear-content protection failed', e); }

        // ── SHEET-BAR LOCK (OG parity) ───────────────────────────────────────
        // OG's SDMS sheet bar is fixed — the analyst can't add / delete / rename / hide /
        // recolour / protect a sheet. Univer's sheet bar (a DOM component, unlike the canvas
        // grid) ships a "+" add button and a right-click tab menu (Delete / Copy / Rename /
        // Change color / Hide / Unhide / Protect / …). The "+" is hidden via CSS
        // (`[data-u-comp="sheet-bar-append-button"]`, see sdmsEntry.css); the tab context menu
        // is blocked here with a CAPTURE-phase contextmenu listener on the Univer container —
        // right-clicking anywhere in the sheet bar is swallowed before Univer opens its menu,
        // while left-click (switching tabs) still works. (user 2026-09-11)
        try {
          const onSheetBarCtx = (ev: MouseEvent) => {
            const t = ev.target as HTMLElement | null;
            // The tab bar is `slide-tab-bar`, each tab `slide-tab-item` (Univer's stable
            // data-u-comp hooks — there is NO `sheet-bar` element). Right-clicking either
            // is what opens Univer's tab menu (onContextMenu), so swallow it here.
            if (t && t.closest('[data-u-comp="slide-tab-bar"],[data-u-comp="slide-tab-item"]')) {
              ev.preventDefault();
              ev.stopImmediatePropagation();
            }
          };
          container.addEventListener('contextmenu', onSheetBarCtx, true);   // capture: run before Univer's handler
          // Mark the first genuine analyst gesture inside the sheet (pointer or key). The
          // edit/spill listener stays dormant until this fires, so no load-time recompute
          // (incl. the Enter-mode lazy-formula rebuild) is ever mistaken for an edit/spill.
          const onFirstTouch = () => { userInteractedRef.current = true; };
          container.addEventListener('pointerdown', onFirstTouch, true);
          container.addEventListener('keydown', onFirstTouch, true);
          sheetBarGuardRef.current = () => {
            container.removeEventListener('contextmenu', onSheetBarCtx, true);
            container.removeEventListener('pointerdown', onFirstTouch, true);
            container.removeEventListener('keydown', onFirstTouch, true);
          };
        } catch (e) { console.warn('[SDMS/Univer] sheet-bar lock failed', e); }

        // Capture cell edits AS THEY HAPPEN via Univer's SheetValueChanged event
        // (reliable — the event fires AFTER the mutation is applied, carrying the
        // affected ranges + their committed values).  Keyed by sheet name + 0-based
        // coords so Update can apply them onto the original .xlsx by name.
        try {
          // Original Univer cellData per sheet NAME — used to REVERT a protected formula cell a
          // paste/fill spilled into (see below). The merge writes an explicit `f` on every formula
          // cell, so restoring `{ f }` re-computes it.
          const cdByName: Record<string, Record<number, Record<number, { f?: string; v?: unknown }>>> = {};
          {
            const so = (mapped.data as { sheets?: Record<string, { name?: string; cellData?: Record<number, Record<number, { f?: string; v?: unknown }>> }> }).sheets ?? {};
            for (const sid of Object.keys(so)) { const s = so[sid]; if (s?.name) cdByName[s.name] = s.cellData ?? {}; }
          }
          const uapi = univerAPI as unknown as {
            addEvent?: (name: string, cb: (p: { effectedRanges?: Array<{
              getSheetName?: () => string; getRow?: () => number; getColumn?: () => number; getValues?: () => unknown[][];
            }> }) => void) => { dispose?: () => void };
            Event?: { SheetValueChanged?: string };
          };
          const evtName = uapi.Event?.SheetValueChanged ?? 'SheetValueChanged';
          eventDisposeRef.current = uapi.addEvent?.(evtName, (p) => {
            // Our own restore writes are not edits — see restoringRef.
            if (restoringRef.current) return;
            // A handle write (Engine B stamp, Apply Rounding, Qualifier pick, …) IS an edit to
            // persist — whether or not the analyst has touched the sheet yet — but never a
            // spill: a locked cell it wrote is left as written (see programmaticCellsRef).
            // Any cell of this event written by the handle? (then the event is not a load-time recompute to skip)
            const duringHandleWrite = handleWritingRef.current > 0;   // raised synchronously by our own write
            const programmatic = duringHandleWrite || (p.effectedRanges ?? []).some(rg => {
              const sn = rg.getSheetName?.() ?? ''; const r0 = rg.getRow?.() ?? -1; const c0 = rg.getColumn?.() ?? -1;
              const vv = rg.getValues?.();
              if (!sn || r0 < 0 || c0 < 0 || !Array.isArray(vv)) return false;
              for (let i = 0; i < vv.length; i++) for (let j = 0; j < ((vv[i] ?? []) as unknown[]).length; j++)
                if (isProgrammaticCell(sn, r0 + i, c0 + j, ((vv[i] ?? []) as unknown[])[j])) return true;
              return false;
            });
            // A SheetValueChanged before the analyst has touched the sheet is a
            // programmatic load-time write / recompute (incl. the Enter-mode lazy-formula
            // rebuild) — never a real edit or paste-spill. Ignore it (see userInteractedRef).
            if (!programmatic && !userInteractedRef.current) return;
            const revert: Array<{ sheetName: string; r: number; c: number; v?: string }> = [];
            // Workbook handle to read a locked cell's CURRENT formula — needed to tell a formula
            // RECOMPUTE (which keeps the formula) from a real SPILL (which removes it). See below.
            const dvWb = fapi.getActiveWorkbook?.() as unknown as {
              getSheetByName?: (n: string) => {
                getRange?: (r: number, c: number, nr: number, nc: number) => { getCellData?: () => { f?: string; si?: string } | null } | null;
              } | null;
            } | null | undefined;
            for (const rng of p.effectedRanges ?? []) {
              const sheetName = rng.getSheetName?.() ?? '';
              const startRow = rng.getRow?.() ?? -1;
              const startCol = rng.getColumn?.() ?? -1;
              const vals = rng.getValues?.();
              if (!sheetName || startRow < 0 || startCol < 0 || !Array.isArray(vals)) continue;
              const lset = locksRef.current[sheetName];
              const shCur = dvWb?.getSheetByName?.(sheetName);
              for (let i = 0; i < vals.length; i++) {
                const rowArr = (vals[i] ?? []) as unknown[];
                for (let j = 0; j < rowArr.length; j++) {
                  const rr = startRow + i, cc = startCol + j;
                  // A system stamp (setCellValue opts.system) still holding its value is not an edit.
                  if (systemCellsRef.current.get(`${sheetName}:${rr}:${cc}`) === String(rowArr[j] ?? '')) continue;
                  // A locked (formula) cell must NEVER become an edit. The BeforeCommandExecute
                  // guard blocks a paste/clear whose SELECTION hits a locked cell, but a paste or
                  // drag-fill can SPILL into a locked cell OUTSIDE the selection — caught here,
                  // after the fact: skip the edit and queue the cell to be reverted.
                  //
                  // BUT Univer's own formula RECOMPUTE also raises this event for locked formula
                  // cells and KEEPS the formula (f, or a shared-formula si); only a real spill
                  // REPLACES it with a literal. Reverting a recompute re-writes the formula ->
                  // recompute -> revert = an infinite loop (the "protected sheet" flicker + a
                  // permanent dialog, even in View mode; user 2026-09-13). So revert ONLY when the
                  // formula is confirmed GONE; a still-formula (or unreadable) cell is a recompute
                  // -> ignore, which can never loop.
                  if (isLockedCell(sheetName, rr, cc)) {
                    // An Engine B output cell locked by COLUMN only (no formula of its own — the merge
                    // wrote a plain value there). Its engine stamp is a real value change Save must carry
                    // as an edit (no formula whose cached value could carry it).
                    const columnOnly = !lset?.has(`${rr},${cc}`) && !lockedSheetsRef.current.has(sheetName.toUpperCase());
                    if (duringHandleWrite || isProgrammaticCell(sheetName, rr, cc, rowArr[j])) {   // our write (or still its value)
                      if (columnOnly) editsRef.current.set(`${sheetName}:${rr}:${cc}`, { sheetName, row: rr, col: cc, value: rowArr[j] });
                      continue;                                  // (a formula cell: Save carries it as the cached value)
                    }
                    if (columnOnly) {
                      const mark = programmaticCellsRef.current.get(`${sheetName}:${rr}:${cc}`);
                      // An output cell Engine B never wrote (it had no result for that row) and that is
                      // STILL EMPTY has not changed: Univer's recalculation after a paste reports it
                      // among its dirty cells. Reverting it called that a spill — on a Calcium 8067 paste
                      // into the EDTAVolume entry column the blank Result / RPD / %Recovery cells of every
                      // row did, and the "protected sheet" warning popped although nothing was refused
                      // (user report 2026-10-10). A real spill writes DATA, which is still reverted.
                      if (mark === undefined && (rowArr[j] == null || String(rowArr[j]) === '')) continue;
                      revert.push({ sheetName, r: rr, c: cc, v: mark }); continue;
                    }
                    const cd = shCur?.getRange?.(rr, cc, 1, 1)?.getCellData?.();
                    const stillFormula = !cd
                      || (typeof cd.f === 'string' && cd.f.trim() !== '')
                      || (typeof cd.si === 'string' && cd.si !== '');
                    if (stillFormula) continue;                 // recompute / intact / unknown — not a spill
                    revert.push({ sheetName, r: rr, c: cc }); continue;
                  }
                  // The analyst typed over an imported FIELD value: their entry keeps its own type.
                  if (!duringHandleWrite && !isProgrammaticCell(sheetName, rr, cc, rowArr[j]))
                    textCellsRef.current.get(sheetName)?.delete(`${rr},${cc}`);
                  editsRef.current.set(`${sheetName}:${rr}:${cc}`, { sheetName, row: rr, col: cc, value: rowArr[j] });
                }
              }
            }
            // Only a paste / fill / move the analyst ran a moment ago can spill. A formula lost any other
            // way (a programmatic write, a recompute Univer reports without its formula) is not theirs:
            // leave it and never warn — that was the "random" protected-sheet message.
            if (revert.length && Date.now() - lastSpillCmdRef.current > SPILL_WINDOW_MS) {
              console.debug('[SDMS/protect] formula change outside a paste/fill ignored —',
                revert.map(x => `${x.sheetName}!r${x.r}c${x.c}`).join(', '));
              revert.length = 0;
            }
            if (revert.length) {
              console.warn('[SDMS/protect] spill reverted —', revert.map(x => `${x.sheetName}!r${x.r}c${x.c}`).join(', '));
              // Revert the spilled-into formula cells to their original formula on the next tick
              // (restoringRef keeps the revert writes from being re-captured as edits), and warn
              // — the same protected-sheet message as typing/clear. (user 2026-09-11)
              setTimeout(() => {
                if (disposed) return;
                restoringRef.current = true;
                try {
                  const wb = fapi.getActiveWorkbook?.() as unknown as {
                    getSheetByName?: (n: string) => { getRange?: (r: number, c: number, nr: number, nc: number) => { setValue?: (v: unknown) => unknown } | null } | null;
                  } | null | undefined;
                  for (const rc of revert) {
                    const orig = cdByName[rc.sheetName]?.[rc.r]?.[rc.c];
                    // A column-locked output cell goes back to Engine B's last value when it has one.
                    const back = rc.v !== undefined && rc.v.trim() !== '' && Number.isFinite(Number(rc.v)) ? Number(rc.v) : rc.v;
                    // A shared-formula cell carries only its `si` in cellData: its own formula is in formulaCellsRef.
                    const origF = formulaCellsRef.current[rc.sheetName]?.[rc.r]?.[rc.c]?.f ?? orig?.f;
                    const v = rc.v !== undefined ? { v: back } : origF ? { f: origF } : { v: orig?.v ?? null };
                    wb?.getSheetByName?.(rc.sheetName)?.getRange?.(rc.r, rc.c, 1, 1)?.setValue?.(v);
                  }
                } catch (err) { console.warn('[SDMS/Univer] locked-cell revert failed', err); }
                finally { setTimeout(() => { restoringRef.current = false; }, 0); }
              }, 0);
              const now = Date.now();
              if (now - lastWarnRef.current > 2500) { lastWarnRef.current = now; void warn(SDMS_PROTECTED_MSG, { title: 'Warning' }); }
            }
            if (import.meta.env.DEV) console.info('[SDMS/Univer] edit event → tracked cells =', editsRef.current.size);
          }) ?? null;
        } catch (e) { console.warn('[SDMS/Univer] SheetValueChanged subscribe failed', e); }
        // Read-only unless the SDMS ribbon's Edit button is active (ALPACA View
        // mode is read-only; cells unlock only after Edit).
        try { fapi.getActiveWorkbook?.()?.setEditable?.(!!editableRef.current); } catch { /* noop */ }

        // Lock the template-hidden columns — in READ-ONLY mode a user must not be
        // able to unhide the sheet's hidden helper columns (the ".." markers, e.g.
        // between R and T). Univer's read-only (setEditable(false)) does NOT block
        // a column unhide, so we re-assert the hidden set on the next microtask
        // whenever an unhide command fires (reverts it before paint).
        // OG parity (user 2026-08-14: "hidden fields not accessible"): DevExpress
        // protects the sheet (no FormatColumns) only in the entered/validated/
        // view-only states — a fresh ENTER-mode sheet is unprotected and the
        // analyst CAN unhide the helper columns. Mirror that: when the ribbon's
        // Edit/Enter mode is active (editableRef), let the unhide through.
        try {
          const hiddenBySheet: Record<string, number[]> = {};
          const sheetsObj = (mapped.data as { sheets?: Record<string, { name?: string; columnData?: Record<string, { hd?: number }> }> }).sheets ?? {};
          for (const sid of Object.keys(sheetsObj)) {
            const s = sheetsObj[sid]; const nm = s?.name ?? '';
            if (!nm) continue;
            const cols: number[] = [];
            for (const [cStr, cd] of Object.entries(s?.columnData ?? {})) if ((cd as { hd?: number })?.hd === 1) cols.push(Number(cStr));
            if (cols.length) hiddenBySheet[nm] = cols;
          }
          if (Object.keys(hiddenBySheet).length) {
            let rehiding = false;
            const wbf = fapi.getActiveWorkbook?.() as unknown as { onCommandExecuted?: (cb: (c: { id?: string }) => void) => { dispose?: () => void } } | undefined;
            cmdDisposeRef.current = wbf?.onCommandExecuted?.((cmd) => {
              // React ONLY to "make columns visible" (unhide) commands/mutations —
              // set-specific/selected-cols-visible + the set-col-visible mutation — and
              // revert them in BOTH View and Enter/Edit modes. OG strips the Home ribbon and
              // offers no unhide affordance in EITHER mode, so the template-hidden helper/config
              // columns (e.g. Metals ICP MS U–Y) can never be revealed there. Univer's
              // column-header context menu DOES offer "Show hidden columns", so we undo it to
              // match OG. (user 2026-09-11 — previously skipped in Enter on the mistaken belief
              // OG left the sheet unprotected; it does not, in either mode.)
              if (rehiding || !/cols?-visible/i.test(cmd?.id ?? '')) return;
              rehiding = true;   // dedupe the command + mutation double-fire into one revert
              queueMicrotask(() => {
                try {
                  for (const ws of fapi.getActiveWorkbook?.()?.getSheets?.() ?? []) {
                    const cols = hiddenBySheet[ws.getSheetName?.() ?? ''];
                    if (cols) for (const c of cols) { try { (ws as unknown as { hideColumns?: (i: number, n?: number) => unknown }).hideColumns?.(c, 1); } catch { /* noop */ } }
                  }
                } finally { rehiding = false; }
              });
            }) ?? null;
          }
        } catch (e) { console.warn('[SDMS/Univer] hidden-column lock failed', e); }

        // Report the active sheet name to the parent so it can overlay the
        // reconstructed calibration chart on the Calibration sheet (Univer drops
        // the embedded Excel chart).  Univer owns its own sheet tabs, so we POLL
        // the active-sheet getter (cheap) rather than depend on a specific event
        // name in the Univer version.  Fires once immediately, then on change.
        let lastSheet = '';
        const readSheet = () => {
          try {
            const nm = fapi.getActiveWorkbook?.()?.getActiveSheet?.()?.getSheetName?.() ?? '';
            if (nm && nm !== lastSheet) { lastSheet = nm; onSheetChangeRef.current?.(nm); }
          } catch { /* noop */ }
        };
        readSheet();
        pollRef.current = setInterval(readSheet, 300);

        // Signal "ready" once the calculation has gone QUIET and Univer has had a frame to paint — so the
        // host's "sheet not ready" panel lifts only on final values. The first pass is not the end: the
        // cached-value restore writes cells, and the cascade then runs more passes, which showed up as a
        // "Recalculating…" chip straight after the panel lifted (user 2026-09-28). Placed AFTER all the
        // setup above: the header-row filters created there mark their ranges for recalculation too, and
        // starting the wait before them let the panel lift ahead of that pass. Until ready, those passes
        // still feed the panel's percent (onLoadStage).
        // Re-checked after the paint yield: a calculation queued just before (its 100 ms timer starved by
        // the busy main thread) can start in that gap, and signalling then showed its tail as a
        // "Recalculating…" chip (user 2026-09-28) — so wait again until it is quiet across the yield too.
        void (async () => {
          for (;;) {
            await waitCalcIdle(() => !disposed);
            await new Promise(res => setTimeout(res, 0));
            if (disposed) return;
            const s = calcStateRef.current;
            if (!s.active && performance.now() - s.last >= CALC_QUIET_MS) break;
          }
          readyFired = true;
          onReadyRef.current?.();
        })();
      } catch (e) {
        console.warn('[SDMS/Univer] init failed', e);
        onReadyRef.current?.();   // never leave the host's panel up on a failed init
      }
    })();
    return () => {
      disposed = true;
      ribbonObserver?.disconnect(); ribbonObserver = null;
      dropCharts(chartPrefix);
      try { resizeObsRef.current?.disconnect(); } catch { /* noop */ }
      resizeObsRef.current = null;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      try { eventDisposeRef.current?.dispose?.(); } catch { /* noop */ }
      eventDisposeRef.current = null;
      try { cmdDisposeRef.current?.dispose?.(); } catch { /* noop */ }
      cmdDisposeRef.current = null;
      try { protectDisposeRef.current?.dispose?.(); } catch { /* noop */ }
      protectDisposeRef.current = null;
      try { clearGuardDisposeRef.current?.dispose?.(); } catch { /* noop */ }
      clearGuardDisposeRef.current = null;
      try { sheetBarGuardRef.current?.(); } catch { /* noop */ }
      sheetBarGuardRef.current = null;
      locksRef.current = {};
      // DISPOSE OFF THE RENDER PHASE (2026-08-13).  Univer's dispose() synchronously
      // unmounts its OWN React root; calling it straight from this cleanup happens while
      // React is still rendering, which logged:
      //   "Attempted to synchronously unmount a root while React was already rendering …
      //    which may lead to a race condition"  (SdmsUniverSheet ▸ Suspense ▸ …)
      // In dev, StrictMode runs effects mount → cleanup → mount, so that race is hit on
      // EVERY open: the teardown of instance #1 could tear down/blank the container that
      // instance #2 had already claimed → the intermittent BLANK SHEET.  Enabling the CF
      // preset added async boot work, shifting the timing so the race lost consistently —
      // which is why the blank looked like a "CF bug" (two attempts chased that).
      // Deferring to a macrotask lets React finish the current render first; `u` is
      // captured now because the refs are nulled synchronously below.
      for (const s of calcSubs) { try { s?.dispose?.(); } catch (e) { console.warn('[SDMS/Univer] calc listener dispose failed', e); } }
      const u = handleRef.current?.univer;
      if (u) setTimeout(() => { try { u.dispose?.(); } catch { /* noop */ } }, 0);
      handleRef.current = null;
      apiRef.current = null;
    };
  }, [buf, formulaKey]);

  // Flip read-only ⇄ editable when the SDMS ribbon Edit/Cancel/Update toggles it.
  useEffect(() => {
    try { apiRef.current?.getActiveWorkbook?.()?.setEditable?.(!!editable); } catch { /* noop */ }
    // Entering Edit on a value-only (read-only) build: rebuild ONCE so the
    // formulas are back and dependent cells recalculate as results are typed.
    // Guarded on an empty editsRef because the boot effect resets it — rebuilding
    // with edits pending would throw them away.
    if (SDMS_LAZY_FORMULAS && editable && !hasFormulasRef.current
        && editsRef.current.size === 0) {
      setFormulaKey(k => k + 1);
    }
  }, [editable]);

  /** Recompute the WHOLE workbook and wait for that run to settle. FUniver's executeCalculation(); waitForCalcRun
   *  waits for THAT calculation to start and settle, so the caller (the save, the success message) reads final
   *  values — Univer's own waits return before it starts (see waitForCalcRun). */
  const runFullCalculation = async (): Promise<boolean> => {
    const f = (apiRef.current as unknown as {
      getFormula?: () => { executeCalculation?: () => void } | undefined;
    })?.getFormula?.();
    if (!f) return false;
    try {
      const startsBefore = calcStartsRef.current;
      f.executeCalculation?.();
      await waitForCalcRun(() => calcStateRef.current, () => calcStartsRef.current, startsBefore, () => !!apiRef.current);
      return true;
    } catch (e) {
      console.warn('[SDMS/Univer] recalc failed', e);
      return false;
    }
  };

  // Update button → force-commit the open cell editor, then write ONLY the
  // actually-edited cells (captured via SheetValueChanged) onto the original .xlsx.
  useImperativeHandle(ref, () => ({
    addImages: (images) => {
      const fapi = apiRef.current;
      if (!fapi) return 0;
      let n = 0;
      for (const im of images) {
        if (!floatPicture(fapi, im.sheetName, im.row, im.col, im.widthPx, im.heightPx, `data:${im.mime};base64,${im.base64}`)) continue;
        importedImagesRef.current.push(im);
        n++;
      }
      return n;
    },
    whenCalcIdle: () => waitCalcIdle(() => !!apiRef.current),
    replaceSheets: async (transform, sheetNames) => {
      const fapi = apiRef.current as unknown as { syncExecuteCommand?: unknown } & FApiLite | null;
      if (!fapi?.syncExecuteCommand || !fapi.getActiveWorkbook?.() || !editableRef.current) return false;
      const next = await transform((baseOverrideRef.current ?? buf).slice(0));
      if (!next) return false;
      // The replaced sheets, mapped by the SAME converter the load uses, so they look exactly as after a reload.
      const { copySheet } = await import('./sdmsMailMerge');
      const src = new ExcelJS.Workbook();
      await src.xlsx.load(next.slice(0));
      const one = new ExcelJS.Workbook();
      for (const name of sheetNames) {
        const from = src.getWorksheet(name);
        if (from && ((from.state as string | undefined) ?? 'visible') === 'visible') copySheet(from, one.addWorksheet(name));
      }
      baseOverrideRef.current = next;
      if (!one.worksheets.length) return true;
      const mapped = await excelToUniver((await one.xlsx.writeBuffer()) as ArrayBuffer, true);
      restoringRef.current = true;   // our own redraw: not an analyst edit, never a spill
      try {
        const redrawn = redrawSheetsInPlace(fapi, mapped.data, (unitId, sheetId) => {
          try { restrictSdmsSheetActions(handleRef.current?.univer, unitId, [sheetId]); }
          catch (e) { console.warn('[SDMS/Univer] sheet-action restriction skipped', e); }
        });
        const redrawnSheets = (mapped.data as { sheets?: Record<string, { name?: string; cellData?: Record<number, Record<number, { f?: string; v?: unknown }>> }> }).sheets ?? {};
        for (const name of redrawn) {
          locksRef.current[name] = mapped.locks[name] ?? new Set();
          // The redrawn sheet's formulas AS CONVERTED replace the old ones (Recalculate and the spill revert restore from them).
          const sh = Object.values(redrawnSheets).find(s => s?.name === name);
          const m: Record<number, Record<number, { f?: string; v?: unknown }>> = {};
          for (const [r, row] of Object.entries(sh?.cellData ?? {}))
            for (const [c, cd] of Object.entries(row ?? {}))
              if (cd && typeof cd.f === 'string') (m[+r] ??= {})[+c] = { f: cd.f, v: cd.v };
          formulaCellsRef.current[name] = m;
          // What was typed or written into the old sheet went with it.
          for (const k of [...editsRef.current.keys()]) if (k.startsWith(`${name}:`)) editsRef.current.delete(k);
          runtimeFormulasRef.current.delete(name);
          runtimeNumFmtsRef.current.delete(name);
        }
      } finally {
        setTimeout(() => { restoringRef.current = false; }, 0);
      }
      // The redraw's own recalculation is PARTIAL, and a partial one reads a formula cell whose result is ""
      // as an empty cell (0) — VOC's Dup QCSample Result, INDEX(INDIRECT(parent!AP…)) over OG's "" limit cell —
      // so the Dup RPD % turned 200 / Fail after an import where a full calculation (and OG) gives 0.0 / Pass
      // (user report 2026-09-30, reproduced headlessly). Finish with a full calculation, after the flag drops.
      await new Promise(r => setTimeout(r, 0));
      await runFullCalculation();
      return true;
    },
    getEditedXlsx: async (opts?: { commitEditor?: boolean }) => {
      // FORCE-COMMIT the open cell editor.  The React "Update" button lives outside
      // the Univer canvas, so a plain click does NOT end the edit — the typed value
      // stays in the editor overlay and never reaches the model (that's why "52"
      // showed on screen but wasn't saved).  Univer commits on Enter, so dispatch
      // Enter to the active editor element, then blur, then wait for its
      // SheetValueChanged event to land in editsRef.
      if (opts?.commitEditor !== false) await commitOpenEditor();
      // Build a SPARSE model from ONLY the event-captured edits — do NOT full-scan
      // getValues (that flagged ~68 unchanged cells as "changed" because Univer
      // returns numbers / normalised text while the sheet stores display text, and
      // spammed the audit with old==new rows).
      const modelBySheet: Record<string, unknown[][]> = {};
      for (const e of editsRef.current.values()) {
        const sheet = (modelBySheet[e.sheetName] ??= []);
        const row = (sheet[e.row] ??= []);
        row[e.col] = e.value;
      }
      if (import.meta.env.DEV) console.info('[SDMS/Univer] tracked edits =', editsRef.current.size);
      const base = baseOverrideRef.current ?? buf;   // + any sheets replaceSheets swapped in place
      try {
        // Univer has already recalculated everything the edit touched. Read the
        // COMPUTED grid per sheet so applyModelToXlsx can refresh the dependent
        // formulas' cached values — DevExpress shows the cache and never
        // recalculates on load, so without this LIMS keeps the pre-edit numbers.
        // Bounds come from the workbook itself; one getValues() call per sheet.
        const computedBySheet: Record<string, unknown[][]> = {};
        // Univer's LIVE formula per cell (2026-09-15). Apply Rounding rewrites a cell's formula in
        // Univer (=_LIMSRound(...)) but the export patches the ORIGINAL xlsx with values only, so the
        // original buffer still carries the template formula — the formula-rewrite pass needs the LIVE one.
        const liveFormulaBySheet: Record<string, (string | undefined)[][]> = {};
        // ONE parse of `base` serves both the bounds below and applyModelToXlsx (it used to parse it again).
        // The bounds are read first, before applyModelToXlsx's reads create any cell, exactly as the
        // separate probe parse read them.
        let parsed: InstanceType<typeof ExcelJS.Workbook> | undefined;
        try {
          const probe = new ExcelJS.Workbook();
          await probe.xlsx.load(base.slice(0));
          parsed = probe;
          const fwb = apiRef.current?.getActiveWorkbook?.();
          for (const fs2 of fwb?.getSheets?.() ?? []) {
            const name = fs2.getSheetName?.() ?? '';
            const ws = name ? probe.getWorksheet(name) : undefined;
            if (!ws) continue;
            const rows = Math.max(1, ws.rowCount), cols = Math.max(1, ws.columnCount);
            const rng = fs2.getRange?.(`A1:${colLetter(cols)}${rows}`) as
              { getValues?: () => unknown[][];
                getCellDatas?: () => Array<Array<{ v?: unknown; f?: string } | null | undefined>> } | null | undefined;
            // getCellDatas() = the same `v` getValues() returns, plus the current formula `f`.
            const datas = rng?.getCellDatas?.();
            if (Array.isArray(datas)) {
              computedBySheet[name]   = datas.map(row => (row ?? []).map(cd => cd?.v));
              liveFormulaBySheet[name] = datas.map(row => (row ?? []).map(cd => (cd?.f ? String(cd.f) : undefined)));
            } else {
              const grid = rng?.getValues?.();
              if (Array.isArray(grid)) computedBySheet[name] = grid;
            }
          }
        } catch (e) {
          // Non-fatal: without the grid the edit still saves, the dependent caches
          // just stay stale — the pre-2026-08-19 behaviour, not a broken file.
          console.warn('[SDMS/Univer] computed-grid read failed', e);
        }
        const { buf: outBuf, changes, headerChanges } = await applyModelToXlsx(base, modelBySheet, computedBySheet, liveFormulaBySheet,
          Object.fromEntries(runtimeFormulasRef.current), Object.fromEntries(runtimeNumFmtsRef.current),
          Object.fromEntries(textCellsRef.current), parsed);
        if (import.meta.env.DEV) console.info('[SDMS/Univer] wrote', outBuf.byteLength, 'bytes,', changes.length, 'change(s)');
        // Pictures imported in this session ride along at the zip level (like the cell patch, the
        // workbook is never re-serialised) so ALPACA opens them on the sample sheets too.
        const withPics = importedImagesRef.current.length
          ? await patchWorkbookPictures(outBuf, importedImagesRef.current) : outBuf;
        return { bytes: withPics, changes, headerChanges };
      } catch (e) { console.warn('[SDMS/Univer] export failed', e); return null; }
    },
    recalc: async (opts?: { commitEditor?: boolean }): Promise<boolean> => {
      // Force-commit the open cell editor first (same reason as getEditedXlsx: the
      // ribbon button lives outside the Univer canvas, so a typed-but-not-Entered
      // value would otherwise be excluded from the recompute).
      if (opts?.commitEditor !== false) await commitOpenEditor();
      return runFullCalculation();
    },

    getActiveCell: () => {
      const sel = selRef.current;
      if (!sel) return null;
      try {
        const wb = (apiRef.current as unknown as { getActiveWorkbook?: () => { getSheetByName?: (n: string) => {
          getRange?: (r: number, c: number, nr?: number, nc?: number) => { getCellData?: () => { v?: unknown } | null } | null;
        } | null } | null })?.getActiveWorkbook?.();
        const sh = wb?.getSheetByName?.(sel.sheetName);
        const v = sh?.getRange?.(sel.row, sel.col, 1, 1)?.getCellData?.()?.v;
        return { sheetName: sel.sheetName, row: sel.row, col: sel.col, value: v == null ? '' : String(v) };
      } catch { return { sheetName: sel.sheetName, row: sel.row, col: sel.col, value: '' }; }
    },

    getActiveRange: () => {
      try {
        type Sh = {
          getSheetName?: () => string;
          getActiveRange?: () => { getRange?: () => { startRow: number; startColumn: number; endRow: number; endColumn: number } } | null;
        };
        const wb = (apiRef.current as unknown as { getActiveWorkbook?: () => {
          getActiveSheet?: () => Sh | null; getSheets?: () => Sh[];
        } | null })?.getActiveWorkbook?.();
        const sh = wb?.getActiveSheet?.();
        const rg = sh?.getActiveRange?.()?.getRange?.();
        const sheetName = sh?.getSheetName?.();
        if (!rg || sheetName == null) return null;
        const names = (wb?.getSheets?.() ?? []).map(s => s.getSheetName?.());
        return {
          sheetName, sheetIndex: Math.max(1, names.indexOf(sheetName) + 1),
          startRow: rg.startRow, startCol: rg.startColumn, endRow: rg.endRow, endCol: rg.endColumn,
        };
      } catch { return null; }
    },

    getCellValue: (sheetName: string, row: number, col: number): string | null => {
      try {
        const wb = (apiRef.current as unknown as { getActiveWorkbook?: () => { getSheetByName?: (n: string) => {
          getRange?: (r: number, c: number, nr?: number, nc?: number) => { getCellData?: () => { v?: unknown } | null } | null;
        } | null } | null })?.getActiveWorkbook?.();
        const sh = wb?.getSheetByName?.(sheetName);
        if (!sh) return null;
        const v = sh.getRange?.(row, col, 1, 1)?.getCellData?.()?.v;
        return v == null ? '' : String(v);
      } catch { return null; }
    },

    getCellFormula: (sheetName: string, row: number, col: number): string | null => {
      try {
        const wb = (apiRef.current as unknown as { getActiveWorkbook?: () => { getSheetByName?: (n: string) => {
          getRange?: (r: number, c: number, nr?: number, nc?: number) => { getFormula?: () => string; getCellData?: () => { f?: unknown } | null } | null;
        } | null } | null })?.getActiveWorkbook?.();
        const rg = wb?.getSheetByName?.(sheetName)?.getRange?.(row, col, 1, 1);
        // getFormula resolves a shared formula (a cell holding only an `si`); getCellData().f is the fallback.
        const f = rg?.getFormula?.() || rg?.getCellData?.()?.f;
        return typeof f === 'string' && f ? f : null;
      } catch { return null; }
    },

    getBaseXlsx: (): ArrayBuffer | null => baseOverrideRef.current ?? buf ?? null,

    getRuntimeFormula: (sheetName: string, row: number, col: number): string | null => {
      const f = runtimeFormulasRef.current.get(sheetName)?.get(`${row},${col}`);
      return f ? f.replace(/^s*=/, '') : null;
    },

    getLoadedFormula: (sheetName: string, row: number, col: number): string | null => {
      const f = formulaCellsRef.current[sheetName]?.[row]?.[col]?.f;
      return typeof f === 'string' && f.trim() !== '' ? (f.trimStart().startsWith('=') ? f : `=${f}`) : null;
    },

    commitEditor: () => commitOpenEditor(),

    getCellRawValue: (sheetName: string, row: number, col: number): string | null => {
      try {
        // getCellDataGrid reads the stored cell (getCellRaw); getCellData goes through the number-format display layer.
        const wb = (apiRef.current as unknown as { getActiveWorkbook?: () => { getSheetByName?: (n: string) => {
          getRange?: (r: number, c: number, nr?: number, nc?: number) => { getCellDataGrid?: () => Array<Array<{ v?: unknown } | null | undefined>> } | null;
        } | null } | null })?.getActiveWorkbook?.();
        const grid = wb?.getSheetByName?.(sheetName)?.getRange?.(row, col, 1, 1)?.getCellDataGrid?.();
        if (!grid) return null;
        const v = grid[0]?.[0]?.v;
        return v == null ? '' : String(v);
      } catch { return null; }
    },

    getSheetValues: (sheetName: string): (string | number | null)[][] | null => {
      try {
        type Rg = { getValues?: () => unknown[][] };
        type Sh = {
          getDataRange?: () => Rg | null;
          getMaxRows?: () => number; getMaxColumns?: () => number;
          getRange?: (r: number, c: number, nr?: number, nc?: number) => Rg | null;
        };
        const wb = (apiRef.current as unknown as { getActiveWorkbook?: () => { getSheetByName?: (n: string) => Sh | null } | null })
          ?.getActiveWorkbook?.();
        const sh = wb?.getSheetByName?.(sheetName);
        if (!sh) return null;
        // Used area when the facade offers it, else the whole sheet (trimmed by the caller).
        const rg = sh.getDataRange?.() ?? sh.getRange?.(0, 0, sh.getMaxRows?.() ?? 1000, sh.getMaxColumns?.() ?? 100);
        const vals = rg?.getValues?.();
        if (!vals) return null;
        const text = (v: unknown): string | number | null => {
          if (v == null) return null;
          if (typeof v === 'object') { const o = v as { v?: unknown }; return o.v == null ? null : text(o.v); }
          if (typeof v === 'boolean') return v ? 'True' : 'False';   // DevExpress bool ToString()
          if (typeof v === 'number') return v;                        // kept typed (header-cell >0 rule)
          const s = String(v);
          return s === '' ? null : s;
        };
        return vals.map(r => (r ?? []).map(text));
      } catch (e) { console.warn('[SDMS/Univer] getSheetValues failed', e); return null; }
    },

    getSheetDisplayValues: (sheetName: string): (string | null)[][] | null => {
      try {
        type Rg = { getDisplayValues?: () => unknown[][] };
        type Sh = {
          getDataRange?: () => Rg | null;
          getMaxRows?: () => number; getMaxColumns?: () => number;
          getRange?: (r: number, c: number, nr?: number, nc?: number) => Rg | null;
        };
        const wb = (apiRef.current as unknown as { getActiveWorkbook?: () => { getSheetByName?: (n: string) => Sh | null } | null })
          ?.getActiveWorkbook?.();
        const sh = wb?.getSheetByName?.(sheetName);
        if (!sh) return null;
        const rg = sh.getDataRange?.() ?? sh.getRange?.(0, 0, sh.getMaxRows?.() ?? 1000, sh.getMaxColumns?.() ?? 100);
        const vals = rg?.getDisplayValues?.();
        if (!vals) return null;
        return vals.map(r => (r ?? []).map(v => (v == null || String(v) === '' ? null : String(v))));
      } catch (e) { console.warn('[SDMS/Univer] getSheetDisplayValues failed', e); return null; }
    },

    getSheetNames: (): string[] => {
      try {
        const wb = (apiRef.current as unknown as { getActiveWorkbook?: () => { getSheets?: () => Array<{ getSheetName?: () => string }> } | null })
          ?.getActiveWorkbook?.();
        return (wb?.getSheets?.() ?? []).map(s => s.getSheetName?.() ?? '').filter(Boolean);
      } catch (e) { console.warn('[SDMS/Univer] getSheetNames failed', e); return []; }
    },

    setQcNotes: (notes: QcCellNote[], group = 'qc'): number => {
      type NoteRg = { createOrUpdateNote?: (n: unknown) => unknown; deleteNote?: () => unknown };
      const wb = (apiRef.current as unknown as { getActiveWorkbook?: () => { getSheetByName?: (n: string) => {
        getRange?: (r: number, c: number, nr?: number, nc?: number) => NoteRg | null;
      } | null } | null })?.getActiveWorkbook?.();
      if (!wb) return 0;
      for (const c of qcNoteCellsRef.current.get(group) ?? []) {
        try { wb.getSheetByName?.(c.sheetName)?.getRange?.(c.row, c.col, 1, 1)?.deleteNote?.(); }
        catch (e) { console.warn('[SDMS/Univer] QC note remove failed', e); }
      }
      const placed: Array<{ sheetName: string; row: number; col: number }> = [];
      qcNoteCellsRef.current.set(group, placed);
      let n = 0;
      for (const note of notes) {
        try {
          const rg = wb.getSheetByName?.(note.sheetName)?.getRange?.(note.row, note.col, 1, 1);
          if (!rg?.createOrUpdateNote) continue;
          const lines = note.text.split('\n');
          const longest = lines.reduce((m, l) => Math.max(m, l.length), 0);
          rg.createOrUpdateNote({
            id: `${group}-${note.sheetName}-${note.row}-${note.col}`, row: note.row, col: note.col, note: note.text,
            width: Math.min(420, Math.max(200, longest * 7 + 24)), height: Math.max(48, lines.length * 20 + 16),
          });
          placed.push({ sheetName: note.sheetName, row: note.row, col: note.col });
          n++;
        } catch (e) { console.warn('[SDMS/Univer] QC note failed', e); }
      }
      return n;
    },

    setCellValue: (sheetName: string, row: number, col: number, value: string | number, numberFormat?: string,
                   opts?: { text?: boolean; system?: boolean }): boolean => {
      if (!editableRef.current) return false;
      try {
        const wb = (apiRef.current as unknown as { getActiveWorkbook?: () => { getSheetByName?: (n: string) => {
          getRange?: (r: number, c: number, nr?: number, nc?: number) => { setValue?: (v: unknown) => unknown } | null;
        } | null } | null })?.getActiveWorkbook?.();
        const sh = wb?.getSheetByName?.(sheetName);
        const rg = sh?.getRange?.(row, col, 1, 1);
        if (!rg?.setValue) return false;
        runtimeFormulasRef.current.get(sheetName)?.delete(`${row},${col}`);   // a value now — no formula to persist
        runtimeNumFmtsRef.current.get(sheetName)?.delete(`${row},${col}`);
        if (opts?.text && typeof value === 'string') {
          let texts = textCellsRef.current.get(sheetName);
          if (!texts) { texts = new Map(); textCellsRef.current.set(sheetName, texts); }
          texts.set(`${row},${col}`, value);
        } else textCellsRef.current.get(sheetName)?.delete(`${row},${col}`);
        markProgrammaticWrite(sheetName, row, col, value);
        // Record the write as an edit NOW, not only when Univer's SheetValueChanged arrives: that event
        // lands AFTER this call returns, and Save / Apply Rounding / the background Engine B reads export
        // straight after the stamps (no wait) — so the export still held the SAVED value, Save persisted
        // it (TOC QB26092401_001: NumericResult 1.0200 saved with Result 1.2) and Apply Rounding wrapped
        // it back onto the sheet (user 2026-09-24). Same rule as the listener: a value cell (incl. an
        // Engine B output column) is an edit; a formula cell carries it as its cached value; a
        // wholesale-locked sheet never records one.
        if (opts?.system) systemCellsRef.current.set(`${sheetName}:${row}:${col}`, String(value));
        else if (!locksRef.current[sheetName]?.has(`${row},${col}`) && !lockedSheetsRef.current.has(sheetName.toUpperCase()))
          editsRef.current.set(`${sheetName}:${row}:${col}`, { sheetName, row, col, value });
        handleWritingRef.current++;
        try {
          rg.setValue(typeof value === 'number' ? { v: value, t: 2 } : { v: value, t: 1 });   // 2 = NUMBER, 1 = STRING
          if (numberFormat) { try { (rg as { setNumberFormat?: (f: string) => unknown }).setNumberFormat?.(numberFormat); } catch { /* format optional */ } }
          // Re-mark with what the cell now HOLDS, so a later event on it compares against the real value.
          const held = (rg as { getValue?: () => unknown }).getValue?.();
          if (held !== undefined) markProgrammaticWrite(sheetName, row, col, held);
        } finally { handleWritingRef.current--; }
        return true;
      } catch (e) { console.warn('[SDMS/Univer] setCellValue failed', e); return false; }
    },

    setCellFormula: (sheetName: string, row: number, col: number, formula: string, numberFormat?: string): boolean => {
      if (!editableRef.current) return false;
      try {
        const wb = (apiRef.current as unknown as { getActiveWorkbook?: () => { getSheetByName?: (n: string) => {
          getRange?: (r: number, c: number, nr?: number, nc?: number) => {
            setValue?: (v: unknown) => unknown; setNumberFormat?: (f: string) => unknown } | null;
        } | null } | null })?.getActiveWorkbook?.();
        const sh = wb?.getSheetByName?.(sheetName);
        const rg = sh?.getRange?.(row, col, 1, 1);
        if (!rg?.setValue) return false;
        programmaticCellsRef.current.delete(`${sheetName}:${row}:${col}`);   // a formula again — the normal recompute rules apply
        textCellsRef.current.get(sheetName)?.delete(`${row},${col}`);
        handleWritingRef.current++;
        try {
          // Same `x=""` → `LEN(x)=0` rewrite as excelToUniver, so a re-pushed template formula
          // (Recalculate / Apply Rounding) keeps Excel's 0-is-not-blank semantics.
          rg.setValue({ f: rewriteEmptyStringComparisons(formula) });   // formula string, e.g. '=_LIMSRound(...)'
          if (numberFormat) { try { rg.setNumberFormat?.(numberFormat); } catch { /* format optional */ } }
          // Remember the formula AS PASSED (not the Univer rewrite) so the export persists it into <f>.
          let cells = runtimeFormulasRef.current.get(sheetName);
          if (!cells) { cells = new Map(); runtimeFormulasRef.current.set(sheetName, cells); }
          cells.set(`${row},${col}`, formula);
          let fmts = runtimeNumFmtsRef.current.get(sheetName);
          if (!fmts) { fmts = new Map(); runtimeNumFmtsRef.current.set(sheetName, fmts); }
          if (numberFormat) fmts.set(`${row},${col}`, numberFormat); else fmts.delete(`${row},${col}`);
        } finally { handleWritingRef.current--; }
        return true;
      } catch (e) { console.warn('[SDMS/Univer] setCellFormula failed', e); return false; }
    },
    setCellFormulas: (edits): number => {
      if (!editableRef.current || !edits.length) return 0;
      const fapi = apiRef.current as unknown as {
        syncExecuteCommand?: (id: string, params: unknown) => boolean;
        getActiveWorkbook?: () => { getId?: () => string; getSheetByName?: (n: string) => { getSheetId?: () => string } | null } | null;
      } | null;
      const wb = fapi?.getActiveWorkbook?.();
      const unitId = wb?.getId?.();
      if (!fapi?.syncExecuteCommand || !wb || !unitId) return 0;
      const bySheet = new Map<string, typeof edits[number][]>();
      for (const e of edits) { const list = bySheet.get(e.sheetName); if (list) list.push(e); else bySheet.set(e.sheetName, [e]); }
      let written = 0;
      handleWritingRef.current++;
      try {
        for (const [sheetName, list] of bySheet) {
          const subUnitId = wb.getSheetByName?.(sheetName)?.getSheetId?.();
          if (!subUnitId) continue;
          // A sparse cell map: set-range-values writes exactly these cells (absolute row / col), nothing between.
          const value: Record<number, Record<number, { f: string }>> = {};
          for (const e of list) {
            (value[e.row] ??= {})[e.col] = { f: rewriteEmptyStringComparisons(e.formula) };   // as setCellFormula
            programmaticCellsRef.current.delete(`${sheetName}:${e.row}:${e.col}`);
            textCellsRef.current.get(sheetName)?.delete(`${e.row},${e.col}`);
          }
          // The range is required (without one the command falls back to the current selection, and has nothing
          // to do when there is none); with a cell map Univer writes only the map's cells inside it.
          const range = {
            startRow: Math.min(...list.map(e => e.row)), endRow: Math.max(...list.map(e => e.row)),
            startColumn: Math.min(...list.map(e => e.col)), endColumn: Math.max(...list.map(e => e.col)),
          };
          try {
            if (!fapi.syncExecuteCommand('sheet.command.set-range-values', { unitId, subUnitId, range, value })) continue;
          } catch (e) { console.warn('[SDMS/Univer] setCellFormulas failed on', sheetName, e); continue; }
          const patterns = list.filter(e => e.numberFormat).map(e => ({ row: e.row, col: e.col, pattern: e.numberFormat }));
          if (patterns.length) {
            try { fapi.syncExecuteCommand('sheet.command.numfmt.set.numfmt', { unitId, subUnitId, values: patterns }); }
            catch (e) { console.warn('[SDMS/Univer] setCellFormulas number formats failed on', sheetName, e); }
          }
          // Remember each formula AS PASSED (and its format) so the export persists it — as setCellFormula does.
          let cells = runtimeFormulasRef.current.get(sheetName);
          if (!cells) { cells = new Map(); runtimeFormulasRef.current.set(sheetName, cells); }
          let fmts = runtimeNumFmtsRef.current.get(sheetName);
          if (!fmts) { fmts = new Map(); runtimeNumFmtsRef.current.set(sheetName, fmts); }
          for (const e of list) {
            cells.set(`${e.row},${e.col}`, e.formula);
            if (e.numberFormat) fmts.set(`${e.row},${e.col}`, e.numberFormat); else fmts.delete(`${e.row},${e.col}`);
          }
          written += list.length;
        }
      } finally { handleWritingRef.current--; }
      return written;
    },
    setCellValues: (edits): number => {
      if (!editableRef.current || !edits.length) return 0;
      const fapi = apiRef.current as unknown as {
        syncExecuteCommand?: (id: string, params: unknown) => boolean;
        getActiveWorkbook?: () => { getId?: () => string; getSheetByName?: (n: string) => {
          getSheetId?: () => string;
          getRange?: (r: number, c: number, nr?: number, nc?: number) => { getValue?: () => unknown } | null } | null } | null;
      } | null;
      const wb = fapi?.getActiveWorkbook?.();
      const unitId = wb?.getId?.();
      if (!fapi?.syncExecuteCommand || !wb || !unitId) return 0;
      const bySheet = new Map<string, typeof edits[number][]>();
      for (const e of edits) { const list = bySheet.get(e.sheetName); if (list) list.push(e); else bySheet.set(e.sheetName, [e]); }
      let written = 0;
      for (const [sheetName, list] of bySheet) {
        const sh = wb.getSheetByName?.(sheetName);
        const subUnitId = sh?.getSheetId?.();
        if (!subUnitId || !sh?.getRange) continue;
        // setCellValue's bookkeeping, per edit and in order (a cell written twice keeps the last value, as it did).
        for (const e of list) {
          runtimeFormulasRef.current.get(sheetName)?.delete(`${e.row},${e.col}`);   // a value now - no formula to persist
          runtimeNumFmtsRef.current.get(sheetName)?.delete(`${e.row},${e.col}`);
          if (e.opts?.text && typeof e.value === 'string') {
            let texts = textCellsRef.current.get(sheetName);
            if (!texts) { texts = new Map(); textCellsRef.current.set(sheetName, texts); }
            texts.set(`${e.row},${e.col}`, e.value);
          } else textCellsRef.current.get(sheetName)?.delete(`${e.row},${e.col}`);
          markProgrammaticWrite(sheetName, e.row, e.col, e.value);
          if (e.opts?.system) systemCellsRef.current.set(`${sheetName}:${e.row}:${e.col}`, String(e.value));
          else if (!locksRef.current[sheetName]?.has(`${e.row},${e.col}`) && !lockedSheetsRef.current.has(sheetName.toUpperCase()))
            editsRef.current.set(`${sheetName}:${e.row}:${e.col}`, { sheetName, row: e.row, col: e.col, value: e.value });
        }
        // One command per contiguous run of rows in a column: its range is exactly the cells written.
        const byCol = new Map<number, typeof edits[number][]>();
        for (const e of list) { const col = byCol.get(e.col); if (col) col.push(e); else byCol.set(e.col, [e]); }
        for (const [col, colEdits] of byCol) {
          const sorted = [...colEdits].sort((a, b) => a.row - b.row);
          let i = 0;
          while (i < sorted.length) {
            let j = i;
            while (j + 1 < sorted.length && sorted[j + 1]!.row <= sorted[j]!.row + 1) j++;
            const run = sorted.slice(i, j + 1);
            i = j + 1;
            // The same cell data setCellValue hands to setValue (2 = NUMBER, 1 = STRING); the facade passes an
            // ICellData object through untouched (covertCellValue), so the command sees identical input.
            const value: Record<number, Record<number, { v: string | number; t: number }>> = {};
            for (const e of run) (value[e.row] ??= {})[col] = typeof e.value === 'number' ? { v: e.value, t: 2 } : { v: e.value, t: 1 };
            const range = { startRow: run[0]!.row, endRow: run[run.length - 1]!.row, startColumn: col, endColumn: col };
            handleWritingRef.current++;
            try {
              try {
                if (!fapi.syncExecuteCommand('sheet.command.set-range-values', { unitId, subUnitId, range, value })) continue;
              } catch (e) { console.warn('[SDMS/Univer] setCellValues failed on', sheetName, e); continue; }
              const patterns = run.filter(e => e.numberFormat).map(e => ({ row: e.row, col: e.col, pattern: e.numberFormat }));
              if (patterns.length) {
                try { fapi.syncExecuteCommand('sheet.command.numfmt.set.numfmt', { unitId, subUnitId, values: patterns }); }
                catch (e) { console.warn('[SDMS/Univer] setCellValues number formats failed on', sheetName, e); }
              }
              // Re-mark with what each cell now HOLDS (as setCellValue), so a later event compares against the real value.
              for (const e of run) {
                const held = sh.getRange(e.row, e.col, 1, 1)?.getValue?.();
                if (held !== undefined) markProgrammaticWrite(sheetName, e.row, e.col, held);
              }
              written += run.length;
            } finally { handleWritingRef.current--; }
          }
        }
      }
      return written;
    },
  }), [buf]);

  return <div ref={containerRef} className="sdms-univer" style={{ width: '100%', height: '100%' }} />;
});
